import { Observer } from '@playcanvas/observer';
import * as pc from 'playcanvas';
import {
    ADDRESS_CLAMP_TO_EDGE,
    BLEND_NORMAL,
    BLENDMODE_ONE,
    BLENDMODE_ZERO,
    BLENDEQUATION_ADD,
    EVENT_KEYDOWN,
    FILTER_NEAREST,
    KEY_F,
    LAYERID_DEPTH,
    LAYERID_SKYBOX,
    PIXELFORMAT_DEPTH,
    PIXELFORMAT_RGBA8,
    PRIMITIVE_POINTS,
    PRIMITIVE_LINELOOP,
    PRIMITIVE_LINES,
    PRIMITIVE_LINESTRIP,
    PRIMITIVE_TRIANGLES,
    PRIMITIVE_TRISTRIP,
    PRIMITIVE_TRIFAN,
    SKYTYPE_BOX,
    SKYTYPE_DOME,
    SKYTYPE_INFINITE,
    SORTMODE_BACK2FRONT,
    TEXTURETYPE_DEFAULT,
    TEXTURETYPE_RGBM,
    TONEMAP_LINEAR,
    TONEMAP_FILMIC,
    TONEMAP_HEJL,
    TONEMAP_ACES,
    TONEMAP_ACES2,
    math,
    path,
    AnimEvents,
    AnimTrack,
    Asset,
    BlendState,
    BoundingBox,
    Color,
    Entity,
    EnvLighting,
    GraphicsDevice,
    GraphNode,
    GSplatComponent,
    Keyboard,
    Mat4,
    Mesh,
    MeshInstance,
    MorphInstance,
    MorphTarget,
    Mouse,
    MiniStats,
    Quat,
    RenderComponent,
    RenderTarget,
    StandardMaterial,
    Texture,
    TouchDevice,
    Vec3,
    Vec4,
    WebglGraphicsDevice
} from 'playcanvas';
// @ts-ignore
import { MultiCamera } from 'playcanvas/scripts/camera/multi-camera.js';

import { App } from './app';
import { DebugLines } from './debug-lines';
import { CreateDropHandler } from './drop-handler';
import { Multiframe } from './multiframe';
import { PngExporter } from './png-exporter';
import { ReadDepth } from './read-depth';
import { ShadowCatcher } from './shadow-catcher';
import arCloseImage from './svg/ar-close.svg';
import arModeImage from './svg/ar-mode.svg';
import { File, HierarchyNode, MorphTargetData } from './types';
import { XRObjectPlacementController } from './xr-mode';
import { MeshoptDecoder } from '../lib/meshopt_decoder.module.js';
import { t } from './translation';
import { SyncVariables } from './ui/popup-panel';
import { render } from 'react-dom';
import { loadStl } from './stl-loader';
import { CAMERA_THRESHOLD, isModelFailed, transformCamerasToGalleryPhotos, transformPhotosToGalleryPhotos } from './transform-cameras';
import { ensureGuestSession, buildAuthHeaders as helpersBuildAuthHeaders } from './helpers';

// model filename extensions
const modelExtensions = ['gltf', 'glb', 'vox'];
const defaultSceneBounds = new BoundingBox(new Vec3(0, 1, 0), new Vec3(1, 1, 1));

// ─── Camera Positions Layer – global config (easy to change) ──────────────────
/** Uniform scale applied to each Camera3DModel.stl instance. */
const CAMERA_MODEL_SCALE = 0.07;
/**
 * Local-space Euler correction applied AFTER lookAt so that:
 *   - The camera model faces the origin.
 *   - The model sits upright according to calibration from RotateGizmo.
 */
const CAMERA_MODEL_FORWARD_FLIP_Y = 180;
const CAMERA_MODEL_ROTATION_X = 120.791;
const CAMERA_MODEL_ROTATION_Y = -3.275;
const CAMERA_MODEL_ROTATION_Z = -2.000;
/** Diffuse colour (r, g, b in 0–1 range). */
const CAMERA_MODEL_COLOR = new Color(0, 229 / 255, 214 / 255);
/** Highlight colour used for currently selected damage camera set. */
const CAMERA_MODEL_SELECTED_COLOR = new Color(1, 0.9, 0);
/** 0 = fully transparent · 1 = fully opaque. */
const CAMERA_MODEL_OPACITY = 1;


// ─── Camera Positions Layer – API response types ─────────────────────────────
interface ApiCameraEntry {
    imageName: string;
    cameraId: string;
    imageUrl: string;
    colmapCameraId: number;
    colmapImageId: number;
    rotation: { x: number; y: number; z: number; w: number };
    position: { x: number; y: number; z: number };
}
interface ApiCamerasResponse {
    s3Prefix: string;
    cameras: ApiCameraEntry[];
    photos?: import('./transform-cameras').ApiPhotoEntry[];
    totalCameras?: number;
    totalPhotos?: number;
}

interface DamagePosition {
    x: string;
    y: string;
    z: string;
}

interface DamageNote {
    id: string;
    dateTime: string;
    author: string;
    message: string;
}

interface DamageRecord {
    id: string;
    name: string;
    description: string;
    selectValue: string;
    photoIds: Array<string | number>;
    position: DamagePosition;
    notes?: DamageNote[];
    cameraId?: string;
    cameraIds?: string[];
}

interface CameraDamageOption {
    id: string;
    name: string;
    description: string;
    selectValue: string;
}
// ─────────────────────────────────────────────────────────────────────────────

const vec = new Vec3();
const bbox = new BoundingBox();

const FOCUS_FOV = 75;

class Viewer {
    canvas: HTMLCanvasElement;

    app: App;

    skyboxUrls: Map<string, string>;

    controlEventKeys: string[] = null;

    pngExporter: PngExporter = null;

    prevCameraMat: Mat4;

    camera: Entity;

    initialCameraPosition: Vec3 | null;

    initialCameraFocus: Vec3 | null;

    light: Entity;

    sceneRoot: Entity;

    debugRoot: Entity;

    entities: Array<Entity>;

    entityAssets: Array<{ entity: Entity; asset: Asset }>;

    assets: Array<Asset>;

    meshInstances: Array<MeshInstance>;

    wireframeMeshInstances: Array<MeshInstance>;

    wireframeMaterial: StandardMaterial;

    animTracks: Array<AnimTrack>;

    animationMap: Record<string, string>;

    firstFrame: boolean;

    skyboxLoaded: boolean;

    animSpeed: number;

    animTransition: number;

    animLoops: number;

    showWireframe: boolean;

    showBounds: boolean;

    showSkeleton: boolean;

    showAxes: boolean;

    showGrid: boolean;

    normalLength: number;

    dirtyWireframe: boolean;

    dirtyBounds: boolean;

    dirtySkeleton: boolean;

    dirtyGrid: boolean;

    dirtyNormals: boolean;

    sceneBounds: BoundingBox;

    dynamicSceneBounds: BoundingBox;

    debugBounds: DebugLines;

    debugSkeleton: DebugLines;

    debugGrid: DebugLines;

    debugNormals: DebugLines;

    miniStats: MiniStats;

    observer: Observer;

    suppressAnimationProgressUpdate: boolean;

    selectedNode: GraphNode | null;

    multiframe: Multiframe | null;

    multiframeBusy = false;

    readDepth: ReadDepth = null;

    cursorWorld = new Vec3();

    loadTimestamp?: number = null;

    shadowCatcher: ShadowCatcher = null;

    xrMode: XRObjectPlacementController;

    canvasResize = true;

    multiCamera: MultiCamera;

    cameraPosUpdated: Vec3;

    zoomCameraUpdated: number;

    isCreating: boolean = false;

    isEditing: boolean = false;

    descriptionsPointer: string[] = [];

    spheresArray: Entity[] = [];

    gizmosArray: Entity[] = [];

    selectPointer: string[] = [];

    photosPointer: string[][] = []; // Array of photo IDs associated with each damage marker

    tempPhotoIds: string[] = []; // Temporary storage for photo IDs when creating/editing a damage

    editingSpherePos: Vec3 = null;

    editingSphereIndex: number;

    selectedOptionGlobal: string;

    positionSphereButtonsComponent: any = null;

    // Camera positions layer ─────────────────────────────────────────────────
    cameraPositionEntities: Entity[] = [];
    cameraModelSpawnPositions: Vec3[] = [];
    cameraModelPhotoUrls: string[] = [];
    cameraModelPhotoIds: string[] = [];
    cameraModelCameraIds: string[] = [];
    cameraModelApiPositions: Vec3[] = [];
    cameraModelMaterials: StandardMaterial[] = [];
    cameraIndexById: Map<string, number> = new Map();
    cameraDamageByCameraId: Map<string, DamageRecord[]> = new Map();
    cameraDamageRecords: DamageRecord[] = [];
    cameraPositionsVisible: boolean = false;
    cameraPositionsInitialImportDone: boolean = false;
    selectedCameraDamageIds: Set<string> = new Set();
    cameraVisibilityFilterIds: Set<string> | null = null;
    /** Invisible parent entity at (0,0,0) used as global rotation-correction controller. */
    cameraParentEntity: Entity | null = null;
    cameraModelRotateGizmo: any = null;
    // ─────────────────────────────────────────────────────────────────────────

    // Auto-front timer – positions camera to Front view after model load
    autoFrontTimer: ReturnType<typeof setTimeout> | null = null;

    // Bound handler so we can remove the listeners after the timer fires or is cancelled
    private _boundCancelAutoFront: (() => void) | null = null;

    // Scene lift – Y offset applied to sceneRoot (does not affect camera models)
    private sceneLiftBaseY: number | null = null;

    STAGE: 'pre' | 'prod' = 'pre'

    API_BASE_URLS: Record<'pre' | 'prod', string> = {
        pre: 'https://s0ixjydkk6.execute-api.eu-west-1.amazonaws.com',
        prod: 'https://8i705rlrck.execute-api.eu-west-1.amazonaws.com'
    }

    API_STAGE_PATHS: Record<'pre' | 'prod', 'pre' | 'pro'> = {
        pre: 'pre',
        prod: 'pro'
    }

    // API endpoint for registering requests and vehicle status
    // PRE: https://s0ixjydkk6.execute-api.eu-west-1.amazonaws.com/pre
    // PROD: https://8i705rlrck.execute-api.eu-west-1.amazonaws.com/pro
    API_BASE_URL = this.API_BASE_URLS[this.STAGE]
    API_STAGE_PATH: 'pre' | 'pro' = this.API_STAGE_PATHS[this.STAGE]
    API_PREFIX = 'api' // API prefix path

    // Static parameters for API request
    CLIENT_ID = 'arval'
    COUNTRY = 'fr'
    SERVICE_ID = 'S-3D'

    // S3 bucket configuration - automatically selected based on STAGE
    S3_BUCKETS = {
        pre: 'pre-mobius-videos',
        prod: 'prod-mobius-videos'
    }

    S3_BUCKET = this.S3_BUCKETS[this.STAGE] // Auto-select bucket based on stage

    // Vehicle information
    vehiclePlate: string = 'FR-1234-XYZ'; // Temporary hardcoded value, will be replaced with actual plate from PWA form

    // Request ID from URL parameters (used for API calls)
    requestId: string = '';

    // Model type detection - automated models have requestID in URL
    isAutomatedModel: boolean = false;

    // Orbital controls enabled when toolButtons parameter is present in URL
    enableOrbitalControls: boolean = false;

    // Right click damage markers enabled when rightClickDamage parameter is present in URL
    enableRightClickDamage: boolean = false;

    // Loading progress tracking
    private loadingProgress = 0;
    private loadingMessage = '';
    private fakeLoadingActive = false;
    private fakeLoadingInterval: any = null;
    private realLoadingStarted = false;

    private isInputFocused: boolean = false;
    private isCameraOverlayOpen: boolean = false;
    private orbitalControlsBeforeCameraOverlay: boolean | null = null;
    private moveSpeedBeforeCameraOverlay: number | null = null;
    private sprintSpeedBeforeCameraOverlay: number | null = null;
    private crouchSpeedBeforeCameraOverlay: number | null = null;
    private wheelSpeedBeforeCameraOverlay: number | null = null;
    private mouseDownPosition: { x: number, y: number } | null = null;
    private mouseMoved: boolean = false;
    private resolvedApiStageForRequestId: 'pre' | 'prod' | null = null;
    private resolvedApiStageRequestId: string = '';

    private setEnvironment(stage: 'pre' | 'prod') {
        this.STAGE = stage;
        this.API_BASE_URL = this.API_BASE_URLS[stage];
        this.API_STAGE_PATH = this.API_STAGE_PATHS[stage];
        this.S3_BUCKET = this.S3_BUCKETS[stage];
    }

    private getStageHintFromLoadParam(): 'pre' | 'prod' | null {
        const urlParams = new URLSearchParams(window.location.search);
        const loadParamRaw = urlParams.get('load');

        if (!loadParamRaw) {
            return null;
        }

        let loadParam = loadParamRaw;
        try {
            loadParam = decodeURIComponent(loadParamRaw);
        } catch {
            // Keep original value if decode fails.
        }

        const normalizedLoadUrl = loadParam.toLowerCase();
        if (normalizedLoadUrl.includes('pro-mobius')) {
            return 'prod';
        }

        if (normalizedLoadUrl.includes('pre-mobius')) {
            return 'pre';
        }

        return null;
    }

    private detectEnvironmentFromUrl(): 'pre' | 'prod' {
        const stageFromLoadUrl = this.getStageHintFromLoadParam();
        if (stageFromLoadUrl) {
            return stageFromLoadUrl;
        }

        const isDev = window.location.search.includes('stageUrlDev') ||
            window.location.origin === 'https://dev.3dexpertise-vw.grupomobius.com';
        return isDev ? 'pre' : 'prod';
    }

    private syncEnvironmentFromUrlHints(): void {
        const expectedStage = this.detectEnvironmentFromUrl();
        if (this.STAGE !== expectedStage) {
            this.setEnvironment(expectedStage);
        }
    }

    private getResolvedApiConfigFromUrl(): { stage: 'pre' | 'prod'; baseUrl: string; stagePath: 'pre' | 'pro' } {
        const stage = this.detectEnvironmentFromUrl();
        return {
            stage,
            baseUrl: this.API_BASE_URLS[stage],
            stagePath: this.API_STAGE_PATHS[stage]
        };
    }

    private buildRequestSearchUrl(stage: 'pre' | 'prod'): string {
        return `${this.API_BASE_URLS[stage]}/${this.API_STAGE_PATHS[stage]}/${this.API_PREFIX}/requests/search`;
    }

    /**
     * Returns the guest invitation token from the URL (`?token=...`), if any.
     * Kept for backwards compatibility — new code should rely on
     * `buildAuthHeaders()` (which uses the Cognito JWT obtained from
     * `ensureGuestSession()`).
     */
    private getGuestToken(): string {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            return (urlParams.get('token') || '').trim();
        } catch {
            return '';
        }
    }

    /**
     * Delegate to the shared `buildAuthHeaders` helper so every Business
     * API call uses the Cognito JWT exchanged from the guest token (set
     * by `ensureGuestSession()` at API resolution time).
     */
    private buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
        return helpersBuildAuthHeaders(extra);
    }

    private async requestExistsInStage(stage: 'pre' | 'prod'): Promise<boolean> {
        if (!this.requestId || this.requestId.trim() === '') {
            return false;
        }

        try {
            const response = await fetch(this.buildRequestSearchUrl(stage), {
                method: 'POST',
                mode: 'cors',
                headers: this.buildAuthHeaders(),
                body: JSON.stringify({
                    requestId: this.requestId
                })
            });

            if (response.status === 404) {
                return false;
            }

            if (!response.ok) {
                console.warn(`[Environment] Request search failed in ${stage.toUpperCase()} (${response.status})`);
                return false;
            }

            const payload = await response.json();
            const list = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload?.items)
                    ? payload.items
                    : Array.isArray(payload)
                        ? payload
                        : [];

            if (list.length === 0) {
                return false;
            }

            return list.some((entry: any) => {
                const candidate = String(entry?.requestId || entry?.requestID || entry?.id || '').trim();
                return candidate === this.requestId;
            }) || list.length > 0;
        } catch (error) {
            console.warn(`[Environment] Request search error in ${stage.toUpperCase()}:`, error);
            return false;
        }
    }

    private async resolveApiConfigByRequestId(): Promise<{ stage: 'pre' | 'prod'; baseUrl: string; stagePath: 'pre' | 'pro' }> {
        if (!this.requestId || this.requestId.trim() === '') {
            return this.getResolvedApiConfigFromUrl();
        }

        // Make sure we have a Cognito JWT in memory before issuing any
        // Business API call. The viewer ships with the raw guest-token
        // UUID in the URL; the Auth API exchanges it for a real JWT that
        // the protected endpoints accept. This is idempotent.
        await ensureGuestSession();

        if (this.resolvedApiStageRequestId === this.requestId && this.resolvedApiStageForRequestId) {
            const cachedStage = this.resolvedApiStageForRequestId;
            return {
                stage: cachedStage,
                baseUrl: this.API_BASE_URLS[cachedStage],
                stagePath: this.API_STAGE_PATHS[cachedStage]
            };
        }

        const existsInProd = await this.requestExistsInStage('prod');
        if (existsInProd) {
            this.resolvedApiStageForRequestId = 'prod';
            this.resolvedApiStageRequestId = this.requestId;
            this.setEnvironment('prod');
            return {
                stage: 'prod',
                baseUrl: this.API_BASE_URLS.prod,
                stagePath: this.API_STAGE_PATHS.prod
            };
        }

        const existsInPre = await this.requestExistsInStage('pre');
        if (existsInPre) {
            this.resolvedApiStageForRequestId = 'pre';
            this.resolvedApiStageRequestId = this.requestId;
            this.setEnvironment('pre');
            return {
                stage: 'pre',
                baseUrl: this.API_BASE_URLS.pre,
                stagePath: this.API_STAGE_PATHS.pre
            };
        }

        const fallbackConfig = this.getResolvedApiConfigFromUrl();
        this.resolvedApiStageForRequestId = fallbackConfig.stage;
        this.resolvedApiStageRequestId = this.requestId;
        this.setEnvironment(fallbackConfig.stage);
        console.warn(`[Environment] requestId ${this.requestId} not found in PROD/PRE search. Falling back to URL stage ${fallbackConfig.stage.toUpperCase()}`);

        return fallbackConfig;
    }

    constructor(
        canvas: HTMLCanvasElement,
        graphicsDevice: GraphicsDevice,
        observer: Observer,
        skyboxUrls: Map<string, string>
    ) {
        this.canvas = canvas;

        // create the application
        const app = new App(canvas, {
            mouse: new Mouse(canvas),
            touch: new TouchDevice(canvas),
            keyboard: new Keyboard(window),
            graphicsDevice: graphicsDevice
        });
        this.app = app;
        this.skyboxUrls = skyboxUrls;

        // Detect if this is an automated model by checking for requestID in URL
        const urlParams = new URLSearchParams(window.location.search);
        const requestID = urlParams.get('requestID');
        this.requestId = requestID || ''; // Store requestId for API calls
        this.isAutomatedModel = !!(requestID && requestID.trim() !== '');

        // Resolve API/S3 environment from URL before any API calls are made.
        this.setEnvironment(this.detectEnvironmentFromUrl());
        
        const currentUrl = window.location.href;

        // Orbital mode must start OFF by default on each load.
        this.enableOrbitalControls = false;

        // Check if right click damage markers should be enabled (when rightClickDamage is in URL)
        this.enableRightClickDamage = currentUrl.includes('rightClickDamage');

        // clustered not needed and has faster startup on windows
        this.app.scene.clusteredLightingEnabled = false;

        // monkeypatch the mouse and touch input devices to ignore touch events
        // when they don't originate from the canvas.
        const origMouseHandler = app.mouse._moveHandler;
        app.mouse.detach();
        app.mouse._moveHandler = (event: MouseEvent) => {
            if (event.target === canvas) {
                origMouseHandler(event);
            }
        };
        app.mouse.attach(canvas);

        const origTouchHandler = app.touch._moveHandler;
        app.touch.detach();
        app.touch._moveHandler = (event: MouseEvent) => {
            if (event.target === canvas) {
                origTouchHandler(event);
            }
        };
        app.touch.attach(canvas);

        // @ts-ignore
        const multisampleSupported = app.graphicsDevice.maxSamples > 1;
        observer.set('camera.multisampleSupported', multisampleSupported);
        observer.set('camera.multisample', multisampleSupported && observer.get('camera.multisample'));
        observer.set('camera.multisample', false); // Disable multisampling by default for performance
        //observer.set('camera.pixelScale', 0.5); // Set default pixel scale to 0.5 for better quality

        // create the exporter
        this.pngExporter = new PngExporter();

        // create drop handler
        CreateDropHandler(document.getElementById('app'), (files: Array<File>, resetScene: boolean) => {
            this.loadFiles(files, resetScene);
        });

        // observe canvas size changes
        new ResizeObserver(() => {
            if (this.xrMode && !this.xrMode.active) {
                this.canvasResize = true;
                this.renderNextFrame();
            }
        }).observe(window.document.getElementById('canvas-wrapper'));

        // Depth layer is where the framebuffer is copied to a texture to be used in the following layers.
        // Move the depth layer to take place after World and Skydome layers, to capture both of them.
        const depthLayer = app.scene.layers.getLayerById(LAYERID_DEPTH);
        app.scene.layers.remove(depthLayer);
        app.scene.layers.insertOpaque(depthLayer, 2);

        // create the camera
        const camera = new Entity('Camera');
        camera.addComponent('camera', {
            fov: 75,
            frustumCulling: true,
            clearColor: new Color(0, 0, 0, 0)
        });
        camera.addComponent('script');
        this.multiCamera = camera.script.create(MultiCamera);
        camera.camera.requestSceneColorMap(true);

        // Reduce WASD sensitivity for better control
        this.multiCamera.moveSpeed = 0.05;  // Default is 1.0, reduced to 0.05 for less sensitivity

        // Set initial camera position and rotation
        camera.setPosition(0, 3, 5);  // X, Y, Z coordinates
        camera.lookAt(0, 0, 0);       // Look at origin (0, 0, 0)

        const synVar = new SyncVariables();

        // ? COULD BE THE SOLUTION TO FIX THE PROBLEM WITH THE CAMERA MOVEMENT

        // Disable middle click (button 1) and right click (button 2) controls
        // Block both pointer and mouse events to completely disable MultiCamera controls
        // Unless orbital controls are enabled (toolButtons parameter)
        
        // Block pointer events (used by MultiCamera)
        canvas.addEventListener('pointerdown', (event) => {
            // Allow middle button when orbital controls are enabled
            if (event.button === 1 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle click for orbital controls
            }
            if (event.button === 1 || event.button === 2) { // Middle or Right button
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        canvas.addEventListener('pointermove', (event) => {
            // Allow middle button drag when orbital controls are enabled
            if (event.buttons === 4 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle button drag for orbital controls
            }
            if (event.buttons === 4 || event.buttons === 2) { // 4 = middle, 2 = right
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        canvas.addEventListener('pointerup', (event) => {
            // Allow middle button release when orbital controls are enabled
            if (event.button === 1 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle click release for orbital controls
            }
            if (event.button === 1 || event.button === 2) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        // Also block mouse events as backup
        canvas.addEventListener('mousedown', (event) => {
            // Allow middle button when orbital controls are enabled
            if (event.button === 1 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle click for orbital controls
            }
            if (event.button === 1 || event.button === 2) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        canvas.addEventListener('mousemove', (event) => {
            // Allow middle button drag when orbital controls are enabled
            if (event.buttons === 4 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle button drag for orbital controls
            }
            if (event.buttons === 4 || event.buttons === 2) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        canvas.addEventListener('mouseup', (event) => {
            // Allow middle button release when orbital controls are enabled
            if (event.button === 1 && this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return; // Allow middle click release for orbital controls
            }
            if (event.button === 1 || event.button === 2) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return false;
            }
        }, { capture: true });

        canvas.addEventListener('contextmenu', (event) => {
            event.preventDefault();  // Prevent default context menu
            //console.log('Canvas right-clicked:', event.clientX, event.clientY);
        });

        // Keep wheel listener always attached so orbital mode can be toggled at runtime.
        canvas.addEventListener('wheel', (event) => {
            if (this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        }, { passive: false });

        const isInteractiveInput = (target: EventTarget | null): boolean => {
            if (!(target instanceof HTMLElement)) {
                return false;
            }

            const tag = target.tagName;
            const editable = target.getAttribute('contenteditable') === 'true';
            return editable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
        };

        document.addEventListener('focusin', (event) => {
            if (isInteractiveInput(event.target)) {
                this.isInputFocused = true;
                this.multiCamera.moveSpeed = 0;
            }
        });

        document.addEventListener('focusout', () => {
            const active = document.activeElement;
            if (!isInteractiveInput(active)) {
                this.isInputFocused = false;
                this.multiCamera.moveSpeed = 0.05;
            }
        });

        // Do not intercept keydown in capture phase when typing.
        // We rely on `isInputFocused` + reduced moveSpeed to prevent camera movement
        // while keeping text input fully functional.

        // Keep touch listeners attached and gate behavior dynamically.
        canvas.addEventListener('touchstart', (event) => {
            if (this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        }, { passive: false });

        canvas.addEventListener('touchmove', (event) => {
            if (this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        }, { passive: false });

        canvas.addEventListener('touchend', (event) => {
            if (this.enableOrbitalControls && !this.isCameraOverlayOpen) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        }, { passive: false });

        const qualitySelect = document.getElementById('qualitySelect') as HTMLSelectElement;
        if (qualitySelect) {
            qualitySelect.addEventListener('change', handleQualityChange);
        }

        function handleQualityChange(e: Event): void {
            const select = e.target as HTMLSelectElement;
            observer.set('camera.pixelScale', select.value);
        }

        /* 
                app.keyboard.on(EVENT_KEYDOWN, (event) => {
                    switch (event.key) {
                        case pc.KEY_W: {
                            if (this.isInputFocused) return;
                            break;
                        }
                    }
                }); */

        // create the light
        const light = new Entity();
        light.addComponent('light', {
            type: 'directional',
            shadowBias: 0.2,
            shadowResolution: 2048
        });
        app.root.addChild(light);

        // disable autorender
        app.autoRender = false;
        this.prevCameraMat = new Mat4();
        app.on('update', this.update, this);
        app.on('framerender', this.onFrameRender, this);
        app.on('prerender', this.onPrerender, this);
        app.on('postrender', this.onPostrender, this);
        app.on('frameend', this.onFrameend, this);

        // create the scene and debug root nodes
        const sceneRoot = new Entity('sceneRoot', app);
        app.root.addChild(sceneRoot);

        const debugRoot = new Entity('debugRoot', app);
        app.root.addChild(debugRoot);

        // store app things
        this.camera = camera;
        this.initialCameraPosition = null;
        this.initialCameraFocus = null;
        this.light = light;
        this.sceneRoot = sceneRoot;
        this.debugRoot = debugRoot;

        // Spawn camera-position models (hidden by default, toggled via UI button)
        this.initCameraPositionsLayer();


        this.entities = [];
        this.entityAssets = [];
        this.assets = [];
        this.meshInstances = [];
        this.wireframeMeshInstances = [];

        const material = new StandardMaterial();
        material.blendState = new BlendState(
            true,
            BLENDEQUATION_ADD,
            BLENDMODE_ONE,
            BLENDMODE_ZERO,
            BLENDEQUATION_ADD,
            BLENDMODE_ZERO,
            BLENDMODE_ONE
        );
        material.useLighting = false;
        material.useSkybox = false;
        material.ambient = new Color(0, 0, 0);
        material.diffuse = new Color(0, 0, 0);
        material.specular = new Color(0, 0, 0);
        material.emissive = new Color(1, 1, 1);
        material.update();
        this.wireframeMaterial = material;

        this.animTracks = [];
        this.animationMap = {};
        this.firstFrame = false;
        this.skyboxLoaded = false;

        this.animSpeed = observer.get('animation.speed');
        this.animTransition = observer.get('animation.transition');
        this.animLoops = observer.get('animation.loops');
        this.showWireframe = observer.get('debug.wireframe');
        this.showBounds = observer.get('debug.bounds');
        this.showSkeleton = observer.get('debug.skeleton');
        this.showAxes = observer.get('debug.axes');
        this.normalLength = observer.get('debug.normals');
        this.setTonemapping(observer.get('camera.tonemapping'));
        this.setBackgroundColor(observer.get('skybox.backgroundColor'));
        this.setLightColor(observer.get('light.color'));
        this.setWireframeColor(observer.get('debug.wireframeColor'));

        this.dirtyWireframe = false;
        this.dirtyBounds = false;
        this.dirtySkeleton = false;
        this.dirtyGrid = false;
        this.dirtyNormals = false;

        this.sceneBounds = new BoundingBox();
        this.dynamicSceneBounds = new BoundingBox();

        this.debugBounds = new DebugLines(app, camera);
        this.debugSkeleton = new DebugLines(app, camera);
        this.debugGrid = new DebugLines(app, camera, false);
        this.debugNormals = new DebugLines(app, camera, false);

        // construct ministats, default off
        this.miniStats = new MiniStats(app);
        this.miniStats.enabled = observer.get('debug.stats');

        this.observer = observer;

        const device = this.app.graphicsDevice as WebglGraphicsDevice;

        // render frame after device restored
        device.on('devicerestored', () => {
            this.renderNextFrame();
        });

        // multiframe
        this.multiframe = new Multiframe(device, this.camera.camera);

        // dynamic shadow catcher
        this.shadowCatcher = new ShadowCatcher(app, this.camera.camera, this.debugRoot, this.sceneRoot);

        // xr support
        this.initXrMode();

        // initialize control events
        this.bindControlEvents();

        // load initial settings
        this.reloadSettings();

        // construct the depth reader
        this.readDepth = new ReadDepth(device);
        this.cursorWorld = new Vec3();

        // Click handler with debounce and drag detection
        let clickTimeout: any = null;
        let clickCount = 0;
        let lastClickEvent: MouseEvent | null = null;

        // Track mousedown position to detect drags
        canvas.addEventListener('mousedown', (event: MouseEvent) => {
            this.mouseDownPosition = { x: event.offsetX, y: event.offsetY };
            this.mouseMoved = false;
            
            // Disable orbital controls when left mouse button is pressed (unless toolButtons is active)
            if (event.button === 0 && !this.enableOrbitalControls) { // 0 = left button
                if (this.multiCamera) {
                    this.multiCamera.detach();
                }
            }
        });

        // Track mouse movement to detect drags
        canvas.addEventListener('mousemove', (event: MouseEvent) => {
            if (this.mouseDownPosition && event.buttons === 1) {
                const dx = Math.abs(event.offsetX - this.mouseDownPosition.x);
                const dy = Math.abs(event.offsetY - this.mouseDownPosition.y);
                // If moved more than 5 pixels, consider it a drag
                if (dx > 5 || dy > 5) {
                    this.mouseMoved = true;
                }
            }
        });

        // Re-enable orbital controls on mouseup (unless toolButtons is active)
        canvas.addEventListener('mouseup', (event: MouseEvent) => {
            if (event.button === 0 && !this.enableOrbitalControls) { // 0 = left button
                if (this.multiCamera) {
                    this.multiCamera.attach(this.camera.camera);
                }
            }
        });

        canvas.addEventListener('click', (event) => {
            // Ignore click if it was actually a drag
            if (this.mouseMoved) {
                this.mouseDownPosition = null;
                this.mouseMoved = false;
                return;
            }

            clickCount++;
            lastClickEvent = event;

            if (clickTimeout) {
                clearTimeout(clickTimeout);
                clickTimeout = null;
            }

            clickTimeout = setTimeout(() => {
                if (clickCount === 1 && lastClickEvent) {
                    // Single click - only handle sphere selection, not creation
                    this.handleSphereClick(lastClickEvent);
                    // Also handle clicks on camera-position 3D models
                    this.handleCameraModelClick(lastClickEvent);
                    // Do NOT call handlePlyClick here - sphere creation moved to right click
                }
                // Reset click count
                clickCount = 0;
                clickTimeout = null;
                lastClickEvent = null;
            }, 200); // 200ms delay to detect double click

            // Reset drag tracking
            this.mouseDownPosition = null;
            this.mouseMoved = false;
        });

        // Right click handler for creating spheres
        canvas.addEventListener('contextmenu', (event) => {
            event.preventDefault(); // Prevent default context menu
            
            // Only create spheres on right click if enabled via URL parameter, not in editing mode, and not focused on input
            if (this.enableRightClickDamage && !this.isEditing && !this.isInputFocused) {
                this.handlePlyClick(event);
            }
        });

        // double click handler
        canvas.addEventListener('dblclick', (event) => {
            // Cancel the single click handler
            if (clickTimeout) {
                clearTimeout(clickTimeout);
                clickTimeout = null;
            }
            clickCount = 0;
            lastClickEvent = null;

            const camera = this.camera.camera;
            const x = event.offsetX / canvas.clientWidth;
            const y = 1.0 - event.offsetY / canvas.clientHeight;

            // read depth
            this.readDepth.read(camera.renderTarget.depthBuffer, x, y)
                .then((depth: number) => {
                    if (depth < 1) {
                        const pos = new Vec4(x, y, depth, 1.0).mulScalar(2.0).subScalar(1.0); // clip space
                        camera.projectionMatrix.clone().invert().transformVec4(pos, pos); // homogeneous view space
                        pos.mulScalar(1.0 / pos.w); // perform perspective divide
                        this.cursorWorld.set(pos.x, pos.y, pos.z);
                        this.camera.getWorldTransform().transformPoint(this.cursorWorld, this.cursorWorld); // world space

                        // focus on cursor
                        this.multiCamera.focus(this.cursorWorld);
                    }
                });
        });

        this.app.scene.layers.getLayerByName('World').transparentSortMode = SORTMODE_BACK2FRONT;

        // start the application
        app.start();

        // TODO Activate the Ammo system
        if (app.systems.rigidbody) {
            app.systems.rigidbody.onLibraryLoaded();


        }

        this.hideNonEssentialButtons(true, true, false, true, true, false, false, false, false);

        const markersButton = document.getElementById('markers-div');
        if (markersButton) {

            if (!this.isCreating) {
                const acceptSphereButton = document.getElementById('position-sphere-confirm');
                const cancelSphereButton = document.getElementById('position-sphere-cancel');
                if (acceptSphereButton) acceptSphereButton.style.display = 'none';
                if (cancelSphereButton) cancelSphereButton.style.display = 'none';
            } else if (this.isCreating) {
                const acceptSphereButton = document.getElementById('position-sphere-confirm');
                const cancelSphereButton = document.getElementById('position-sphere-cancel');
                if (acceptSphereButton) acceptSphereButton.style.display = 'block';
                if (cancelSphereButton) cancelSphereButton.style.display = 'block';
            }
        }


        /*         // Create the plane entity for the pointer
                const plane = new Entity('plane');
                plane.addComponent('render', {
                    type: 'plane'
                });
        
                // Set the scale of the plane to adjust its size
                plane.setLocalScale(0.25, 0.25, 0.25);
        
                // Set the position of the plane to the mouse position
                plane.setPosition(1, 0, 1);
                // Removed invalid method call as 'setRadius' does not exist on 'Entity'
                this.app.root.addChild(plane); */
    }



    listAllEntities(node: Entity) {
        //console.log('Entidad:', node.name, node.getPosition(), node.render?.type);

        // Recorrer los hijos de la entidad actual
        node.children.forEach((child) => {
            if (child instanceof Entity) {
                this.listAllEntities(child);
            }
        });
    }


    createPointerSpawner() {
        alert(t('markers.legacyReadOnly'));
    }

    cancelPointerSpawner() {
        // Handle the cancellation of the pointer spawner
        const allSpheres = this.getDamageSphereRenderComponents();
        const globalMessage = document.getElementById('alert-message-spheres') as HTMLDivElement;
        if (globalMessage) globalMessage.style.display = 'none';

        if (this.isCreating) {
            const lastSphere = allSpheres[allSpheres.length - 1];
            if (lastSphere) {
                lastSphere.entity.destroy();

                // Look for all gizmos in the scene and remove them
                const allGizmos: Entity[] = [];
                this.app.root.children.forEach((child) => {
                    if (child instanceof Entity && child.name === 'gizmo') {
                        allGizmos.push(child);
                    }
                });

                const lastGizmo = allGizmos[allGizmos.length - 1];
                if (lastGizmo) {
                    lastGizmo.enabled = false; // Deactivate the gizmo
                    lastGizmo.name = `inactive-gizmo-${Date.now()}`; // Rename the gizmo to mark it as inactive
                    this.gizmosArray = this.gizmosArray.filter(gizmo => gizmo !== lastGizmo); // Remove it from the gizmo array
                }
                //this.listAllEntities(this.app.root);

                this.hidePointerInput();
                this.hideNonEssentialButtons(true, true, true, true, true, false, false, false, false);
                this.isCreating = false;
                this.renderNextFrame();
                
                // Notify React component
                if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setCreatingState === 'function') {
                    this.positionSphereButtonsComponent.setCreatingState(false);
                }
            }

        } else {
            // Cancel editing mode
            this.isEditing = false;

            const sphereBeforePosition = this.editingSpherePos;
            
            // Check if we have a valid saved position
            if (!sphereBeforePosition) {
                console.warn('No saved position found for sphere. Skipping position reset.');
                this.hidePointerInput();
                this.hideNonEssentialButtons(true, true, true, true, true, false, false, false, false);
                this.renderNextFrame();
                
                // Notify React component
                if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setEditingState === 'function') {
                    this.positionSphereButtonsComponent.setEditingState(false);
                }
                return;
            }
            
            //console.log('sphereBeforePosition', sphereBeforePosition);
            // Look for all gizmos in the scene and remove them
            const allGizmos: Entity[] = [];
            this.app.root.children.forEach((child) => {
                if (child instanceof Entity && child.name === 'gizmo') {
                    allGizmos.push(child);
                }
            });

            allGizmos.forEach((gizmo) => {
                gizmo.enabled = false;
            });
            const sphere = allSpheres[this.editingSphereIndex]?.entity;
            //console.log(this.editingSpherePos, this.editingSphereIndex);

            if (sphere) {
                sphere.setPosition(sphereBeforePosition.x, sphereBeforePosition.y, sphereBeforePosition.z);
                this.renderNextFrame(); // Ensure the scene updates after position change
            } else {
                console.error('Sphere not found or editingSphereIndex is invalid.');
            }
            this.hidePointerInput();
            this.hideNonEssentialButtons(true, true, true, true, true, false, false, false, false);
            this.renderNextFrame();
            
            // Notify React component
            if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setEditingState === 'function') {
                this.positionSphereButtonsComponent.setEditingState(false);
            }
        }

    }

    finishedPointerSpawner() {

        const nameInput = document.getElementById('sphere-name-input') as HTMLInputElement;
        const descInput = document.getElementById('sphere-description-input') as HTMLInputElement;
        const selectInput = document.getElementById('sphere-select-input') as HTMLInputElement;
        const globalMessage = document.getElementById('alert-message-spheres') as HTMLDivElement;


        //Usage of the global message
        if (nameInput.value === '' || descInput.value === '' || selectInput.value === '') {
            //show this for 5 seconds and then hide it
            globalMessage.style.display = 'block';
            globalMessage.innerHTML = t('markers.fillAllFields');
            globalMessage.style.backgroundColor = 'red';

            setTimeout(() => {
                globalMessage.style.display = 'block';
                globalMessage.innerHTML = t('markers.createMessage');
                globalMessage.style.backgroundColor = 'blue';
            }, 5000);
        } else {
            // Look for all gizmos in the scene and remove them
            const allGizmos: Entity[] = [];
            this.app.root.children.forEach((child) => {
                if (child instanceof Entity && child.name === 'gizmo') {
                    allGizmos.push(child);
                }
            });

            allGizmos.forEach((gizmo) => {
                gizmo.enabled = false;
            });

            const nameInput = document.getElementById('sphere-name-input') as HTMLInputElement;
            const descInput = document.getElementById('sphere-description-input') as HTMLInputElement;
            const selectInput = document.getElementById('sphere-select-input') as HTMLInputElement;
            const photoButton = document.getElementById('sphere-select-photos-button') as HTMLButtonElement;
            if (nameInput && descInput && selectInput) {
                nameInput.style.display = 'none';
                descInput.style.display = 'none';
                selectInput.style.display = 'none';
                if (photoButton) photoButton.style.display = 'none';
            }

            this.isCreating = false;
            this.nameDescrPointerSpawner();
            this.hideNonEssentialButtons(true, true, true, true, true, false, false, false, false);
            if (globalMessage) globalMessage.style.display = 'none';
            
            // Notify React component
            if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setCreatingState === 'function') {
                this.positionSphereButtonsComponent.setCreatingState(false);
            }

        }

    }

    editPointerSpawner(event: React.ChangeEvent<HTMLSelectElement>) {
        void event;
        alert(t('markers.legacyReadOnly'));



    }

    acceptEditPointerSpawner() {
        this.finishedPointerSpawner();
        this.isEditing = false;
        
        // Notify React component
        if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setEditingState === 'function') {
            this.positionSphereButtonsComponent.setEditingState(false);
        }
        //this.hideNonEssentialButtons(true, true, false, true, true, false, false, false);

    }

    moveCameraToSphere(index: number) {
        const allSpheres = this.getDamageSphereRenderComponents();
        const selectedSphere = allSpheres[index];
        //console.log('Moving camera to sphere index:', index, selectedSphere);
        if (selectedSphere) {
            const spherePos = selectedSphere.entity.getPosition();
            this.multiCamera.focus(spherePos);
            //console.log('Focusing camera on sphere at position:', spherePos);
        }

    }

    handlePlyClick(event: MouseEvent) {
        // Don't handle clicks if we're in creation mode, editing mode, or if inputs are focused
        if (this.isCreating || this.isEditing || this.isInputFocused) {
            return;
        }

        const canvas = this.canvas;
        const camera = this.camera.camera;

        // Get the PLY entity
        const plyEntity = this.getPlyEntity();
        if (!plyEntity) {
            return;
        }

        // console.log('=== PLY Click Debug Info ===');
        // console.log('PLY Entity:', plyEntity.name);

        // Check if it's a Gaussian Splat
        if (plyEntity.gsplat) {
            // console.log('️ This is a Gaussian Splat model, not a traditional mesh PLY');
            // console.log('Gaussian Splats use point-based rendering and cannot be raycasted like meshes');
            // console.log('Gsplat component:', plyEntity.gsplat);

            // For Gaussian Splats, we can use the camera's depth buffer approach
            this.handleGaussianSplatClick(event);
            return;
        }

        // Calculate normalized device coordinates
        const x = event.offsetX / canvas.clientWidth;
        const y = 1.0 - event.offsetY / canvas.clientHeight;

        // Create ray from camera through click position
        const cameraEntity = this.camera;
        const cameraPos = cameraEntity.getPosition();

        // Calculate world space ray direction
        const near = new Vec3();
        const far = new Vec3();
        camera.screenToWorld(event.offsetX, event.offsetY, camera.nearClip, near);
        camera.screenToWorld(event.offsetX, event.offsetY, camera.farClip, far);

        const direction = new Vec3();
        direction.sub2(far, near).normalize();

        // Raycast against all mesh instances in the PLY model
        let closestHit: { point: Vec3; distance: number; meshInstance: any; normal?: Vec3 } | null = null;
        let closestDistance = Infinity;

        // Collect all mesh instances from PLY entity
        const meshInstances: MeshInstance[] = [];
        this.collectMeshInstancesForRaycast(plyEntity, meshInstances);

        // console.log('=== PLY Click Debug Info ===');
        // console.log('PLY Entity:', plyEntity.name);
        // console.log('Mesh Instances found:', meshInstances.length);

        if (meshInstances.length === 0) {
            // console.warn('️ No mesh instances found in PLY entity!');
            // console.log('PLY Entity structure:', plyEntity);
            // console.log('PLY Entity children:', plyEntity.children);
            // Don't allow sphere creation on mesh-less PLY models (only Gaussian Splats)
            return;
        }

        // Check each mesh instance for intersection
        meshInstances.forEach((meshInstance, meshIndex) => {
            // console.log(`\n--- Checking Mesh Instance ${meshIndex + 1}/${meshInstances.length} ---`);

            if (!meshInstance.visible) {
                // console.log(' Mesh instance is not visible, skipping');
                return;
            }

            const mesh = meshInstance.mesh;
            if (!mesh) {
                // console.log(' No mesh data found, skipping');
                return;
            }

            // console.log(' Mesh found:', {
            //     vertices: mesh.vertexBuffer?.numVertices || 0,
            //     hasIndexBuffer: !!mesh.indexBuffer?.[0],
            //     indexCount: mesh.indexBuffer?.[0] ? 
            //         (mesh.indexBuffer[0].bytesPerIndex === 2 
            //             ? new Uint16Array(mesh.indexBuffer[0].storage).length
            //             : new Uint32Array(mesh.indexBuffer[0].storage).length) 
            //         : 0
            // });

            // Get mesh world transform
            const worldTransform = meshInstance.node.getWorldTransform();

            // Transform ray to local space
            const invTransform = worldTransform.clone().invert();
            const localFrom = new Vec3();
            const localDir = new Vec3();
            invTransform.transformPoint(near, localFrom);
            invTransform.transformVector(direction, localDir);
            localDir.normalize();

            // Get vertex data
            const positions = [];
            const vertexBuffer = mesh.vertexBuffer;
            const format = vertexBuffer.format;

            // console.log('Vertex buffer info:', {
            //     numVertices: vertexBuffer.numVertices,
            //     formatSize: format.size,
            //     elements: format.elements.map(e => ({ name: e.name, offset: e.offset, type: e.dataType }))
            // });

            // Find position element
            let positionElement = null;
            for (let i = 0; i < format.elements.length; i++) {
                if (format.elements[i].name === 'POSITION') {
                    positionElement = format.elements[i];
                    break;
                }
            }

            if (!positionElement) {
                // console.log(' No POSITION element found in vertex format!');
                return;
            }

            // console.log(' Position element found:', positionElement);

            // Read vertex positions
            const vertexData = new Float32Array(vertexBuffer.storage);
            const stride = format.size / 4; // size in floats
            const offset = positionElement.offset / 4; // offset in floats
            const numVertices = vertexBuffer.numVertices;

            // console.log('Reading vertices:', {
            //     stride,
            //     offset,
            //     numVertices,
            //     totalFloats: vertexData.length
            // });

            for (let i = 0; i < numVertices; i++) {
                const idx = i * stride + offset;
                positions.push(new Vec3(
                    vertexData[idx],
                    vertexData[idx + 1],
                    vertexData[idx + 2]
                ));
            }

            // console.log(` Read ${positions.length} vertex positions`);
            // if (positions.length > 0) {
            //     console.log('First vertex:', positions[0].toString());
            //     console.log('Last vertex:', positions[positions.length - 1].toString());
            // }

            // Get indices
            const indexBuffer = mesh.indexBuffer[0];
            if (!indexBuffer) {
                // console.log(' No index buffer found!');
                return;
            }

            const indices = indexBuffer.bytesPerIndex === 2
                ? new Uint16Array(indexBuffer.storage)
                : new Uint32Array(indexBuffer.storage);

            // console.log(` Read ${indices.length} indices (${Math.floor(indices.length / 3)} triangles)`);
            // console.log('First triangle indices:', [indices[0], indices[1], indices[2]]);

            // Check each triangle for intersection
            let trianglesChecked = 0;
            for (let i = 0; i < indices.length; i += 3) {
                trianglesChecked++;
                const v0 = positions[indices[i]];
                const v1 = positions[indices[i + 1]];
                const v2 = positions[indices[i + 2]];

                // Ray-triangle intersection using Möller–Trumbore algorithm
                const edge1 = new Vec3();
                const edge2 = new Vec3();
                edge1.sub2(v1, v0);
                edge2.sub2(v2, v0);

                const h = new Vec3();
                h.cross(localDir, edge2);
                const a = edge1.dot(h);

                if (a > -0.00001 && a < 0.00001) continue; // Ray parallel to triangle

                const f = 1.0 / a;
                const s = new Vec3();
                s.sub2(localFrom, v0);
                const u = f * s.dot(h);

                if (u < 0.0 || u > 1.0) continue;

                const q = new Vec3();
                q.cross(s, edge1);
                const v = f * localDir.dot(q);

                if (v < 0.0 || u + v > 1.0) continue;

                // Compute intersection point
                const t = f * edge2.dot(q);

                if (t > 0.00001) {
                    // Hit! Calculate world position
                    const localHitPoint = new Vec3();
                    localHitPoint.copy(localFrom).add(localDir.clone().mulScalar(t));

                    const worldHitPoint = new Vec3();
                    worldTransform.transformPoint(localHitPoint, worldHitPoint);

                    const distance = worldHitPoint.distance(cameraPos);

                    // Calculate triangle normal in local space
                    const normal = new Vec3();
                    normal.cross(edge1, edge2).normalize();

                    // Transform normal to world space
                    const worldNormal = new Vec3();
                    worldTransform.transformVector(normal, worldNormal);
                    worldNormal.normalize();

                    // console.log(` Hit found on triangle ${Math.floor(i / 3)} at distance ${distance.toFixed(3)}`);

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestHit = {
                            point: worldHitPoint,
                            distance: distance,
                            meshInstance: meshInstance,
                            normal: worldNormal
                        };
                    }
                }
            }

            // console.log(`Checked ${trianglesChecked} triangles in this mesh instance`);
        });

        // console.log('=== End PLY Click Debug ===\n');

        // If we hit the PLY model, start sphere creation at that position
        if (closestHit) {
            // Sphere radius is 0.06 units (scaled) - matching current sphere size
            const sphereRadius = 0.06; // scale value

            // Move sphere slightly away from camera toward surface
            const viewDirection = new Vec3();
            viewDirection.sub2(cameraPos, closestHit.point).normalize();

            // Apply small offset away from camera (negative = toward surface)
            const offset = sphereRadius * 0.5; // Small offset toward surface
            const clickedPosition = closestHit.point.clone().sub(viewDirection.mulScalar(offset));

            // Call createPointerSpawner to start the normal creation flow
            this.createPointerSpawner();

            // After the sphere is created, position it at the clicked location
            // We need to wait for the next frame to ensure the sphere exists
            setTimeout(() => {
                const allSpheres = this.getDamageSphereRenderComponents();
                if (allSpheres.length > 0) {
                    const lastSphere = allSpheres[allSpheres.length - 1];
                    lastSphere.entity.setPosition(clickedPosition);
                    this.renderNextFrame();
                }
            }, 100);
        }
    }

    /**
     * Handle click detection for Gaussian Splat models
     * Uses depth buffer reading instead of raycast since GSplats don't have triangle meshes
     */
    handleGaussianSplatClick(event: MouseEvent) {
        // Don't handle clicks if we're in creation mode, editing mode, or if inputs are focused
        if (this.isCreating || this.isEditing || this.isInputFocused) {
            return;
        }

        const canvas = this.canvas;
        const camera = this.camera.camera;

        // console.log('=== Gaussian Splat Click Detection ===');

        // Calculate normalized device coordinates
        const x = event.offsetX / canvas.clientWidth;
        const y = 1.0 - event.offsetY / canvas.clientHeight;

        // console.log('Click position:', { 
        //     screenX: event.offsetX, 
        //     screenY: event.offsetY,
        //     normalizedX: x,
        //     normalizedY: y
        // });

        // Try to read depth from the camera's render target
        const depthBuffer = camera.renderTarget?.depthBuffer;

        if (!this.readDepth) {
            // Fallback: use AABB center of the gsplat as approximation
            const plyEntity = this.getPlyEntity();
            if (plyEntity?.gsplat?.instance?.meshInstance?.aabb) {
                const aabb = plyEntity.gsplat.instance.meshInstance.aabb;
                const center = aabb.center.clone();

                // Sphere radius
                const sphereRadius = 0.125 * 0.125;

                // Calculate direction from center to surface (approximate)
                const cameraPos = this.camera.getPosition();
                const directionToCamera = new Vec3();
                directionToCamera.sub2(cameraPos, center).normalize();

                // Offset position very slightly along direction to camera
                const adjustedPosition = center.clone().add(directionToCamera.mulScalar(sphereRadius * 0.02));

                // Start creation flow and position sphere
                this.createPointerSpawner();
                setTimeout(() => {
                    const allSpheres = this.getDamageSphereRenderComponents();
                    if (allSpheres.length > 0) {
                        const lastSphere = allSpheres[allSpheres.length - 1];
                        lastSphere.entity.setPosition(adjustedPosition);
                        this.renderNextFrame();
                    }
                }, 100);
            }
            return;
        }

        if (!depthBuffer) {
            // console.error(' No depth buffer available on camera render target');
            // console.log('Camera render target:', camera.renderTarget);
            // console.log('Trying app graphics device default framebuffer...');

            // Try reading from the main framebuffer instead
            const device = this.app.graphicsDevice;
            if (device) {
                // For Gaussian Splats rendered to screen, we need to read from the default framebuffer
                // This requires a different approach - we'll use the scene bounds as approximation
                const plyEntity = this.getPlyEntity();
                if (plyEntity?.gsplat?.instance?.meshInstance?.aabb) {
                    const aabb = plyEntity.gsplat.instance.meshInstance.aabb;

                    // Project click ray into scene and intersect with AABB
                    const near = new Vec3();
                    const far = new Vec3();
                    camera.screenToWorld(event.offsetX, event.offsetY, camera.nearClip, near);
                    camera.screenToWorld(event.offsetX, event.offsetY, camera.farClip, far);

                    const direction = new Vec3();
                    direction.sub2(far, near).normalize();

                    // Simple AABB ray intersection
                    const cameraPos = this.camera.getPosition();
                    const intersectPoint = this.rayIntersectAABB(cameraPos, direction, aabb);

                    if (intersectPoint) {
                        const sphereRadius = 0.06 * 0.0006; // scale * local_radius

                        // Calculate direction from AABB center to intersection point as pseudo-normal
                        const center = plyEntity.gsplat.instance.meshInstance.aabb.center;
                        const pseudoNormal = new Vec3();
                        // Move sphere toward camera to place closer to surface
                        const viewDirection = new Vec3();
                        viewDirection.sub2(cameraPos, intersectPoint).normalize();
                        const offset = sphereRadius * 0.8;
                        const clickedPosition = intersectPoint.clone().add(viewDirection.mulScalar(offset));

                        this.createPointerSpawner();
                        setTimeout(() => {
                            const allSpheres = this.getDamageSphereRenderComponents();
                            if (allSpheres.length > 0) {
                                const lastSphere = allSpheres[allSpheres.length - 1];
                                lastSphere.entity.setPosition(clickedPosition);
                                this.renderNextFrame();
                            }
                        }, 100);
                    }
                }
            }
            return;
        }

        // Read depth at click position
        this.readDepth.read(depthBuffer, x, y)
            .then((depth: number) => {
                // console.log('Depth at click:', depth);

                if (depth < 1) {
                    // Convert screen position + depth to world position
                    const pos = new Vec4(x, y, depth, 1.0).mulScalar(2.0).subScalar(1.0); // clip space
                    camera.projectionMatrix.clone().invert().transformVec4(pos, pos); // homogeneous view space
                    pos.mulScalar(1.0 / pos.w); // perform perspective divide

                    const worldPos = new Vec3(pos.x, pos.y, pos.z);
                    this.camera.getWorldTransform().transformPoint(worldPos, worldPos); // world space

                    // Calculate direction from camera toward point
                    const cameraPos = this.camera.getPosition();
                    const viewDirection = new Vec3();
                    viewDirection.sub2(cameraPos, worldPos).normalize(); // Direction: camera -> point

                    // Sphere radius
                    const sphereRadius = 0.06; // scale value
                    // Move sphere slightly away from camera toward surface
                    const offset = sphereRadius * 5; // Small offset toward surface
                    const clickedPosition = worldPos.clone().sub(viewDirection.mulScalar(offset));

                    this.createPointerSpawner();
                    setTimeout(() => {
                        const allSpheres = this.getDamageSphereRenderComponents();
                        if (allSpheres.length > 0) {
                            const lastSphere = allSpheres[allSpheres.length - 1];
                            lastSphere.entity.setPosition(clickedPosition);
                            this.renderNextFrame();
                        }
                    }, 100);
                } else {
                    // console.log(' No surface detected at click position (depth = 1)');
                    // console.log('️ Attempting AABB intersection fallback...');

                    // Fallback to AABB intersection
                    const plyEntity = this.getPlyEntity();
                    if (plyEntity?.gsplat?.instance?.meshInstance?.aabb) {
                        const aabb = plyEntity.gsplat.instance.meshInstance.aabb;

                        const near = new Vec3();
                        const far = new Vec3();
                        camera.screenToWorld(event.offsetX, event.offsetY, camera.nearClip, near);
                        camera.screenToWorld(event.offsetX, event.offsetY, camera.farClip, far);

                        const direction = new Vec3();
                        direction.sub2(far, near).normalize();

                        const cameraPos = this.camera.getPosition();
                        const intersectPoint = this.rayIntersectAABB(cameraPos, direction, aabb);

                        if (intersectPoint) {
                            const sphereRadius = 0.125 * 0.125;

                            // Calculate direction from AABB center to intersection point as pseudo-normal
                            const plyEntity = this.getPlyEntity();
                            if (plyEntity?.gsplat?.instance?.meshInstance?.aabb) {
                                const sphereRadius = 0.06; // scale value
                                const center = plyEntity.gsplat.instance.meshInstance.aabb.center;
                                const pseudoNormal = new Vec3();
                                // Move sphere slightly away from camera toward surface
                                const viewDirection = new Vec3();
                                viewDirection.sub2(cameraPos, intersectPoint).normalize();
                                const offset = sphereRadius * 5; // Small offset toward surface
                                const clickedPosition = intersectPoint.clone().sub(viewDirection.mulScalar(offset)); this.createPointerSpawner();
                                setTimeout(() => {
                                    const allSpheres = this.getDamageSphereRenderComponents();
                                    if (allSpheres.length > 0) {
                                        const lastSphere = allSpheres[allSpheres.length - 1];
                                        lastSphere.entity.setPosition(clickedPosition);
                                        this.renderNextFrame();
                                    }
                                }, 100);
                            }
                        }
                    }
                }
            })
            .catch((error: any) => {
                // console.error('Error reading depth buffer:', error);
            });
    }

    /**
     * Ray-AABB intersection test
     * Returns the intersection point or null if no intersection
     */
    private rayIntersectAABB(origin: Vec3, direction: Vec3, aabb: any): Vec3 | null {
        const min = new Vec3().sub2(aabb.center, aabb.halfExtents);
        const max = new Vec3().add2(aabb.center, aabb.halfExtents);

        let tmin = (min.x - origin.x) / direction.x;
        let tmax = (max.x - origin.x) / direction.x;

        if (tmin > tmax) [tmin, tmax] = [tmax, tmin];

        let tymin = (min.y - origin.y) / direction.y;
        let tymax = (max.y - origin.y) / direction.y;

        if (tymin > tymax) [tymin, tymax] = [tymax, tymin];

        if ((tmin > tymax) || (tymin > tmax)) return null;

        if (tymin > tmin) tmin = tymin;
        if (tymax < tmax) tmax = tymax;

        let tzmin = (min.z - origin.z) / direction.z;
        let tzmax = (max.z - origin.z) / direction.z;

        if (tzmin > tzmax) [tzmin, tzmax] = [tzmax, tzmin];

        if ((tmin > tzmax) || (tzmin > tmax)) return null;

        if (tzmin > tmin) tmin = tzmin;
        if (tzmax < tmax) tmax = tzmax;

        // Use the closest intersection point (tmin) if it's positive
        const t = tmin > 0 ? tmin : tmax;
        if (t < 0) return null;

        const intersectPoint = new Vec3();
        intersectPoint.add2(origin, direction.clone().mulScalar(t));

        return intersectPoint;
    }

    handleSphereClick(event: MouseEvent) {
        // Don't handle clicks if we're in creation mode or if inputs are focused
        if (this.isCreating || this.isInputFocused) {
            return;
        }

        // Check if the marker buttons are visible (markers mode is active)
        const markersDiv = document.getElementById('markers-div');
        if (!markersDiv || markersDiv.style.display === 'none') {
            return;
        }

        // Perform raycast to detect sphere
        const canvas = this.canvas;
        const camera = this.camera.camera;

        // Get all spheres
        const allSpheres = this.getDamageSphereRenderComponents();

        if (allSpheres.length === 0) {
            return;
        }

        // Check each sphere for intersection using screen-space distance
        let closestSphere: any = null;
        let closestScreenDistance = Infinity;
        const clickThreshold = 50; // pixels - increased for easier clicking

        allSpheres.forEach((sphereComponent, index) => {
            const sphere = sphereComponent.entity;

            // Only consider enabled (visible) spheres
            if (!sphere.enabled) {
                return;
            }

            const spherePos = sphere.getPosition();

            // Project sphere position to screen space
            const screenPos = new Vec3();
            camera.worldToScreen(spherePos, screenPos);

            // Calculate screen-space distance from click point
            const screenX = screenPos.x;
            const screenY = screenPos.y;
            const screenDistance = Math.sqrt(
                Math.pow(screenX - event.offsetX, 2) +
                Math.pow(screenY - event.offsetY, 2)
            );

            // Check if this sphere is closer and within click threshold
            if (screenDistance < clickThreshold && screenDistance < closestScreenDistance) {
                closestScreenDistance = screenDistance;
                closestSphere = { entity: sphere, index: index };
            }
        });

        // If a sphere was clicked, select it
        if (closestSphere) {
            const sphereName = closestSphere.entity.name;
            const sphereIndex = closestSphere.index;

            // Check if list is visible, if not, open it first
            const checkboxContainer = document.getElementById('position-sphere-checkbox-container');
            const listButton = document.getElementById('position-sphere-list') as HTMLButtonElement;

            if (checkboxContainer && checkboxContainer.style.display === 'none') {
                // Simulate clicking the list button to show the list
                if (listButton) {
                    listButton.click();
                }

                // Wait a bit for the list to be populated and React to render
                setTimeout(() => {
                    this.selectSphereInDropdown(sphereName, sphereIndex);
                }, 300);
            } else {
                // List is already visible, ensure it's updated and then select
                if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.updateSphereList === 'function') {
                    this.positionSphereButtonsComponent.updateSphereList();
                }

                // Small delay to ensure React has updated
                setTimeout(() => {
                    this.selectSphereInDropdown(sphereName, sphereIndex);
                }, 50);
            }
        }
    }

    /**
    * Handles clicks on camera-position 3D models (Camera3DModel.stl instances).
     * Uses the same screen-space proximity detection as handleSphereClick and
     * repositions the camera to focus on the clicked model, mirroring the
     * behaviour of moveCameraToSphere.
     */
    handleCameraModelClick(event: MouseEvent) {
        // Only act when the camera-position layer is visible
        if (!this.cameraPositionsVisible || this.cameraPositionEntities.length === 0) {
            return;
        }

        const camera = this.camera.camera;
        const cameraPos = this.camera.getPosition();
        const clickThreshold = 50; // pixels – same as sphere detection

        let closestEntity: Entity | null = null;
        // Primary key: world distance to viewer (picks the front-most camera
        // when several overlap on screen, avoiding accidental selection of a
        // camera that is visually behind the intended one).
        let closestWorldDistance = Infinity;

        this.cameraPositionEntities.forEach((entity) => {
            if (!entity.enabled) {
                return;
            }

            const entityPos = entity.getPosition();
            const screenPos = new Vec3();
            camera.worldToScreen(entityPos, screenPos);

            const screenDistance = Math.sqrt(
                Math.pow(screenPos.x - event.offsetX, 2) +
                Math.pow(screenPos.y - event.offsetY, 2)
            );

            if (screenDistance < clickThreshold) {
                const worldDistance = entityPos.distance(cameraPos);
                if (worldDistance < closestWorldDistance) {
                    closestWorldDistance = worldDistance;
                    closestEntity = entity;
                }
            }
        });

        if (closestEntity) {
            // Open the camera photo popup for the clicked entity (no camera movement)
            const entityIndex = this.cameraPositionEntities.indexOf(closestEntity as Entity);
            if (entityIndex >= 0 && this.cameraModelPhotoUrls[entityIndex]) {
                const s3Url = this.cameraModelPhotoUrls[entityIndex];
                const httpUrl = this.s3UrlToHttps(s3Url);
                const cameraId = this.cameraModelCameraIds[entityIndex] || (closestEntity as Entity).name.replace('camera-position-model-', '');
                if ((window as any).openCameraPhotoPopup) {
                    (window as any).openCameraPhotoPopup(cameraId, httpUrl, null, 'all');
                }
            }
        }
    }

    /** Converts an S3 URI (s3://bucket/key) to a public HTTPS URL. */
    private s3UrlToHttps(s3Url: string): string {
        const match = s3Url.match(/^s3:\/\/([^/]+)\/(.+)$/);
        if (!match) return s3Url;
        return `https://${match[1]}.s3.amazonaws.com/${match[2]}`;
    }

    private selectSphereInDropdown(sphereName: string, sphereIndex: number) {
        // Try to use the React component method for better state management
        if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.selectSphereByName === 'function') {
            this.positionSphereButtonsComponent.selectSphereByName(sphereName, sphereIndex);
        } else {
            // Fallback: Update the dropdown selection directly
            const sphereSelect = document.getElementById('sphere-select') as HTMLSelectElement;
            if (sphereSelect) {
                sphereSelect.value = sphereName;

                // Create a synthetic React event
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set;
                if (nativeInputValueSetter) {
                    nativeInputValueSetter.call(sphereSelect, sphereName);
                }

                // Trigger React onChange
                const event = new Event('input', { bubbles: true });
                sphereSelect.dispatchEvent(event);

                const changeEvent = new Event('change', { bubbles: true });
                sphereSelect.dispatchEvent(changeEvent);
            }

            // Move camera to sphere
            this.moveCameraToSphere(sphereIndex);
        }
    }

    selectedPointerSpawner() {
        // Handle the selection of a sphere, get all spheres and their data
        const checkboxContainer = document.getElementById('position-sphere-checkbox-container');
        const editButton = document.getElementById('position-sphere-edit');
        const allSpheres = this.getDamageSphereRenderComponents();
        let nameSpheres: string[] = [];
        let descSpheres: string[] = [];

        if (allSpheres.length > 0) {
            if (editButton) editButton.style.display = 'block';
            // Don't show info panel here - it will be shown when a sphere is selected
            // positionSphereInfoPanel.style.display = 'block';
            this.hideNonEssentialButtons(true, true, true, true, true, false, false, false, false);
        }
        //console.group(editButton.style.display);

        allSpheres?.forEach((s, index) => {
            // Get the actual name of the entity (it might have been renamed)
            nameSpheres.push(s.entity.name);
            descSpheres.push(this.descriptionsPointer[index] || '');
        });

        //console.log('All spheres in the scene:', nameSpheres.length);
        //console.log('Sphere names:', nameSpheres);
        return { nameSpheres, descSpheres };
    }

    showCheckboxPointerSpawner() {

        const checkboxContainer = document.getElementById('position-sphere-checkbox-container');
        if (checkboxContainer) {
            checkboxContainer.style.display = 'block';
        }
    }

    removePointerSpawner() {
        alert(t('markers.legacyReadOnly'));

    }

    private getCameraIndex(cameraId: string): number {
        return this.cameraIndexById.get(cameraId) ?? -1;
    }

    private isCameraPositionMarker(entity: Entity): boolean {
        return entity.tags.has('camera-position-marker') || entity.name.startsWith('camera-position-model-');
    }

    public getDamageSphereRenderComponents() {
        return this.app.root.findComponents('render').filter((component) => {
            return component.entity.render.type === 'sphere' && !this.isCameraPositionMarker(component.entity);
        });
    }

    private getCameraNumericOrderValue(index: number): number {
        const raw = (this.cameraModelPhotoIds[index] || this.cameraModelCameraIds[index] || '').toString();
        const matches = raw.match(/\d+/g);
        if (!matches || !matches.length) {
            return Number.POSITIVE_INFINITY;
        }

        const parsed = Number.parseInt(matches[matches.length - 1], 10);
        return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
    }

    public getCameraPhotoEntries(cameraIds?: string[]): Array<{ cameraId: string; imageUrl: string; imageName: string }> {
        const validIds = Array.isArray(cameraIds)
            ? cameraIds.filter((value) => typeof value === 'string' && value.trim() !== '')
            : [];

        const idFilter = validIds.length ? new Set(validIds) : null;
        const entries: Array<{ cameraId: string; imageUrl: string; imageName: string; order: number }> = [];

        this.cameraModelCameraIds.forEach((cameraId, index) => {
            if (!cameraId || (idFilter && !idFilter.has(cameraId))) {
                return;
            }

            const s3Url = this.cameraModelPhotoUrls[index];
            if (!s3Url) {
                return;
            }

            entries.push({
                cameraId,
                imageUrl: this.s3UrlToHttps(s3Url),
                imageName: this.cameraModelPhotoIds[index] || '',
                order: this.getCameraNumericOrderValue(index)
            });
        });

        entries.sort((a, b) => {
            if (a.order !== b.order) {
                return a.order - b.order;
            }

            const byName = a.imageName.localeCompare(b.imageName);
            if (byName !== 0) {
                return byName;
            }

            return a.cameraId.localeCompare(b.cameraId);
        });

        return entries.map(({ cameraId, imageUrl, imageName }) => ({ cameraId, imageUrl, imageName }));
    }

    public getClosestCameraPhotoEntryInDirection(
        currentCameraId: string,
        direction: 'left' | 'right' | 'up' | 'down',
        cameraIds?: string[]
    ): { cameraId: string; imageUrl: string; imageName: string } | null {
        const currentIndex = this.getCameraIndex(currentCameraId);
        if (currentIndex < 0) {
            return null;
        }

        const validIds = Array.isArray(cameraIds)
            ? cameraIds.filter((value) => typeof value === 'string' && value.trim() !== '')
            : [];
        const idFilter = validIds.length ? new Set(validIds) : null;

        if (direction === 'left' || direction === 'right') {
            const ordered = this.cameraModelCameraIds
                .map((cameraId, index) => ({ cameraId, index }))
                .filter((entry) => {
                    if (!entry.cameraId) {
                        return false;
                    }
                    if (idFilter && !idFilter.has(entry.cameraId)) {
                        return false;
                    }
                    return Boolean(this.cameraModelPhotoUrls[entry.index]);
                })
                .sort((a, b) => {
                    const aOrder = this.getCameraNumericOrderValue(a.index);
                    const bOrder = this.getCameraNumericOrderValue(b.index);

                    if (aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }

                    const aName = this.cameraModelPhotoIds[a.index] || '';
                    const bName = this.cameraModelPhotoIds[b.index] || '';
                    const byName = aName.localeCompare(bName);
                    if (byName !== 0) {
                        return byName;
                    }

                    return a.cameraId.localeCompare(b.cameraId);
                });

            if (ordered.length <= 1) {
                return null;
            }

            const currentOrderedIndex = ordered.findIndex((entry) => entry.cameraId === currentCameraId);
            if (currentOrderedIndex < 0) {
                return null;
            }

            const step = direction === 'right' ? 1 : -1;
            const nextOrderedIndex = (currentOrderedIndex + step + ordered.length) % ordered.length;
            const picked = ordered[nextOrderedIndex];
            const s3Url = this.cameraModelPhotoUrls[picked.index];
            if (!s3Url) {
                return null;
            }

            return {
                cameraId: picked.cameraId,
                imageUrl: this.s3UrlToHttps(s3Url),
                imageName: this.cameraModelPhotoIds[picked.index] || ''
            };
        }

        const currentPosition = this.cameraModelApiPositions[currentIndex];
        if (!currentPosition) {
            return null;
        }

        type Candidate = {
            index: number;
            cameraId: string;
            position: Vec3;
            distance: number;
            deltaX: number;
            deltaY: number;
            deltaZ: number;
        };
        const candidates: Candidate[] = [];

        this.cameraModelCameraIds.forEach((cameraId, index) => {
            if (!cameraId || cameraId === currentCameraId) {
                return;
            }

            if (idFilter && !idFilter.has(cameraId)) {
                return;
            }

            const position = this.cameraModelApiPositions[index];
            const photoUrl = this.cameraModelPhotoUrls[index];
            if (!position || !photoUrl) {
                return;
            }

            const dx = position.x - currentPosition.x;
            const dy = position.y - currentPosition.y;
            const dz = position.z - currentPosition.z;
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

            candidates.push({
                index,
                cameraId,
                position,
                distance,
                deltaX: dx,
                deltaY: dy,
                deltaZ: dz
            });
        });

        if (!candidates.length) {
            return null;
        }

        const epsilon = 0.0001;
        const directional = candidates.filter((candidate) => {
            switch (direction) {
                case 'up':
                    return candidate.deltaZ < -epsilon;
                case 'down':
                    return candidate.deltaZ > epsilon;
                default:
                    return false;
            }
        });

        const pickDirectionalCandidate = (list: Candidate[]): Candidate | null => {
            if (!list.length) {
                return null;
            }

            const getAxisScores = (item: Candidate) => {
                return {
                    primary: Math.abs(item.deltaZ),
                    secondary: Math.abs(item.deltaX) + Math.abs(item.deltaY) * 0.25
                };
            };

            return list.reduce((best, item) => {
                const bestScore = getAxisScores(best);
                const itemScore = getAxisScores(item);

                if (itemScore.primary < bestScore.primary - epsilon) {
                    return item;
                }

                if (Math.abs(itemScore.primary - bestScore.primary) <= epsilon) {
                    if (itemScore.secondary < bestScore.secondary - epsilon) {
                        return item;
                    }

                    if (Math.abs(itemScore.secondary - bestScore.secondary) <= epsilon && item.distance < best.distance) {
                        return item;
                    }
                }

                return best;
            });
        };

        const pickVerticalWrapCandidate = (list: Candidate[]): Candidate | null => {
            if (!list.length) {
                return null;
            }

            return list.reduce((best, item) => {
                const bestLateral = Math.abs(best.deltaX) + Math.abs(best.deltaY) * 0.25;
                const itemLateral = Math.abs(item.deltaX) + Math.abs(item.deltaY) * 0.25;

                if (itemLateral < bestLateral - epsilon) {
                    return item;
                }

                if (Math.abs(itemLateral - bestLateral) <= epsilon && item.distance < best.distance) {
                    return item;
                }

                return best;
            });
        };

        let picked = pickDirectionalCandidate(directional);

        if (!picked) {
            // For vertical wrap, prefer the opposite camera aligned with the same side
            // to avoid jumps across the vehicle.
            const oppositeDirectional = candidates.filter((candidate) => {
                return direction === 'up'
                    ? candidate.deltaZ > epsilon
                    : candidate.deltaZ < -epsilon;
            });

            picked = pickVerticalWrapCandidate(oppositeDirectional);

            if (!picked) {
                const sortedByZ = [...candidates].sort((a, b) => a.position.z - b.position.z);
                picked = direction === 'up' ? sortedByZ[sortedByZ.length - 1] : sortedByZ[0];
            }
        }

        if (!picked) {
            return null;
        }

        const s3Url = this.cameraModelPhotoUrls[picked.index];
        if (!s3Url) {
            return null;
        }

        return {
            cameraId: picked.cameraId,
            imageUrl: this.s3UrlToHttps(s3Url),
            imageName: this.cameraModelPhotoIds[picked.index] || ''
        };
    }

    public highlightCameraDamageSelection(cameraIds: string[] | null): void {
        const normalized = Array.isArray(cameraIds)
            ? cameraIds.filter((value) => typeof value === 'string' && value.trim() !== '')
            : [];

        this.selectedCameraDamageIds = new Set(normalized);
        this.applyCameraDamageColors();
    }

    private getCameraApiPosition(cameraId: string): Vec3 | null {
        const index = this.getCameraIndex(cameraId);
        if (index < 0) {
            return null;
        }
        return this.cameraModelApiPositions[index] ?? null;
    }

    public focusCameraDamage(cameraId: string): boolean {
        const index = this.getCameraIndex(cameraId);
        if (index < 0) {
            return false;
        }

        const entity = this.cameraPositionEntities[index];
        if (!entity) {
            return false;
        }

        this.multiCamera.focus(entity.getPosition());
        this.renderNextFrame();
        return true;
    }

    public openCameraDamageEditor(cameraId: string): boolean {
        const index = this.getCameraIndex(cameraId);
        if (index < 0) {
            return false;
        }

        const s3Url = this.cameraModelPhotoUrls[index];
        if (!s3Url) {
            return false;
        }

        const httpUrl = this.s3UrlToHttps(s3Url);
        if ((window as any).openCameraPhotoPopup) {
            (window as any).openCameraPhotoPopup(cameraId, httpUrl);
            return true;
        }

        return false;
    }

    private getCameraDefaultPhotoIds(cameraId: string): Array<string | number> {
        const index = this.getCameraIndex(cameraId);
        if (index < 0) {
            return [];
        }

        const photoId = this.cameraModelPhotoIds[index];
        if (photoId && photoId.trim() !== '') {
            return [photoId];
        }

        return [];
    }

    private getLegacyDamageRecordsFromSpheres(): DamageRecord[] {
        const allSpheres = this.getDamageSphereRenderComponents();
        return allSpheres.map((pointer, index) => {
            const position = pointer.entity.getPosition();
            return {
                id: `legacy-${index}-${Date.now()}`,
                name: pointer.entity.name || '',
                description: this.descriptionsPointer[index] || '',
                selectValue: this.selectPointer[index] || '',
                photoIds: this.photosPointer[index] || [],
                position: {
                    x: position.x.toString(),
                    y: position.y.toString(),
                    z: position.z.toString()
                }
            };
        });
    }

    private buildDamageListForApi(): DamageRecord[] {
        return [...this.cameraDamageRecords, ...this.getLegacyDamageRecordsFromSpheres()];
    }

    private normalizeDamageNotes(notes: unknown): DamageNote[] {
        if (!Array.isArray(notes)) {
            return [];
        }

        return notes
            .map((entry, index) => {
                const dateTime = typeof (entry as any)?.dateTime === 'string'
                    ? (entry as any).dateTime.trim()
                    : '';
                const author = typeof (entry as any)?.author === 'string'
                    ? (entry as any).author.trim()
                    : '';
                const message = typeof (entry as any)?.message === 'string'
                    ? (entry as any).message.trim()
                    : '';
                const rawId = typeof (entry as any)?.id === 'string' ? (entry as any).id.trim() : '';

                if (!author || !message) {
                    return null;
                }

                return {
                    id: rawId || `note-${Date.now()}-${index}-${Math.floor(Math.random() * 1000)}`,
                    dateTime: dateTime || new Date().toISOString(),
                    author,
                    message
                };
            })
            .filter((note): note is DamageNote => Boolean(note));
    }

    private mergeDamageNotes(target: DamageNote[], incoming: DamageNote[]): DamageNote[] {
        const merged = [...target];
        const indexById = new Map<string, number>();

        merged.forEach((note, index) => {
            indexById.set(note.id, index);
        });

        incoming.forEach((note) => {
            const existingIndex = indexById.get(note.id);
            if (existingIndex === undefined) {
                indexById.set(note.id, merged.length);
                merged.push({ ...note });
                return;
            }

            merged[existingIndex] = {
                ...merged[existingIndex],
                ...note
            };
        });

        return merged;
    }

    private shouldCameraMarkerBeVisible(cameraId: string): boolean {
        if (!this.cameraPositionsVisible) {
            return false;
        }

        if (!this.cameraVisibilityFilterIds || this.cameraVisibilityFilterIds.size === 0) {
            return true;
        }

        return this.cameraVisibilityFilterIds.has(cameraId);
    }

    private applyCameraMarkerVisibility(): void {
        this.cameraPositionEntities.forEach((entity, index) => {
            const cameraId = this.cameraModelCameraIds[index] || '';
            entity.enabled = this.shouldCameraMarkerBeVisible(cameraId);
        });

        this.renderNextFrame();
    }

    private normalizePhotoId(value: string | number): number | null {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return Math.trunc(value);
        }

        if (typeof value !== 'string') {
            return null;
        }

        const normalized = value.trim();
        if (normalized === '') {
            return null;
        }

        if (/^\d+$/.test(normalized)) {
            return parseInt(normalized, 10);
        }

        const match = normalized.match(/(\d+)/g);
        if (!match || match.length === 0) {
            return null;
        }

        return parseInt(match[match.length - 1], 10);
    }

    private normalizePhotoIdsForApi(photoIds: Array<string | number>): number[] {
        const result: number[] = [];
        const seen = new Set<number>();

        photoIds.forEach((value) => {
            const parsed = this.normalizePhotoId(value);
            if (parsed === null || seen.has(parsed)) {
                return;
            }
            seen.add(parsed);
            result.push(parsed);
        });

        return result;
    }

    private generateDamageId(): string {
        return `damage-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    }

    private getCameraDamages(cameraId: string): DamageRecord[] {
        const entries = this.cameraDamageByCameraId.get(cameraId);
        return entries ? [...entries] : [];
    }

    private findCameraDamageById(cameraId: string, damageId?: string): DamageRecord | null {
        const list = this.getCameraDamages(cameraId);
        if (!list.length) {
            return null;
        }

        const normalizedId = typeof damageId === 'string' ? damageId.trim() : '';
        if (normalizedId) {
            const target = list.find((entry) => entry.id === normalizedId);
            if (target) {
                return target;
            }
        }

        return list[0];
    }

    private rebuildCameraDamageIndex(): void {
        this.cameraDamageByCameraId.clear();

        this.cameraDamageRecords.forEach((record) => {
            if (!record.id || record.id.trim() === '') {
                record.id = this.generateDamageId();
            }

            const cameraIds = (record.cameraIds && record.cameraIds.length > 0)
                ? record.cameraIds
                : (record.cameraId ? [record.cameraId] : []);

            record.cameraIds = cameraIds;
            record.cameraId = cameraIds[0];

            cameraIds.forEach((id) => {
                const existing = this.cameraDamageByCameraId.get(id) || [];
                existing.push(record);
                this.cameraDamageByCameraId.set(id, existing);
            });
        });
    }

    private findCameraIdByPosition(position: Vec3, epsilon = 0.001): string | null {
        for (let i = 0; i < this.cameraModelApiPositions.length; i++) {
            const camPos = this.cameraModelApiPositions[i];
            if (
                Math.abs(camPos.x - position.x) <= epsilon &&
                Math.abs(camPos.y - position.y) <= epsilon &&
                Math.abs(camPos.z - position.z) <= epsilon
            ) {
                return this.cameraModelCameraIds[i] || null;
            }
        }
        return null;
    }

    private applyCameraDamageColors(): void {
        const damagedCameraIds = new Set<string>();
        this.cameraDamageRecords.forEach((record) => {
            const ids = record.cameraIds || (record.cameraId ? [record.cameraId] : []);
            ids.forEach(id => damagedCameraIds.add(id));
        });

        this.cameraModelMaterials.forEach((material, index) => {
            const cameraId = this.cameraModelCameraIds[index];
            const isSelected = this.selectedCameraDamageIds.has(cameraId);
            const hasDamage = damagedCameraIds.has(this.cameraModelCameraIds[index]);
            if (isSelected) {
                material.diffuse.copy(CAMERA_MODEL_SELECTED_COLOR);
                material.emissive.set(0.18, 0.12, 0);
            } else if (hasDamage) {
                material.diffuse.set(1, 0, 0);
                material.emissive.set(0.15, 0, 0);
            } else {
                material.diffuse.copy(CAMERA_MODEL_COLOR);
                material.emissive.set(0, 0.12, 0.11);
            }
            material.update();
        });

        this.renderNextFrame();
    }

    public getCameraDamageContext(
        cameraId: string,
        selectedDamageId?: string
    ): { current: DamageRecord | null; damages: DamageRecord[]; options: CameraDamageOption[] } {
        const damages = this.getCameraDamages(cameraId);
        const current = this.findCameraDamageById(cameraId, selectedDamageId);
        const options = this.cameraDamageRecords
            .map((record) => ({
                id: record.id,
                name: record.name,
                description: record.description,
                selectValue: record.selectValue,
                cameraIds: record.cameraIds || (record.cameraId ? [record.cameraId] : [])
            }))
            .filter(record => !record.cameraIds.includes(cameraId))
            .map(({ id, name, description, selectValue }) => ({ id, name, description, selectValue }));

        return {
            current,
            damages: damages.map((entry) => ({ ...entry })),
            options
        };
    }

    public createOrUpdateCameraDamage(
        cameraId: string,
        payload: { name: string; description: string; selectValue: string },
        damageId?: string
    ): boolean {
        const cameraPosition = this.getCameraApiPosition(cameraId);
        if (!cameraPosition) {
            return false;
        }

        const defaultPhotoIds = this.getCameraDefaultPhotoIds(cameraId);
        const normalizedDamageId = typeof damageId === 'string' ? damageId.trim() : '';
        const existing = normalizedDamageId
            ? this.findCameraDamageById(cameraId, normalizedDamageId)
            : null;

        if (existing) {
            existing.name = payload.name.trim();
            existing.description = payload.description.trim();
            existing.selectValue = payload.selectValue.trim();
            existing.position = {
                x: cameraPosition.x.toString(),
                y: cameraPosition.y.toString(),
                z: cameraPosition.z.toString()
            };
            existing.notes = this.normalizeDamageNotes(existing.notes);
            const mergedPhotoIds = [...existing.photoIds, ...defaultPhotoIds];
            existing.photoIds = mergedPhotoIds.filter((value, index, arr) => arr.indexOf(value) === index);
            existing.cameraIds = existing.cameraIds || (existing.cameraId ? [existing.cameraId] : []);
            if (!existing.cameraIds.includes(cameraId)) {
                existing.cameraIds.push(cameraId);
            }
            existing.cameraId = existing.cameraIds[0];
        } else {
            this.cameraDamageRecords.push({
                id: this.generateDamageId(),
                name: payload.name.trim(),
                description: payload.description.trim(),
                selectValue: payload.selectValue.trim(),
                photoIds: defaultPhotoIds,
                position: {
                    x: cameraPosition.x.toString(),
                    y: cameraPosition.y.toString(),
                    z: cameraPosition.z.toString()
                },
                notes: [],
                cameraId,
                cameraIds: [cameraId]
            });
        }

        this.rebuildCameraDamageIndex();
        this.applyCameraDamageColors();
        return true;
    }

    public assignExistingDamageToCamera(cameraId: string, optionId: string): boolean {
        const normalizedOptionId = optionId?.trim() || '';
        if (!normalizedOptionId) {
            return false;
        }

        const source = this.cameraDamageRecords.find((entry) => entry.id === normalizedOptionId);
        if (!source) {
            return false;
        }

        source.cameraIds = source.cameraIds || (source.cameraId ? [source.cameraId] : []);
        if (!source.cameraIds.includes(cameraId)) {
            source.cameraIds.push(cameraId);
        }
        source.cameraId = source.cameraIds[0];

        const defaultPhotoIds = this.getCameraDefaultPhotoIds(cameraId);
        const mergedPhotoIds = [...source.photoIds, ...defaultPhotoIds];
        source.photoIds = mergedPhotoIds.filter((value, index, arr) => arr.indexOf(value) === index);

        this.rebuildCameraDamageIndex();
        this.applyCameraDamageColors();

        return true;
    }

    public deleteCameraDamage(cameraId: string, damageId?: string): boolean {
        const existing = this.findCameraDamageById(cameraId, damageId);
        if (!existing) {
            return false;
        }

        existing.cameraIds = (existing.cameraIds || []).filter(id => id !== cameraId);
        existing.cameraId = existing.cameraIds[0];

        if (!existing.cameraIds.length) {
            this.cameraDamageRecords = this.cameraDamageRecords.filter(record => record !== existing);
        }

        this.rebuildCameraDamageIndex();
        this.applyCameraDamageColors();

        return true;
    }

    public getCameraDamageNotes(cameraId: string, damageId?: string): DamageNote[] {
        const existing = this.findCameraDamageById(cameraId, damageId);
        if (!existing) {
            return [];
        }

        existing.notes = this.normalizeDamageNotes(existing.notes);
        return existing.notes.map((note) => ({ ...note }));
    }

    public addCameraDamageNote(
        cameraId: string,
        damageId: string | undefined,
        payload: { dateTime: string; author: string; message: string }
    ): boolean {
        const existing = this.findCameraDamageById(cameraId, damageId);
        if (!existing) {
            return false;
        }

        const author = payload.author?.trim() || '';
        const message = payload.message?.trim() || '';
        if (!author || !message) {
            return false;
        }

        existing.notes = this.normalizeDamageNotes(existing.notes);
        existing.notes.push({
            id: `note-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
            dateTime: payload.dateTime?.trim() || new Date().toISOString(),
            author,
            message
        });

        return true;
    }

    public updateCameraDamageNote(
        cameraId: string,
        damageId: string | undefined,
        noteId: string,
        payload: { dateTime: string; author: string; message: string }
    ): boolean {
        const existing = this.findCameraDamageById(cameraId, damageId);
        if (!existing) {
            return false;
        }

        const normalizedNoteId = noteId?.trim() || '';
        const author = payload.author?.trim() || '';
        const message = payload.message?.trim() || '';
        if (!normalizedNoteId || !author || !message) {
            return false;
        }

        existing.notes = this.normalizeDamageNotes(existing.notes);
        const note = existing.notes.find((entry) => entry.id === normalizedNoteId);
        if (!note) {
            return false;
        }

        note.dateTime = payload.dateTime?.trim() || note.dateTime || new Date().toISOString();
        note.author = author;
        note.message = message;

        return true;
    }

    public deleteCameraDamageNote(cameraId: string, damageId: string | undefined, noteId: string): boolean {
        const existing = this.findCameraDamageById(cameraId, damageId);
        if (!existing) {
            return false;
        }

        const normalizedNoteId = noteId?.trim() || '';
        if (!normalizedNoteId) {
            return false;
        }

        existing.notes = this.normalizeDamageNotes(existing.notes);
        const initialLength = existing.notes.length;
        existing.notes = existing.notes.filter((entry) => entry.id !== normalizedNoteId);
        return existing.notes.length !== initialLength;
    }

    public setCameraDamageNavigationFilter(cameraIds: string[] | null): void {
        const normalized = Array.isArray(cameraIds)
            ? cameraIds.filter((value) => typeof value === 'string' && value.trim() !== '')
            : [];

        this.cameraVisibilityFilterIds = normalized.length ? new Set(normalized) : null;
        this.applyCameraMarkerVisibility();
    }

    public clearCameraDamageNavigationFilter(): void {
        this.cameraVisibilityFilterIds = null;
        this.applyCameraMarkerVisibility();
    }

    async saveAllPointers() {
        try {
            const apiConfig = await this.resolveApiConfigByRequestId();

            // Validate requestId exists
            if (!this.requestId || this.requestId.trim() === '') {
                console.error('No requestId available for saving damages');
                alert(t('api.noRequestId'));
                this.observer.set('ui.error', t('api.noRequestId'));
                return;
            }

            const damageList = this.buildDamageListForApi();
            if (damageList.length === 0) {
                console.warn('No damage markers to save');
                alert('No hay daños para guardar');
                return;
            }

            const apiDamageList = damageList.map((damage) => {
                const cameraIds = (damage.cameraIds && damage.cameraIds.length > 0)
                    ? damage.cameraIds
                    : (damage.cameraId ? [damage.cameraId] : []);

                return {
                    id: damage.id,
                    name: damage.name,
                    description: damage.description,
                    selectValue: damage.selectValue,
                    photoIds: this.normalizePhotoIdsForApi(damage.photoIds || []),
                    position: damage.position,
                    notes: this.normalizeDamageNotes((damage as any).notes),
                    cameraId: cameraIds[0],
                    cameraIds: cameraIds
                };
            });

            this.updateLoadingProgress(0, t('api.savingDamages'));
            this.observer.set('ui.spinner', true);

            // Step 1: Check if damages already exist (GET)
            const checkUrl = `${apiConfig.baseUrl}/${apiConfig.stagePath}/${this.API_PREFIX}/damages/${this.requestId}`;

            
            let damagesExist = false;
            let existingMarkerMetadataEntries: any[] = [];
            try {
                const checkResponse = await fetch(checkUrl, {
                    method: 'GET',
                    mode: 'cors',
                    headers: this.buildAuthHeaders()
                });
                
                if (checkResponse.ok) {
                    const existingData = await checkResponse.json();
                    damagesExist = true;
                    const existingDamageList = Array.isArray(existingData?.damageList) ? existingData.damageList : [];
                    existingMarkerMetadataEntries = existingDamageList.filter((entry: any) => {
                        return entry?.markerMetadata === true || entry?.name === '__camera_markers__';
                    });
                } else if (checkResponse.status === 404) {
                    damagesExist = false;
                }
            } catch (checkError) {
                console.warn('Error checking existing damages (will try POST):', checkError.message);
                damagesExist = false;
            }

            // Step 2: Use POST (create) or PUT (update) based on existence
            const method = damagesExist ? 'PUT' : 'POST';
            const apiUrl = damagesExist 
                ? `${apiConfig.baseUrl}/${apiConfig.stagePath}/${this.API_PREFIX}/damages/${this.requestId}`
                : `${apiConfig.baseUrl}/${apiConfig.stagePath}/${this.API_PREFIX}/damages`;

            const mergedDamageList = apiDamageList.concat(existingMarkerMetadataEntries);
            
            const apiPayload = damagesExist
                ? {
                    damageList: mergedDamageList
                }
                : {
                    requestId: this.requestId,
                    damageList: mergedDamageList
                };

            // Make the API call
            const response = await fetch(apiUrl, {
                method: method,
                mode: 'cors',
                headers: this.buildAuthHeaders(),
                body: JSON.stringify(apiPayload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API error response:', errorText);
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();

            this.updateLoadingProgress(100, method === 'POST' ? t('api.damagesSaved') : t('api.damagesUpdated'));
            this.observer.set('ui.spinner', false);

            // Clear loading progress message after 20 seconds
            setTimeout(() => {
                this.updateLoadingProgress(0, '');
            }, 20000);

        } catch (error) {
            console.error('Error saving damages to API:', error);

            // More detailed error message for CORS and network issues
            let errorMessage = error.message;
            let errorDetail = '';

            // Check if it's a CORS error
            if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
                errorDetail = 'CORS error - El servidor API debe configurar Access-Control-Allow-Origin. ';
                errorMessage = 'Error de CORS: El servidor no permite peticiones desde este origen';
            }

            console.error('Error details:', {
                message: error.message,
                requestId: this.requestId,
                apiUrl: `${this.API_BASE_URL}/${this.API_STAGE_PATH}/${this.API_PREFIX}/damages`
            });

            this.observer.set('ui.error', `${t('api.errorSavingDamages')}: ${errorMessage}`);
            this.observer.set('ui.spinner', false);
            this.updateLoadingProgress(0, '');

            // Fallback: Export as JSON if API fails
            console.warn('Falling back to JSON export due to API failure');
            alert(`${errorDetail}${errorMessage}\\n\\nSe exportará a JSON como alternativa.`);
            this.exportAllPointers();

            // Clear error message after 20 seconds
            setTimeout(() => {
                this.observer.set('ui.error', null);
            }, 20000);
        }
    }

    async updateDamagesInAPI() {
        await this.saveAllPointers();
    }

    async loadDamagesFromAPI() {
        try {
            const apiConfig = await this.resolveApiConfigByRequestId();

            // Validate requestId exists
            if (!this.requestId || this.requestId.trim() === '') {
                console.warn('No requestId available to load damages');
                alert(t('api.noRequestId'));
                return;
            }

            const damagesUrl = (baseUrl: string, stagePath: 'pre' | 'pro') =>
                `${baseUrl}/${stagePath}/${this.API_PREFIX}/damages/${this.requestId}`;

            const fetchDamages = (baseUrl: string, stagePath: 'pre' | 'pro') =>
                fetch(damagesUrl(baseUrl, stagePath), {
                    method: 'GET',
                    mode: 'cors',
                    headers: this.buildAuthHeaders()
                });

            let apiUrl = damagesUrl(apiConfig.baseUrl, apiConfig.stagePath);

            // Make the API call (without custom headers to avoid CORS preflight issues)
            this.updateLoadingProgress(0, t('api.loadingDamages'));
            this.observer.set('ui.spinner', true);

            let response = await fetchDamages(apiConfig.baseUrl, apiConfig.stagePath);

            if (response.status === 404) {
                const explicitStageFromLoad = this.getStageHintFromLoadParam();

                if (explicitStageFromLoad) {
                    console.warn(
                        `[DamagesAPI] Damages not found in ${explicitStageFromLoad.toUpperCase()} for request ${this.requestId}. ` +
                        'Skipping cross-environment fallback because stage is explicit in ?load='
                    );
                } else {
                    const fallbackStage: 'pre' | 'prod' = apiConfig.stage === 'pre' ? 'prod' : 'pre';
                    const fallbackBase = this.API_BASE_URLS[fallbackStage];
                    const fallbackPath = this.API_STAGE_PATHS[fallbackStage];
                    const fallbackResponse = await fetchDamages(fallbackBase, fallbackPath);

                    if (fallbackResponse.ok) {
                        this.setEnvironment(fallbackStage);
                        response = fallbackResponse;
                        apiUrl = damagesUrl(fallbackBase, fallbackPath);
                        console.info(`[DamagesAPI] Switched API environment to ${fallbackStage.toUpperCase()} for request ${this.requestId}`);
                    } else if (fallbackResponse.status !== 404) {
                        response = fallbackResponse;
                        apiUrl = damagesUrl(fallbackBase, fallbackPath);
                    }
                }
            }

            if (response.status === 404) {
                console.info('No damages found yet for this requestId. Continuing without damage markers.');

                this.cameraDamageByCameraId.clear();
                this.cameraDamageRecords = [];
                this.selectedCameraDamageIds.clear();
                this.applyCameraDamageColors();

                this.observer.set('ui.spinner', false);
                this.updateLoadingProgress(0, '');
                this.renderNextFrame();
                return;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API error response:', errorText);
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();

            // Create standard material for damage markers
            const materialSphere = new pc.StandardMaterial();
            materialSphere.gloss = 0.5;
            materialSphere.metalness = 1;
            materialSphere.diffuse = new pc.Color(1, 0, 0);
            materialSphere.useMetalness = false;

            // Clear existing damage markers
            const existingSpheres = this.getDamageSphereRenderComponents();
            existingSpheres.forEach(sphere => {
                sphere.entity.destroy();
            });

            // Clear arrays
            this.descriptionsPointer = [];
            this.selectPointer = [];
            this.photosPointer = [];
            this.spheresArray = [];
            this.gizmosArray = [];
            this.cameraDamageByCameraId.clear();
            this.cameraDamageRecords = [];

            // Process loaded damages
            const damageList = result.damageList || [];
            
            if (damageList.length === 0) {
                console.warn('No damages found in API response');
                alert(t('api.noDamagesFound'));
                this.observer.set('ui.spinner', false);
                this.updateLoadingProgress(0, '');
                return;
            }

            // Create damage markers from API data.
            // Camera-linked damages are stored in cameraDamageRecords and highlighted in red.
            // Legacy manual damages are still rendered as read-only spheres.
            damageList.forEach((damage: any, index: number) => {
                if (damage?.markerMetadata === true || damage?.name === '__camera_markers__') {
                    return;
                }

                const name = damage.name || damage.damageType || `Damage ${index + 1}`;
                const description = damage.description || '';
                const selectValue = damage.location || damage.selectValue || '';
                const photoIds = damage.photoIds || [];
                const notes = this.normalizeDamageNotes(damage.notes);
                const recordId = typeof damage.id === 'string' && damage.id.trim() !== ''
                    ? damage.id.trim()
                    : this.generateDamageId();

                if (!damage.position || damage.position.x === undefined) {
                    return;
                }

                const x = typeof damage.position.x === 'string' ? parseFloat(damage.position.x) : damage.position.x;
                const y = typeof damage.position.y === 'string' ? parseFloat(damage.position.y) : damage.position.y;
                const z = typeof damage.position.z === 'string' ? parseFloat(damage.position.z) : damage.position.z;
                const position = new Vec3(x, y, z);

                let cameraIds: string[] = [];
                if (Array.isArray(damage.cameraIds)) {
                    cameraIds = damage.cameraIds.filter((value: unknown) => typeof value === 'string' && value.trim() !== '');
                }

                if (!cameraIds.length && typeof damage.cameraId === 'string' && damage.cameraId.trim() !== '') {
                    cameraIds = [damage.cameraId];
                }

                if (!cameraIds.length) {
                    const matchedCamera = this.findCameraIdByPosition(position);
                    if (matchedCamera) {
                        cameraIds = [matchedCamera];
                    }
                }

                if (cameraIds.length) {
                    const existingDamage = this.cameraDamageRecords.find((record) => record.id === recordId);

                    if (existingDamage) {
                        existingDamage.cameraIds = existingDamage.cameraIds || [];
                        cameraIds.forEach((id) => {
                            if (!existingDamage.cameraIds.includes(id)) {
                                existingDamage.cameraIds.push(id);
                            }
                        });
                        const mergedPhotoIds = [...existingDamage.photoIds, ...photoIds];
                        existingDamage.photoIds = mergedPhotoIds.filter((value, idx, arr) => arr.indexOf(value) === idx);
                        existingDamage.notes = this.mergeDamageNotes(
                            this.normalizeDamageNotes(existingDamage.notes),
                            notes
                        );
                        existingDamage.cameraId = existingDamage.cameraIds[0];
                    } else {
                        this.cameraDamageRecords.push({
                            id: recordId,
                            name,
                            description,
                            selectValue,
                            photoIds,
                            position: {
                                x: position.x.toString(),
                                y: position.y.toString(),
                                z: position.z.toString()
                            },
                            notes,
                            cameraId: cameraIds[0],
                            cameraIds
                        });
                    }
                    return;
                }

                this.descriptionsPointer.push(description);
                this.selectPointer.push(selectValue);
                this.photosPointer.push(photoIds);

                const sphere = new Entity(name);
                sphere.addComponent('render', {
                    type: 'sphere',
                    material: materialSphere
                });
                sphere.setLocalScale(0.125, 0.125, 0.125);
                sphere.setPosition(position);
                sphere.tags.add('sphere');
                this.app.root.addChild(sphere);
                this.spheresArray.push(sphere);
            });

            this.rebuildCameraDamageIndex();

            this.applyCameraDamageColors();

            // Render the scene to show loaded markers
            this.renderNextFrame();

            this.updateLoadingProgress(100, t('api.damagesLoaded'));
            this.observer.set('ui.spinner', false);

            // Clear loading progress message after 20 seconds
            setTimeout(() => {
                this.updateLoadingProgress(0, '');
            }, 20000);

        } catch (error) {
            console.error('Error loading damages from API:', error);

            let errorMessage = error.message;

            this.observer.set('ui.error', `${t('api.errorLoadingDamages')}: ${errorMessage}`);
            this.observer.set('ui.spinner', false);
            this.updateLoadingProgress(0, '');

            // Clear error message after 20 seconds
            setTimeout(() => {
                this.observer.set('ui.error', null);
            }, 20000);
        }
    }

    exportAllPointers() {
        const pointerData = this.buildDamageListForApi();

        if (pointerData.length === 0) {
            console.warn('No damages to export');
            alert('No damage markers to export');
            return;
        }

        // Create JSON string
        const jsonString = JSON.stringify(pointerData, null, 2);

        // Create blob and download
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        // Create temporary link element for download
        const link = document.createElement('a');
        link.href = url;
        link.download = `damage-markers-${this.vehiclePlate || 'export'}-${new Date().getTime()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        URL.revokeObjectURL(url);
        //alert('Damage markers exported successfully');
    }

    importAllPointers() {

        // Import the sphere-select-input value  
        const input = document.createElement('input');

        // Create standard material - this will be used for instanced, but also non-instanced rendering
        const materialSphere = new pc.StandardMaterial();
        materialSphere.gloss = 0.5;
        materialSphere.metalness = 1;
        materialSphere.diffuse = new pc.Color(1, 0, 0);
        materialSphere.useMetalness = false;

        // Check if spheres should be visible or hidden
        // Check existing spheres to determine current visibility state
        const existingSpheres = this.getDamageSphereRenderComponents();
        const shouldBeVisible = existingSpheres.length > 0 ? existingSpheres[0].entity.enabled : false;

        input.type = 'file';
        input.accept = 'application/json';
        input.onchange = (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const json = e.target?.result;
                    if (json) {
                        const pointerData = JSON.parse(json as string);
                        // Instantiate pointers in the scene
                        pointerData.forEach((pointer: any) => {
                            const sphere = new Entity(pointer.name);
                            const arrayDesc = pointer.description || '';
                            const selectValue = pointer.selectValue || '';
                            const photoIds = pointer.photoIds || [];
                            this.descriptionsPointer.push(arrayDesc);
                            this.selectPointer.push(selectValue);
                            this.photosPointer.push(photoIds);
                            sphere.addComponent('render', {
                                type: 'sphere',
                                material: materialSphere,
                                collider: {
                                    type: 'sphere',
                                    radius: 0.125
                                },
                                rigidbody: {
                                    type: 'dynamic'
                                }
                            });
                            sphere.setLocalScale(0.125, 0.125, 0.125);
                            
                            // Parse position values (handle both string and number formats)
                            const x = typeof pointer.position.x === 'string' ? parseFloat(pointer.position.x) : pointer.position.x;
                            const y = typeof pointer.position.y === 'string' ? parseFloat(pointer.position.y) : pointer.position.y;
                            const z = typeof pointer.position.z === 'string' ? parseFloat(pointer.position.z) : pointer.position.z;
                            
                            sphere.setPosition(x, y, z);
                            sphere.tags.add('sphere');
                            // Apply visibility state based on existing spheres
                            sphere.enabled = shouldBeVisible;
                            this.app.root.addChild(sphere);
                        });
                    }
                };
                reader.readAsText(file);
                this.renderNextFrame();
            }
        };
        input.click();
    }


    listPointerSpawner() {
        // List all pointer spawners in the scene
        const allSpawners = this.app.root.findComponents('sphere');
        //console.log('All spheres spawners in the scene:', allSpawners.length);
        allSpawners.forEach(spawner => {
            const position = spawner.entity.getPosition();
            //console.log('Pointer spawner position:', position.toString());
        });
    }

    hideNonEssentialButtons(createBut: boolean, listBut: boolean, editBut: boolean, exportBut: boolean, importBut: boolean, confirmBut: boolean, cancelBut: boolean, acceptBut: boolean = false, deleteBut: boolean = false) {
        const createButton = document.getElementById('position-sphere-create');
        const listButton = document.getElementById('position-sphere-list');
        const editButton = document.getElementById('position-sphere-edit');
        const exportButton = document.getElementById('position-sphere-export');
        const importButton = document.getElementById('position-sphere-import');
        const confirmButton = document.getElementById('position-sphere-confirm');
        const cancelButton = document.getElementById('position-sphere-cancel');
        const acceptButton = document.getElementById('position-sphere-accept-edit');
        const deleteButton = document.getElementById('position-sphere-delete');

        if (createButton) createButton.style.display = 'none';
        if (listButton) listButton.style.display = listBut ? 'block' : 'none';
        if (editButton) editButton.style.display = editBut ? 'block' : 'none';
        if (exportButton) exportButton.style.display = exportBut ? 'block' : 'none';
        if (importButton) importButton.style.display = importBut ? 'block' : 'none';
        if (confirmButton) confirmButton.style.display = 'none';
        if (cancelButton) cancelButton.style.display = 'none';
        if (acceptButton) acceptButton.style.display = 'none';
        if (deleteButton) deleteButton.style.display = 'none';

    }


    nameDescrPointerSpawner() {
        // Method to change name and store a description for the newest sphere by getting the text from two input fields
        const nameInput = document.getElementById('sphere-name-input') as HTMLInputElement;
        const descInput = document.getElementById('sphere-description-input') as HTMLTextAreaElement;
        const selectInput = document.getElementById('sphere-select-input') as HTMLInputElement;
        const sphereName = nameInput.value;
        const sphereDesc = descInput.value;
        const sphereSelect = selectInput.value;

        const allSpheres = this.getDamageSphereRenderComponents();

        if (allSpheres.length > 0) {
            // Use editingSphereIndex when editing, otherwise use lastIndex for new spheres
            const targetIndex = this.isEditing ? this.editingSphereIndex : allSpheres.length - 1;
            const targetSphere = allSpheres[targetIndex].entity;

            // Update sphere name if provided
            if (sphereName && sphereName.trim() !== '') {
                targetSphere.name = sphereName.trim();
                //console.log('Sphere renamed to:', sphereName);
                //console.log('All spheres:', allSpheres);
            }

            // Store description in the array
            if (sphereDesc && sphereDesc.trim() !== '') {
                this.descriptionsPointer[targetIndex] = sphereDesc.trim();
                //console.log('Description stored for sphere', targetIndex, ':', sphereDesc);
            }

            // Store select value in the array
            if (sphereSelect && sphereSelect.trim() !== '') {
                this.selectPointer[targetIndex] = sphereSelect.trim();
                //console.log('Select value stored for sphere', targetIndex, ':', sphereSelect);
            }

            // Store photo IDs in the array (from temporary storage)
            this.photosPointer[targetIndex] = [...this.tempPhotoIds];
            
            // Clear temporary photo IDs
            this.tempPhotoIds = [];

            // Clear input fields after storing
            nameInput.value = '';
            descInput.value = '';
        }
    }

    showPointerInput() {
        const nameInput = document.getElementById('sphere-name-input') as HTMLInputElement;
        const descInput = document.getElementById('sphere-description-input') as HTMLInputElement;
        const selectInput = document.getElementById('sphere-select-input') as HTMLInputElement;
        const photoButton = document.getElementById('sphere-select-photos-button') as HTMLButtonElement;

        const namePointer = this.getDamageSphereRenderComponents()
            .map(entity => entity.entity.name);

        const valueNameInput: string = namePointer[this.editingSphereIndex] || '';
        const valueDescInput: string = this.descriptionsPointer[this.editingSphereIndex] || '';
        const valueSelectInput: string = this.selectPointer[this.editingSphereIndex] || '';

        if (nameInput && descInput && selectInput) {
            nameInput.style.display = 'block';
            descInput.style.display = 'block';
            selectInput.style.display = 'block';
            if (photoButton) photoButton.style.display = 'block';

            if (this.isCreating) {
                nameInput.value = '';
                descInput.value = '';
                selectInput.value = '';
                // Clear temporary photo IDs for new damage
                this.tempPhotoIds = [];
            } else {
                nameInput.value = valueNameInput;
                descInput.value = valueDescInput;
                selectInput.value = valueSelectInput;
                // Load existing photo IDs when editing
                this.tempPhotoIds = [...(this.photosPointer[this.editingSphereIndex] || [])];
            }
        }


    }

    hidePointerInput() {
        const nameInput = document.getElementById('sphere-name-input') as HTMLInputElement;
        const descInput = document.getElementById('sphere-description-input') as HTMLInputElement;
        const selectInput = document.getElementById('sphere-select-input') as HTMLInputElement;
        const photoButton = document.getElementById('sphere-select-photos-button') as HTMLButtonElement;
        
        if (nameInput && descInput && selectInput) {
            nameInput.style.display = 'none';
            descInput.style.display = 'none';
            selectInput.style.display = 'none';
            if (photoButton) photoButton.style.display = 'none';
        }
    }

    // Photo management methods for damage markers
    setTempPhotoIds(photoIds: string[]) {
        this.tempPhotoIds = [...photoIds];
    }

    getTempPhotoIds(): string[] {
        return [...this.tempPhotoIds];
    }

    getPhotosForDamage(damageIndex: number): string[] {
        const photos = this.photosPointer[damageIndex] || [];
        return photos;
    }

    pointerSpawner() {

        //this.listAllEntities(this.app.root);
        if (!this.isCreating) {

            // Create standard material
            const materialSphere = new pc.StandardMaterial();
            materialSphere.gloss = 0.5;
            materialSphere.metalness = 1;
            materialSphere.diffuse = new pc.Color(1, 0, 0);
            materialSphere.useMetalness = false;


            // Create the sphere entity for the pointer
            const sphere = new pc.Entity('sphere');
            sphere.addComponent('render', {
                type: 'sphere',
                material: materialSphere,
                collider: {
                    type: 'sphere',
                    radius: 0.05
                },
                rigidbody: {
                    type: 'dynamic'
                }

            });

            // Set the scale of the sphere to adjust its size
            sphere.setLocalScale(0.125, 0.125, 0.125);

            // Set the position of the sphere
            // Use editingSpherePos if it was set by a click, otherwise use default position
            if (this.editingSpherePos) {
                sphere.setPosition(this.editingSpherePos);
                this.editingSpherePos = null; // Reset after using
            } else {
                sphere.setPosition(1, 0, 1);
            }

            this.app.root.addChild(sphere);


            /*            // Check for all the spheres in the scene by reading the array length and the position of each one 
                       const allSpheres = this.getDamageSphereRenderComponents();
                       console.log('All spheres in the scene:', allSpheres.length);
                       allSpheres.forEach(s => {
                           const position = s.entity.getPosition();
                           console.log('Sphere position:', position.toString());
                       }); */

            // Create gizmo layer using PlayCanvas Gizmo API
            const gizmoLayer = (pc as any).Gizmo.createLayer(this.app);

            // Create a TranslateGizmo and attach it to the sphere
            const gizmo = new (pc as any).TranslateGizmo(this.camera.camera, gizmoLayer);

            // Configure gizmo properties
            gizmo.size = 1;
            gizmo.snap = false;
            gizmo.coordSpace = 'world';
            gizmo.name = 'gizmo';

            // Attach gizmo to the sphere
            gizmo.attach(sphere);

            // Add event listeners to render when gizmo is being used
            gizmo.on('transform:start', () => {
                this.renderNextFrame();
            });

            gizmo.on('transform:move', () => {
                this.renderNextFrame();
            });

            gizmo.on('transform:end', () => {
                this.renderNextFrame();
            });

            this.gizmosArray.push(gizmo);

            // Continuously render while gizmo exists (alternative approach)
            let gizmoActive = true;
            const renderLoop = () => {
                if (gizmoActive) {
                    this.renderNextFrame();
                    requestAnimationFrame(renderLoop);
                }
            };
            renderLoop();

            // Log sphere creation
            //console.log('Sphere created at position:', spherePos.toString());

            this.spheresArray.push(sphere);

            this.isCreating = true;
            this.hideNonEssentialButtons(false, false, false, false, false, true, true, false, false);
            
            // Notify React component that creation mode is active
            if (this.positionSphereButtonsComponent && typeof this.positionSphereButtonsComponent.setCreatingState === 'function') {
                this.positionSphereButtonsComponent.setCreatingState(true);
            } else {
                console.warn('positionSphereButtonsComponent not available or setCreatingState not a function');
            }
        }


    }

    private initXrMode() {
        const xr = this.app.xr;

        this.xrMode = new XRObjectPlacementController({
            xr: xr,
            camera: this.camera,
            content: this.sceneRoot,
            showUI: true,
            startArImgSrc: arModeImage.src,
            stopArImgSrc: arCloseImage.src
        });

        const events = this.xrMode.events;

        events.on('xr:started', () => {
            // prepare scene settings for AR mode
            this.setShadowCatcherEnabled(true);
            this.setShadowCatcherIntensity(0.4);
            this.setDebugGrid(false);
            this.setDebugBounds(false);
            this.setLightEnabled(true);
            this.setLightShadow(true);
            this.setLightFollow(false);
            this.setCenterScene(true);

            this.setSkyboxBackground('None');
            this.setSkyboxExposure(0);
            this.setBackgroundColor(Color.BLACK);
            this.app.scene.layers.getLayerById(LAYERID_SKYBOX).enabled = false;

            this.multiframe.blend = 0.5;

            // detach multi camera
            this.multiCamera.detach();
        });

        events.on('xr:initial-place', () => {
            this.multiframe.blend = 1.0;
        });

        events.on('xr:ended', () => {
            // reload all user options
            this.reloadSettings();

            // background color isn't correctly restored
            this.setBackgroundColor(this.observer.get('skybox.backgroundColor'));

            // attach multicamera
            this.multiCamera.attach(this.camera);

            this.multiframe.blend = 1.0;
        });
    }

    private getSelectedMeshInstances() {
        return this.selectedNode ? this.collectMeshInstances(this.selectedNode as Entity) : this.meshInstances;
    }

    // collects all mesh instances from entity hierarchy for raycast (without modifying animations)
    private collectMeshInstancesForRaycast(entity: Entity, meshInstancesArray: MeshInstance[]) {
        if (entity.render) {
            meshInstancesArray.push(...entity.render.meshInstances);
        }

        const children = entity.children;
        for (let i = 0; i < children.length; ++i) {
            const child = children[i];
            if (child instanceof Entity) {
                this.collectMeshInstancesForRaycast(child, meshInstancesArray);
            }
        }
    }

    // collects all mesh instances from entity hierarchy
    private collectMeshInstances(entity: Entity) {
        const meshInstances: Array<MeshInstance> = [];
        if (entity) {
            const components = entity.findComponents('render');
            for (let i = 0; i < components.length; i++) {
                const render = components[i] as RenderComponent;
                if (render.meshInstances) {
                    for (let m = 0; m < render.meshInstances.length; m++) {
                        const meshInstance = render.meshInstances[m];
                        meshInstances.push(meshInstance);
                    }
                }
            }

            const gsplatComponents = entity.findComponents('gsplat');
            for (let i = 0; i < gsplatComponents.length; i++) {
                const gsplat = gsplatComponents[i] as GSplatComponent;
                if (gsplat.instance) {
                    meshInstances.push(gsplat.instance.meshInstance);
                }
            }
        }
        return meshInstances;
    }

    // calculate the bounding box of the given mesh
    private static calcMeshBoundingBox(result: BoundingBox, meshInstances: Array<MeshInstance>) {
        if (meshInstances.length > 0) {
            result.copy(meshInstances[0].aabb);
            for (let i = 1; i < meshInstances.length; ++i) {
                result.add(meshInstances[i].aabb);
            }
        }
    }

    // calculate the bounding box of the graph-node hierarchy
    private static calcHierBoundingBox(result: BoundingBox, rootNode: Entity) {
        const position = rootNode.getPosition();
        let min_x = position.x;
        let min_y = position.y;
        let min_z = position.z;
        let max_x = position.x;
        let max_y = position.y;
        let max_z = position.z;

        const recurse = (node: GraphNode) => {
            const p = node.getPosition();
            min_x = Math.min(min_x, p.x);
            min_y = Math.min(min_y, p.y);
            min_z = Math.min(min_z, p.z);

            max_x = Math.max(max_x, p.x);
            max_y = Math.max(max_y, p.y);
            max_z = Math.max(max_z, p.z);

            for (let i = 0; i < node.children.length; ++i) {
                recurse(node.children[i]);
            }
        };
        recurse(rootNode);

        result.setMinMax(new Vec3(min_x, min_y, min_z), new Vec3(max_x, max_y, max_z));
    }

    // construct the controls interface and initialize controls
    private bindControlEvents() {
        const controlEvents: Record<string, (...args: any[]) => void> = {
            // camera
            'camera.fov': this.setFov.bind(this),
            'camera.tonemapping': this.setTonemapping.bind(this),
            'camera.pixelScale': () => {
                this.canvasResize = true;
                this.renderNextFrame();
            },
            'camera.multisample': () => {
                this.destroyRenderTargets();
                this.renderNextFrame();
            },
            'camera.hq': (enabled: boolean) => {
                this.multiframe.enabled = !this.app.graphicsDevice.isWebGPU && enabled;
                this.renderNextFrame();
            },

            // skybox
            'skybox.value': (value: string) => {
                if (this.skyboxUrls.has(value)) {
                    const url = this.skyboxUrls.get(value);
                    this.loadFiles([{ url, filename: url }]);
                } else if (value === 'None') {
                    this.clearSkybox();
                } else {
                    this.loadFiles([{ url: value, filename: value }]);
                }
            },
            'skybox.blur': this.setSkyboxBlur.bind(this),
            'skybox.exposure': this.setSkyboxExposure.bind(this),
            'skybox.rotation': this.setSkyboxRotation.bind(this),
            'skybox.background': this.setSkyboxBackground.bind(this),
            'skybox.backgroundColor': this.setBackgroundColor.bind(this),
            'skybox.domeProjection.domeRadius': this.setSkyboxDomeRadius.bind(this),
            'skybox.domeProjection.tripodOffset': this.setSkyboxTripodOffset.bind(this),

            // light
            'light.enabled': this.setLightEnabled.bind(this),
            'light.intensity': this.setLightIntensity.bind(this),
            'light.color': this.setLightColor.bind(this),
            'light.follow': this.setLightFollow.bind(this),
            'light.shadow': this.setLightShadow.bind(this),

            // shadow catcher
            'shadowCatcher.enabled': this.setShadowCatcherEnabled.bind(this),
            'shadowCatcher.intensity': this.setShadowCatcherIntensity.bind(this),

            // debug
            'debug.stats': this.setDebugStats.bind(this),
            'debug.wireframe': this.setDebugWireframe.bind(this),
            'debug.wireframeColor': this.setWireframeColor.bind(this),
            'debug.bounds': this.setDebugBounds.bind(this),
            'debug.skeleton': this.setDebugSkeleton.bind(this),
            'debug.axes': this.setDebugAxes.bind(this),
            'debug.grid': this.setDebugGrid.bind(this),
            'debug.normals': this.setNormalLength.bind(this),
            'debug.renderMode': this.setRenderMode.bind(this),

            // animation
            'animation.playing': (playing: boolean) => {
                if (playing) {
                    this.play();
                } else {
                    this.stop();
                }
            },
            'animation.selectedTrack': this.setSelectedTrack.bind(this),
            'animation.speed': this.setSpeed.bind(this),
            'animation.transition': this.setTransition.bind(this),
            'animation.loops': this.setLoops.bind(this),
            'animation.progress': this.setAnimationProgress.bind(this),

            'scene.selectedNode.path': this.setSelectedNode.bind(this),
            'scene.variant.selected': this.setSelectedVariant.bind(this),

            centerScene: this.setCenterScene.bind(this)
        };

        // store control event keys
        this.controlEventKeys = Object.keys(controlEvents);

        // register control events
        this.controlEventKeys.forEach((e) => {
            this.observer.on(`${e}:set`, controlEvents[e]);
        });
    }

    private reloadSettings() {
        this.controlEventKeys.forEach((e) => {
            this.observer.set(e, this.observer.get(e), false, false, true);
        });
    }

    private clearSkybox() {
        this.app.scene.envAtlas = null;
        this.app.scene.setSkybox(null);
        this.renderNextFrame();
        this.skyboxLoaded = false;
    }

    // initialize the faces and prefiltered lighting data from the given
    // skybox texture, which is either a cubemap or equirect texture.
    private initSkybox(source: Texture) {
        const skybox = EnvLighting.generateSkyboxCubemap(source);
        const lighting = EnvLighting.generateLightingSource(source);
        // The second options parameter should not be necessary but the TS declarations require it for now
        const envAtlas = EnvLighting.generateAtlas(lighting, {});
        lighting.destroy();
        this.app.scene.envAtlas = envAtlas;
        this.app.scene.skybox = skybox;

        this.renderNextFrame();
    }

    // load the image files into the skybox. this function supports loading a single equirectangular
    // skybox image or 6 cubemap faces.
    private loadSkybox(files: Array<File>) {
        const app = this.app;

        if (files.length !== 6) {
            // load equirectangular skybox
            const textureAsset = new Asset('skybox_equi', 'texture', {
                url: files[0].url,
                filename: files[0].filename
            });
            textureAsset.ready(() => {
                const texture = textureAsset.resource;
                if (texture.type === TEXTURETYPE_DEFAULT && texture.format === PIXELFORMAT_RGBA8) {
                    // assume RGBA data (pngs) are RGBM
                    texture.type = TEXTURETYPE_RGBM;
                }
                this.initSkybox(texture);
            });
            app.assets.add(textureAsset);
            app.assets.load(textureAsset);
        } else {
            // sort files into the correct order based on filename
            const names = [
                ['posx', 'negx', 'posy', 'negy', 'posz', 'negz'],
                ['px', 'nx', 'py', 'ny', 'pz', 'nz'],
                ['right', 'left', 'up', 'down', 'front', 'back'],
                ['right', 'left', 'top', 'bottom', 'forward', 'backward'],
                ['0', '1', '2', '3', '4', '5']
            ];

            const getOrder = (filename: string) => {
                const fn = filename.toLowerCase();
                for (let i = 0; i < names.length; ++i) {
                    const nameList = names[i];
                    for (let j = 0; j < nameList.length; ++j) {
                        if (fn.indexOf(`${nameList[j]}.`) !== -1) {
                            return j;
                        }
                    }
                }
                return 0;
            };

            const sortPred = (first: File, second: File) => {
                const firstOrder = getOrder(first.filename);
                const secondOrder = getOrder(second.filename);
                return firstOrder < secondOrder ? -1 : secondOrder < firstOrder ? 1 : 0;
            };

            files.sort(sortPred);

            // construct an asset for each cubemap face
            const faceAssets = files.map((file, index) => {
                const faceAsset = new Asset(`skybox_face${index}`, 'texture', file);
                app.assets.add(faceAsset);
                app.assets.load(faceAsset);
                return faceAsset;
            });

            // construct the cubemap asset
            const cubemapAsset = new Asset('skybox_cubemap', 'cubemap', null, {
                textures: faceAssets.map(faceAsset => faceAsset.id)
            });
            cubemapAsset.loadFaces = true;
            cubemapAsset.on('load', () => {
                this.initSkybox(cubemapAsset.resource);
            });
            app.assets.add(cubemapAsset);
            app.assets.load(cubemapAsset);
        }
        this.skyboxLoaded = true;
    }

    private getCanvasSize() {
        const s = this.canvas.getBoundingClientRect();
        return {
            width: s.width,
            height: s.height
        };
    }

    private getFocusPosition(bbox: BoundingBox) {
        const focus = new Vec3();
        if (this.initialCameraFocus) {
            focus.copy(this.initialCameraFocus);
            this.initialCameraFocus = null;
        } else {
            const entityAsset = this.entityAssets[0];
            const splatData = entityAsset?.asset?.resource?.splatData;
            if (splatData) {
                splatData.calcFocalPoint(focus);
                entityAsset.entity.getWorldTransform().transformPoint(focus, focus);
            } else {
                focus.copy(bbox.center);
            }
        }
        return focus;
    }

    public getZoomDist() {
        const camera = this.camera.camera;
        const d1 = Math.tan(0.5 * FOCUS_FOV * math.DEG_TO_RAD);
        const d2 = Math.tan(0.5 * camera.fov * math.DEG_TO_RAD);

        const scale = (d1 / d2) * (1 / camera.aspectRatio);
        const normalDistance = scale * this.multiCamera.sceneSize + this.multiCamera.sceneSize;

        // Position camera inside the model by using negative distance or very small distance

        const insideModelFactor = -0.02; // Negative value places camera inside
        return normalDistance * insideModelFactor;


    }

    private focusSelection(calcStart = true) {
        // calculate scene bounding box
        this.calcSceneBounds(bbox, this.selectedNode as Entity);
        this.multiCamera.sceneSize = bbox.halfExtents.length();

        // calculate the camera focus point
        const focus = this.getFocusPosition(bbox);

        const upperStartY: number = 2; // Raise the camera's Y position by this value for better viewing angle

        let start: Vec3 | null = null;
        if (calcStart) {
            start = new Vec3();
            if (this.initialCameraPosition) {
                start.copy(this.initialCameraPosition);
                this.initialCameraPosition = null;
            } else {
                start.copy(focus);
                start.z += this.getZoomDist();

                // Flip camera horizontally (180 degrees rotation)
                // Invert Z position to face the opposite direction
                start.z = focus.z - (start.z - focus.z);
                start.y = focus.y + upperStartY;

            }
        }

        // focus orbit camera on object and set focus and sceneSize
        this.multiCamera.sceneSize = bbox.halfExtents.length();
        this.multiCamera.focus(focus, start);
    }




    destroyRenderTargets() {
        const rt = this.camera.camera.renderTarget;
        if (rt) {
            rt.colorBuffer?.destroy();
            rt.depthBuffer?.destroy();
            rt.destroy();
            this.camera.camera.renderTarget = null;
        }
    }

    rebuildRenderTargets() {
        const device = this.app.graphicsDevice;

        // get the canvas UI size
        const widthPixels = device.width;
        const heightPixels = device.height;

        const old = this.camera.camera.renderTarget;
        if (old && old.width === widthPixels && old.height === heightPixels) {
            return;
        }

        // out with the old
        this.destroyRenderTargets();

        const createTexture = (width: number, height: number, format: number) => {
            return new Texture(device, {
                name: 'viewer-rt-texture',
                width: width,
                height: height,
                format: format,
                mipmaps: false,
                minFilter: FILTER_NEAREST,
                magFilter: FILTER_NEAREST,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE
            });
        };

        // @ts-ignore
        const maxSamples = device.maxSamples;

        // in with the new
        const colorBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_RGBA8);
        const depthBuffer = createTexture(widthPixels, heightPixels, PIXELFORMAT_DEPTH);
        const renderTarget = new RenderTarget({
            name: 'viewer-rt',
            colorBuffer: colorBuffer,
            depthBuffer: depthBuffer,
            flipY: false,
            samples: this.observer.get('camera.multisample') ? maxSamples : 1,
            autoResolve: false
        });
        this.camera.camera.renderTarget = renderTarget;
    }

    // reset the viewer, unloading resources
    resetScene() {
        const app = this.app;

        // Cancel any pending auto-front timer and its listeners
        this._cancelAutoFront();

        this.entities.forEach((entity) => {
            this.sceneRoot.removeChild(entity);
            this.shadowCatcher.onEntityRemoved(entity);
            entity.destroy();
        });
        this.entities = [];
        this.entityAssets = [];

        this.assets.forEach((asset) => {
            app.assets.remove(asset);
            asset.unload();
        });
        this.assets = [];

        this.meshInstances = [];
        this.resetWireframeMeshes();

        // reset animation state
        this.animTracks = [];
        this.animationMap = {};
    }

    updateSceneStats() {
        let meshCount = 0;
        let meshVRAM = 0;
        let vertexCount = 0;
        let primitiveCount = 0;
        let materialCount = 0;
        let textureCount = 0;
        let textureVRAM = 0;
        let variants: string[] = [];

        // update mesh stats
        this.assets.forEach((asset) => {
            const resource = asset.resource;

            if (resource.splatData) {
                meshCount++;
                materialCount++;
                primitiveCount += resource.splatData.numSplats;
                vertexCount += resource.splatData.numSplats * 4;
                meshVRAM += resource.splatData.numSplats * 64; // 16 * float32
            } else {
                variants = variants.concat(resource.getMaterialVariants() ?? []);

                resource.renders.forEach((renderAsset: Asset) => {
                    meshCount += renderAsset.resource.meshes.length;
                    renderAsset.resource.meshes.forEach((mesh: Mesh) => {
                        vertexCount += mesh.vertexBuffer.getNumVertices();

                        const prim = mesh.primitive[0];
                        switch (prim.type) {
                            case PRIMITIVE_POINTS:
                                primitiveCount += prim.count;
                                break;
                            case PRIMITIVE_LINES:
                                primitiveCount += prim.count / 2;
                                break;
                            case PRIMITIVE_LINELOOP:
                                primitiveCount += prim.count;
                                break;
                            case PRIMITIVE_LINESTRIP:
                                primitiveCount += prim.count - 1;
                                break;
                            case PRIMITIVE_TRIANGLES:
                                primitiveCount += prim.count / 3;
                                break;
                            case PRIMITIVE_TRISTRIP:
                                primitiveCount += prim.count - 2;
                                break;
                            case PRIMITIVE_TRIFAN:
                                primitiveCount += prim.count - 2;
                                break;
                        }
                        meshVRAM += mesh.vertexBuffer.numBytes + (mesh.indexBuffer?.[0]?.numBytes ?? 0);
                    });
                });

                materialCount += resource.materials.length ?? 0;
                textureCount += resource.textures.length ?? 0;
                (resource.textures ?? []).forEach((texture: Asset) => {
                    textureVRAM += texture.resource.gpuSize;
                });
            }
        });

        const mapChildren = function (node: GraphNode): Array<HierarchyNode> {
            return node.children.map((child: GraphNode) => ({
                name: child.name,
                path: child.path,
                children: mapChildren(child)
            }));
        };

        const graph: Array<HierarchyNode> = this.entities.map((entity) => {
            return {
                name: entity.name,
                path: entity.path,
                children: mapChildren(entity)
            };
        });

        // hierarchy
        this.observer.set('scene.nodes', JSON.stringify(graph));

        // mesh stats
        this.observer.set('scene.meshCount', meshCount);
        this.observer.set('scene.materialCount', materialCount);
        this.observer.set('scene.textureCount', textureCount);
        this.observer.set('scene.vertexCount', vertexCount);
        this.observer.set('scene.primitiveCount', primitiveCount);
        this.observer.set('scene.textureVRAM', textureVRAM);
        this.observer.set('scene.meshVRAM', meshVRAM);

        // variant stats
        this.observer.set('scene.variants.list', JSON.stringify(variants));
        this.observer.set('scene.variant.selected', variants[0]);
    }

    downloadPngScreenshot() {
        const actualObserver = this.observer.get('camera.pixelScale');

        // Get all sphere entities
        const allSpheres = this.getDamageSphereRenderComponents();

        // Store original enabled state of each sphere
        const sphereStates = allSpheres.map(sphere => ({
            entity: sphere.entity,
            enabled: sphere.entity.enabled
        }));

        // Step 1: Hide spheres, set high quality and rebuild render targets
        allSpheres.forEach(sphere => {
            sphere.entity.enabled = false;
        });

        this.observer.set('camera.pixelScale', 0.5);
        this.rebuildRenderTargets();
        this.renderNextFrame();

        // Step 2: Wait for the frame to render, then capture
        requestAnimationFrame(() => {
            const texture = this.camera.camera.renderTarget.colorBuffer;
            const newDate = new Date();

            texture.read(0, 0, texture.width, texture.height).then((typedArray: Uint32Array) => {
                // Step 3: Composite the Vysia logo onto the screenshot
                const width = texture.width;
                const height = texture.height;

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                // Convert the raw pixel data (RGBA, bottom-up) to ImageData (top-down)
                const imageData = ctx.createImageData(width, height);
                const src = new Uint8Array(typedArray.buffer);
                for (let y = 0; y < height; y++) {
                    const srcRow = (height - 1 - y) * width * 4;
                    const dstRow = y * width * 4;
                    for (let x = 0; x < width; x++) {
                        const si = srcRow + x * 4;
                        const di = dstRow + x * 4;
                        imageData.data[di] = src[si];       // R
                        imageData.data[di + 1] = src[si + 1]; // G
                        imageData.data[di + 2] = src[si + 2]; // B
                        imageData.data[di + 3] = src[si + 3]; // A
                    }
                }
                ctx.putImageData(imageData, 0, 0);

                // Draw the Vysia logo in the top-left corner
                const logo = new Image();
                logo.crossOrigin = 'anonymous';
                logo.onload = () => {
                    // Scale logo to a reasonable size (height ~5% of image height, min 30px)
                    const logoHeight = Math.max(30, Math.round(height * 0.05));
                    const logoWidth = Math.round(logo.naturalWidth * (logoHeight / logo.naturalHeight));
                    const padding = Math.round(logoHeight * 0.4);

                    ctx.drawImage(logo, padding, padding, logoWidth, logoHeight);

                    // Export the composited canvas as PNG
                    canvas.toBlob((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const el = document.createElement('a');
                        el.download = `model-viewer-${newDate.getTime()}.png`;
                        el.href = url;
                        el.click();
                        window.URL.revokeObjectURL(url);
                    }, 'image/png');

                    // Step 4: Restore spheres visibility, original quality after capture
                    sphereStates.forEach(state => {
                        state.entity.enabled = state.enabled;
                    });

                    this.observer.set('camera.pixelScale', actualObserver);
                    this.rebuildRenderTargets();
                    this.renderNextFrame();
                };
                logo.onerror = () => {
                    // Fallback: export without logo if it fails to load
                    canvas.toBlob((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const el = document.createElement('a');
                        el.download = `model-viewer-${newDate.getTime()}.png`;
                        el.href = url;
                        el.click();
                        window.URL.revokeObjectURL(url);
                    }, 'image/png');

                    sphereStates.forEach(state => {
                        state.entity.enabled = state.enabled;
                    });

                    this.observer.set('camera.pixelScale', actualObserver);
                    this.rebuildRenderTargets();
                    this.renderNextFrame();
                };
                logo.src = `${window.location.href.split('?')[0]}static/Logo_VYSIA_White_Fluor.png`;
            });
        });
    }

    // adjust camera clipping planes to fit the scene
    fitCameraClipPlanes() {
        if (this.xrMode?.active) {
            return;
        }

        const mat = this.camera.getWorldTransform();

        const cameraPosition = mat.getTranslation();
        const cameraForward = mat.getZ();

        const bound = this.dynamicSceneBounds;
        const boundCenter = bound.center;
        const boundRadius = bound.halfExtents.length() * 2;

        vec.sub2(boundCenter, cameraPosition);
        const dist = -vec.dot(cameraForward);

        const far = dist + boundRadius;
        const near = Math.max(0.001, dist < boundRadius ? far / 1024 : dist - boundRadius);

        this.camera.camera.nearClip = near;
        this.camera.camera.farClip = far;
        this.light.light.shadowDistance = far;
        this.light.light.normalOffsetBias = far / 1024;
    }

    // load gltf model given its url and list of external urls
    private loadGltf(gltfUrl: File, externalUrls: Array<File>) {
        return new Promise((resolve, reject) => {
            // provide buffer view callback so we can handle models compressed with MeshOptimizer
            // https://github.com/zeux/meshoptimizer
            const processBufferView = function (
                gltfBuffer: any,
                buffers: Array<any>,
                continuation: (err: string, result: any) => void
            ) {
                if (gltfBuffer.extensions && gltfBuffer.extensions.EXT_meshopt_compression) {
                    const extensionDef = gltfBuffer.extensions.EXT_meshopt_compression;

                    Promise.all([MeshoptDecoder.ready, buffers[extensionDef.buffer]]).then((promiseResult) => {
                        const buffer = promiseResult[1];

                        const byteOffset = extensionDef.byteOffset || 0;
                        const byteLength = extensionDef.byteLength || 0;

                        const count = extensionDef.count;
                        const stride = extensionDef.byteStride;

                        const result = new Uint8Array(count * stride);
                        const source = new Uint8Array(buffer.buffer, buffer.byteOffset + byteOffset, byteLength);

                        MeshoptDecoder.decodeGltfBuffer(
                            result,
                            count,
                            stride,
                            source,
                            extensionDef.mode,
                            extensionDef.filter
                        );

                        continuation(null, result);
                    });
                } else {
                    continuation(null, null);
                }
            };

            const processImage = function (gltfImage: any, continuation: (err: string, result: any) => void) {
                const u: File = externalUrls.find((url) => {
                    return url.filename === decodeURIComponent(path.normalize(gltfImage.uri || ''));
                });
                if (u) {
                    const textureAsset = new Asset(u.filename, 'texture', {
                        url: u.url,
                        filename: u.filename
                    });
                    textureAsset.on('load', () => {
                        continuation(null, textureAsset);
                    });
                    this.app.assets.add(textureAsset);
                    this.app.assets.load(textureAsset);
                } else {
                    continuation(null, null);
                }
            };

            const postProcessImage = (gltfImage: any, textureAsset: Asset) => {
                // max anisotropy on all textures
                textureAsset.resource.anisotropy = this.app.graphicsDevice.maxAnisotropy;
            };

            const processBuffer = function (gltfBuffer: any, continuation: (err: string, result: any) => void) {
                const u = externalUrls.find((url) => {
                    return url.filename === decodeURIComponent(path.normalize(gltfBuffer.uri || ''));
                });
                if (u) {
                    const bufferAsset = new Asset(u.filename, 'binary', {
                        url: u.url,
                        filename: u.filename
                    });
                    bufferAsset.on('load', () => {
                        continuation(null, new Uint8Array(bufferAsset.resource));
                    });
                    this.app.assets.add(bufferAsset);
                    this.app.assets.load(bufferAsset);
                } else {
                    continuation(null, null);
                }
            };

            const containerAsset = new Asset(gltfUrl.filename, 'container', gltfUrl, null, {
                // @ts-ignore TODO no definition in pc
                bufferView: {
                    processAsync: processBufferView.bind(this)
                },
                image: {
                    processAsync: processImage.bind(this),
                    postprocess: postProcessImage
                },
                buffer: {
                    processAsync: processBuffer.bind(this)
                }
            }
            );
            containerAsset.on('load', () => resolve(containerAsset));
            containerAsset.on('error', (err: string) => reject(err));
            this.app.assets.add(containerAsset);
            this.app.assets.load(containerAsset);
        });
    }

    private loadPly(url: File) {
        return new Promise((resolve, reject) => {
            const asset = new Asset(url.filename, 'gsplat', url);
            asset.on('load', () => resolve(asset));
            asset.on('error', (err: string) => reject(err));
            this.app.assets.add(asset);
            this.app.assets.load(asset);

            // Extract vehicle plate from .ply filename
            this.extractVehiclePlateFromFilename(url.filename);
        });
    }

    /**
     * Extract vehicle plate from .ply filename
     * Expected format: [PLATE]_Model.ply or [PLATE]_Model Complete.ply
     * Examples: [F306rs]_Model.ply → F306rs, [GE960HR]_Model.ply → GE960HR
     */
    private extractVehiclePlateFromFilename(filename: string) {
        try {
            // Match pattern: anything between square brackets at the start of filename
            const plateMatch = filename.match(/^\[([^\]]+)\]/);

            if (plateMatch && plateMatch[1]) {
                const extractedPlate = plateMatch[1].trim().toUpperCase();
                this.vehiclePlate = extractedPlate;
            } else {
                console.warn(` Could not extract vehicle plate from filename: ${filename}`);
                console.warn(`   Expected format: [PLATE]_Model.ply`);
                console.warn(`   Using default plate: ${this.vehiclePlate}`);
            }
        } catch (error) {
            console.error('Error extracting vehicle plate from filename:', error);
            console.warn(`   Using default plate: ${this.vehiclePlate}`);
        }
    }

    // returns true if the filename has one of the recognized model extensions
    isModelFilename(filename: string) {
        const parts = filename.split('?')[0].split('/').pop().split('.');
        const result = parts.length === 1 || modelExtensions.includes(parts.pop().toLowerCase());
        return result;
    }

    isGSplatFilename(filename: string) {
        const parts = filename.split('?')[0].split('/').pop().split('.');
        const result = parts.length > 0 && parts.pop().toLowerCase() === 'ply';
        return result;
    }

    // Update loading progress and message
    private updateLoadingProgress(progress: number, message: string = '', isRealProgress: boolean = false) {
        // Si estamos en modo de carga falseada (0-80%)
        if (!isRealProgress && this.fakeLoadingActive) {
            // Solo actualizar si el progreso falseado no ha llegado a 80%
            if (progress <= 80) {
                this.loadingProgress = Math.min(80, Math.max(0, progress));
                this.loadingMessage = message;
                this.observer.set('ui.loadingProgress', this.loadingProgress);
                this.observer.set('ui.loadingMessage', this.loadingMessage);
            }
        } 
        // Si es progreso real (80-100%)
        else if (isRealProgress) {
            // Mapear el progreso real de 0-100 al rango 80-100
            const mappedProgress = 80 + (progress * 0.20);
            this.loadingProgress = Math.min(100, Math.max(80, mappedProgress));
            this.loadingMessage = message;
            this.observer.set('ui.loadingProgress', this.loadingProgress);
            this.observer.set('ui.loadingMessage', this.loadingMessage);
        }
        // Caso por defecto (para llamadas directas sin sistema de carga)
        else {
            this.loadingProgress = Math.min(100, Math.max(0, progress));
            this.loadingMessage = message;
            this.observer.set('ui.loadingProgress', this.loadingProgress);
            this.observer.set('ui.loadingMessage', this.loadingMessage);
        }
    }

    // load the list of urls.
    // urls can reference glTF files, glb files and skybox textures.
    // returns true if a model was loaded.
    loadFiles(files: Array<File>, resetScene = false) {
        // convert single url to list
        if (!Array.isArray(files)) {
            files = [files];
        }

        // check if any file is a model
        const hasModelFilename = files.reduce(
            (p, f) => p || this.isModelFilename(f.filename) || this.isGSplatFilename(f.filename),
            false
        );

        if (hasModelFilename) {
            if (resetScene) {
                this.resetScene();
            }

            const loadTimestamp = Date.now();

            // Initialize loading state
            this.observer.set('ui.spinner', true);
            this.observer.set('ui.error', null);
            this.fakeLoadingActive = true;
            this.realLoadingStarted = false;
            this.updateLoadingProgress(0, t('app.loading'));
            this.clearCta();

            // Carga falseada hasta 80% con incrementos irregulares
            // Duración total: 3 minutos (180,000ms) para alcanzar 80%
            // Incremento necesario: 80% / (180000ms / intervalo) = incremento por tick
            const FAKE_LOAD_DURATION = 180000; // 3 minutos en milisegundos
            const UPDATE_INTERVAL = 200; // Intervalo de actualización en ms
            const TOTAL_TICKS = FAKE_LOAD_DURATION / UPDATE_INTERVAL; // 900 ticks
            const BASE_INCREMENT = 80 / TOTAL_TICKS; // ~0.089% por tick
            
            let fakeProgress = 0;
            this.fakeLoadingInterval = setInterval(() => {
                // Incremento base con pequeña variación aleatoria (±20%)
                const variation = (Math.random() - 0.5) * 0.4; // -0.2 a +0.2
                const randomIncrement = BASE_INCREMENT * (1 + variation);
                fakeProgress += randomIncrement;
                
                // Limitar al 80%
                if (fakeProgress >= 80) {
                    fakeProgress = 80;
                    clearInterval(this.fakeLoadingInterval);
                    this.fakeLoadingInterval = null;
                }
                
                // Mensajes dinámicos según el progreso
                let message = t('app.loading');
                if (fakeProgress > 15 && fakeProgress <= 35) {
                    message = t('app.preparingFiles') || 'Preparando archivos...';
                } else if (fakeProgress > 35 && fakeProgress <= 55) {
                    message = t('app.processingModel') || 'Procesando modelo...';
                } else if (fakeProgress > 55 && fakeProgress <= 75) {
                    message = t('app.loadingTextures') || 'Cargando texturas...';
                } else if (fakeProgress > 75) {
                    message = t('app.optimizing') || 'Optimizando...';
                }
                
                this.updateLoadingProgress(Math.floor(fakeProgress), message, false);
            }, UPDATE_INTERVAL);

            // load asset files
            const promises = files.map((file, index) => {
                if (this.isModelFilename(file.filename)) {
                    return this.loadGltf(file, files);
                } else if (this.isGSplatFilename(file.filename)) {
                    return this.loadPly(file);
                }
                return null;
            });

            Promise.all(promises)
                .then((assets: Asset[]) => {
                    // Detener carga falseada si aún está activa
                    if (this.fakeLoadingInterval) {
                        clearInterval(this.fakeLoadingInterval);
                        this.fakeLoadingInterval = null;
                    }
                    
                    // Activar modo de progreso real (80-100%)
                    this.fakeLoadingActive = false;
                    this.realLoadingStarted = true;
                    this.loadTimestamp = loadTimestamp;
                    
                    // Progreso real: 0% del rango real = 80% total
                    this.updateLoadingProgress(10, t('app.loadingModels') || 'Cargando modelos...', true);

                    // add assets to the scene
                    assets.forEach((asset) => {
                        if (asset) {
                            this.addToScene(asset);
                        }
                    });

                    // Progreso real: 50% del rango real = 90% total
                    this.updateLoadingProgress(50, t('app.configuringScene') || 'Configurando escena...', true);

                    // prepare scene post load
                    this.postSceneLoad();

                    // Progreso real: 80% del rango real = 96% total
                    this.updateLoadingProgress(80, t('app.finishingLoad') || 'Finalizando carga...', true);

                    // update scene urls
                    const urls = files.map(f => f.url);
                    const filenames = files.map(f => f.filename.split('/').pop());
                    if (resetScene) {
                        this.observer.set('scene.urls', urls);
                        this.observer.set('scene.filenames', filenames);
                    } else {
                        this.observer.set('scene.urls', this.observer.get('scene.urls').concat(urls));
                        this.observer.set('scene.filenames', this.observer.get('scene.filenames').concat(filenames));
                    }

                    // Progreso real: 100% del rango real = 100% total
                    this.updateLoadingProgress(100, t('app.finishedLoad') || 'Carga completada', true);

                    // Hide loading bar after a brief delay
                    setTimeout(() => {
                        this.observer.set('ui.spinner', false);
                        this.fakeLoadingActive = false;
                        this.realLoadingStarted = false;
                    }, 800);
                })
                .catch((err) => {
                    // Limpiar intervalo en caso de error
                    if (this.fakeLoadingInterval) {
                        clearInterval(this.fakeLoadingInterval);
                        this.fakeLoadingInterval = null;
                    }
                    this.fakeLoadingActive = false;
                    this.realLoadingStarted = false;

                    this.observer.set('ui.error', err?.toString() || err);
                    this.observer.set('ui.spinner', false);
                })
        } else {
            // load skybox
            this.loadSkybox(files);
        }

        // return true if a model/scene was loaded and false otherwise
        return hasModelFilename;
    }

    // set the currently selected track
    setSelectedTrack(trackName: string) {
        if (trackName !== 'ALL_TRACKS') {
            const a = this.animationMap[trackName];
            this.entities.forEach((e) => {
                e.anim?.baseLayer?.transition(a);
            });
        }
    }

    // play an animation / play all the animations
    play() {
        this.entities.forEach((e) => {
            if (e.anim) {
                e.anim.playing = true;
                e.anim.baseLayer?.play();
            }
        });
    }

    // stop playing animations
    stop() {
        this.entities.forEach((e) => {
            if (e.anim) {
                e.anim.playing = false;
                e.anim.baseLayer?.pause();
            }
        });
    }

    // set the animation speed
    setSpeed(speed: number) {
        this.animSpeed = speed;
        this.entities.forEach((e) => {
            const anim = e.anim;
            if (anim) {
                anim.speed = speed;
            }
        });
    }

    setTransition(transition: number) {
        this.animTransition = transition;

        // it's not possible to change the transition time after creation,
        // so rebuilt the animation graph with the new transition
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }
    }

    setLoops(loops: number) {
        this.animLoops = loops;

        // it's not possible to change the transition time after creation,
        // so rebuilt the animation graph with the new transition
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }
    }

    setAnimationProgress(progress: number) {
        if (this.suppressAnimationProgressUpdate) return;
        this.entities.forEach((e) => {
            const anim = e.anim;
            const baseLayer = anim?.baseLayer;
            if (baseLayer) {
                this.play();
                baseLayer.activeStateCurrentTime = baseLayer.activeStateDuration * progress;
                anim.update(0);
                anim.playing = false;
            }
        });
        this.renderNextFrame();
    }

    setSelectedNode(path: string) {
        const graphNode = this.app.root.findByPath(path);
        if (graphNode) {
            this.observer.set('scene.selectedNode', {
                name: graphNode.name,
                path: path,
                position: graphNode.getLocalPosition().toString(),
                rotation: graphNode.getLocalEulerAngles().toString(),
                scale: graphNode.getLocalScale().toString()
            });
        }

        this.selectedNode = graphNode;
        this.dirtyWireframe = true;
        this.dirtyBounds = true;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    setSelectedVariant(variant: string) {
        if (variant) {
            this.entityAssets.forEach((entityAsset) => {
                if (entityAsset.asset.resource.getMaterialVariants().indexOf(variant) !== -1) {
                    entityAsset.asset.resource.applyMaterialVariant(entityAsset.entity, variant);
                }
            });
            this.renderNextFrame();
        }
    }

    setCenterScene(value: boolean) {
        this.sceneRoot.setLocalPosition(0, 0, 0);

        // calculate scene bounds after first render in order to get accurate morph target and skinned bounds
        this.calcSceneBounds(this.sceneBounds);

        // offset scene geometry to place it at the origin
        if (value) {
            this.sceneRoot.setLocalPosition(
                -this.sceneBounds.center.x,
                -this.sceneBounds.getMin().y,
                -this.sceneBounds.center.z
            );
        }

        // Reset lift base so the next setSceneLift call re-captures the new center Y
        this.sceneLiftBaseY = null;

        this.dirtyBounds = true;

        this.renderNextFrame();
    }

    /**
     * Moves the entire scene (sceneRoot) vertically by yOffset units above its
     * centered base position. Camera model entities live at app.root level and
     * are therefore unaffected by this transform.
     */
    public setSceneLift(yOffset: number): void {
        // Capture the base Y of sceneRoot the first time lift is applied
        if (this.sceneLiftBaseY === null) {
            this.sceneLiftBaseY = this.sceneRoot.getLocalPosition().y;
        }
        const pos = this.sceneRoot.getLocalPosition();
        this.sceneRoot.setLocalPosition(pos.x, this.sceneLiftBaseY + yOffset, pos.z);
        this.renderNextFrame();
    }

    public setOrbitalControlsEnabled(enabled: boolean): void {
        this.enableOrbitalControls = enabled;
        window.localStorage.setItem('vysia-orbital-controls', String(enabled));
        window.dispatchEvent(new CustomEvent('orbital-controls-changed', {
            detail: { enabled }
        }));
    }

    public setCameraOverlayOpen(open: boolean): void {
        if (open === this.isCameraOverlayOpen) {
            return;
        }

        if (open) {
            this.orbitalControlsBeforeCameraOverlay = this.enableOrbitalControls;
            if (this.enableOrbitalControls) {
                this.setOrbitalControlsEnabled(false);
            }

            if (this.multiCamera) {
                this.moveSpeedBeforeCameraOverlay = this.multiCamera.moveSpeed;
                this.sprintSpeedBeforeCameraOverlay = this.multiCamera.sprintSpeed;
                this.crouchSpeedBeforeCameraOverlay = this.multiCamera.crouchSpeed;
                this.wheelSpeedBeforeCameraOverlay = this.multiCamera.wheelSpeed;
                this.multiCamera.moveSpeed = 0;
                this.multiCamera.sprintSpeed = 0;
                this.multiCamera.crouchSpeed = 0;
                this.multiCamera.wheelSpeed = 0;
            }
        } else {
            const shouldRestoreOrbital = this.orbitalControlsBeforeCameraOverlay === true;
            this.orbitalControlsBeforeCameraOverlay = null;
            if (shouldRestoreOrbital && !this.enableOrbitalControls) {
                this.setOrbitalControlsEnabled(true);
            }

            if (this.multiCamera) {
                this.multiCamera.moveSpeed = this.moveSpeedBeforeCameraOverlay ?? this.multiCamera.moveSpeed;
                this.multiCamera.sprintSpeed = this.sprintSpeedBeforeCameraOverlay ?? this.multiCamera.sprintSpeed;
                this.multiCamera.crouchSpeed = this.crouchSpeedBeforeCameraOverlay ?? this.multiCamera.crouchSpeed;
                this.multiCamera.wheelSpeed = this.wheelSpeedBeforeCameraOverlay ?? this.multiCamera.wheelSpeed;
            }

            this.moveSpeedBeforeCameraOverlay = null;
            this.sprintSpeedBeforeCameraOverlay = null;
            this.crouchSpeedBeforeCameraOverlay = null;
            this.wheelSpeedBeforeCameraOverlay = null;
        }

        this.isCameraOverlayOpen = open;

        if (!this.multiCamera) {
            return;
        }

        if (open) {
            this.multiCamera.moveSpeed = 0;
            return;
        }

        if (!this.isInputFocused) {
            this.multiCamera.moveSpeed = 0.05;
        }
    }

    setDebugStats(show: boolean) {
        this.miniStats.enabled = show;
        this.renderNextFrame();
    }

    setDebugWireframe(show: boolean) {
        this.showWireframe = show;
        this.dirtyWireframe = true;
        this.renderNextFrame();
    }

    setWireframeColor(color: { r: number; g: number; b: number }) {
        this.wireframeMaterial.emissive = new Color(color.r, color.g, color.b);
        this.wireframeMaterial.update();
        this.renderNextFrame();
    }

    setDebugBounds(show: boolean) {
        this.showBounds = show;
        this.dirtyBounds = true;
        this.renderNextFrame();
    }

    setDebugSkeleton(show: boolean) {
        this.showSkeleton = show;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    setDebugAxes(show: boolean) {
        this.showAxes = show;
        this.dirtySkeleton = true;
        this.renderNextFrame();
    }

    setDebugGrid(show: boolean) {
        this.showGrid = show;
        this.dirtyGrid = true;
        this.renderNextFrame();
    }

    setNormalLength(length: number) {
        this.normalLength = length;
        this.dirtyNormals = true;
        this.renderNextFrame();
    }

    setFov(fov: number) {
        this.camera.camera.fov = fov;
        this.renderNextFrame();
    }

    setRenderMode(renderMode: string) {
        this.camera.camera.setShaderPass(renderMode !== 'default' ? `debug_${renderMode}` : 'forward');
        this.renderNextFrame();
    }

    setLightEnabled(value: boolean) {
        this.light.enabled = value;
        this.renderNextFrame();
    }

    setLightIntensity(factor: number) {
        this.light.light.intensity = factor;
        this.renderNextFrame();
    }

    setLightColor(color: { r: number; g: number; b: number }) {
        this.light.light.color = new Color(color.r, color.g, color.b);
        this.renderNextFrame();
    }

    setLightFollow(enable: boolean) {
        this.light.reparent(enable ? this.camera : this.app.root);
        if (enable) {
            this.light.setLocalEulerAngles(90, 0, 0);
        } else {
            this.light.setLocalEulerAngles(45, 30, 0);
        }
        this.renderNextFrame();
    }

    setLightShadow(enable: boolean) {
        this.light.light.castShadows = enable;
        this.renderNextFrame();
    }

    setShadowCatcherEnabled(value: boolean) {
        this.shadowCatcher.enabled = value;
        this.renderNextFrame();
    }

    setShadowCatcherIntensity(value: number) {
        this.shadowCatcher.intensity = value;
        this.renderNextFrame();
    }

    setSkyboxExposure(factor: number) {
        this.app.scene.skyboxIntensity = Math.pow(2, factor);
        this.renderNextFrame();
    }

    setSkyboxRotation(factor: number) {
        const rot = new Quat();
        rot.setFromEulerAngles(0, factor, 0);
        this.app.scene.skyboxRotation = rot;

        this.renderNextFrame();
    }

    setSkyboxBackground(background: string) {
        const { scene } = this.app;

        this.app.scene.layers.getLayerById(LAYERID_SKYBOX).enabled = background !== 'Solid Color';

        switch (background) {
            case 'Solid Color':
                break;
            case 'Infinite Sphere':
                scene.sky.type = SKYTYPE_INFINITE;
                break;
            case 'Projective Dome':
                scene.sky.type = SKYTYPE_DOME;
                break;
            case 'Projective Box':
                scene.sky.type = SKYTYPE_BOX;
                break;
        }

        this.app.scene.skyboxMip = background === 'Infinite Sphere' ? this.observer.get('skybox.blur') : 0;

        this.renderNextFrame();
    }

    setSkyboxBlur(blur: number) {
        this.app.scene.skyboxMip = this.observer.get('skybox.background') === 'Infinite Sphere' ? blur : 0;
        this.renderNextFrame();
    }

    setSkyboxDomeRadius(radius: number) {
        const scale = (this.sceneBounds?.halfExtents.length() ?? 1) * radius;
        this.app.scene.sky.node.setLocalScale(scale, scale, scale);
        this.renderNextFrame();
    }

    setSkyboxTripodOffset(offset: number) {
        this.app.scene.sky.center = new Vec3(0, offset, 0);
        this.renderNextFrame();
    }

    setTonemapping(tonemapping: string) {
        const mapping: Record<string, number> = {
            Linear: TONEMAP_LINEAR,
            Filmic: TONEMAP_FILMIC,
            Hejl: TONEMAP_HEJL,
            ACES: TONEMAP_ACES,
            ACES2: TONEMAP_ACES2
        };

        this.app.scene.rendering.toneMapping = mapping.hasOwnProperty(tonemapping) ? mapping[tonemapping] : TONEMAP_ACES;
        this.renderNextFrame();
    }

    setBackgroundColor(color: { r: number; g: number; b: number }) {
        const cnv = (value: number) => Math.max(0, Math.min(255, Math.floor(value * 255)));
        document.getElementById('canvas-wrapper').style.backgroundColor = `rgb(${cnv(color.r)}, ${cnv(color.g)}, ${cnv(
            color.b
        )})`;
    }




    update(deltaTime: number) {
        // update the orbit camera
        if (!this.xrMode?.active && !this.isInputFocused && !this.isCameraOverlayOpen) {
            this.multiCamera.update(deltaTime);
        }

        // * Here is managed the button panel



        const maxdiff = (a: Mat4, b: Mat4) => {
            let result = 0;
            for (let i = 0; i < 16; ++i) {
                result = Math.max(result, Math.abs(a.data[i] - b.data[i]));
            }
            return result;
        };

        // if the camera has moved since the last render
        const cameraWorldTransform = this.camera.getWorldTransform();
        if (maxdiff(cameraWorldTransform, this.prevCameraMat) > 1e-4) {
            this.prevCameraMat.copy(cameraWorldTransform);
            this.renderNextFrame();
        }

        // always render during xr sessions
        if (this.xrMode?.active) {
            this.renderNextFrame();
        }

        // or an animation is loaded and we're animating
        let isAnimationPlaying = false;
        for (let i = 0; i < this.entities.length; ++i) {
            const anim = this.entities[i].anim;
            if (anim && anim.baseLayer && anim.baseLayer.playing) {
                isAnimationPlaying = true;
                break;
            }
        }

        if (isAnimationPlaying) {
            this.dirtyBounds = true;
            this.dirtySkeleton = true;
            this.dirtyNormals = true;
            this.renderNextFrame();
            this.observer.emit('animationUpdate');
        }

        // or the ministats is enabled
        if (this.miniStats.enabled) {
            this.renderNextFrame();
        }
    }

    renderNextFrame() {
        this.app.renderNextFrame = true;
        if (this.multiframe) {
            this.multiframe.moved();
        }
    }

    clearCta() {
        document.querySelector('#panel-left').classList.add('no-cta');
        document.querySelector('#application-canvas').classList.add('no-cta');
        document.querySelector('.load-button-panel').classList.add('hide');
    }

    // add a loaded asset to the scene
    // asset is a container asset with renders and/or animations
    private addToScene(asset: Asset) {
        const resource = asset.resource;
        const meshesLoaded = resource.renders && resource.renders.length > 0;
        const animsLoaded = resource.animations && resource.animations.length > 0;
        const prevEntity: Entity = this.entities.length === 0 ? null : this.entities[this.entities.length - 1];

        let entity: Entity;

        // create entity
        if (!meshesLoaded && prevEntity && prevEntity.findComponent('render')) {
            entity = prevEntity;
        } else {
            if (asset.type === 'container') {
                // container/glb
                entity = asset.resource.instantiateRenderEntity();
            } else {
                // gaussian splat scene
                entity = asset.resource.instantiate();

                // render frame if gaussian splat sorter updates)
                entity.gsplat.instance.sorter.on('updated', () => {
                    this.renderNextFrame();
                });
            }

            this.entities.push(entity);
            this.entityAssets.push({ entity: entity, asset: asset });
            this.sceneRoot.addChild(entity);
            this.shadowCatcher.onEntityAdded(entity);
        }

        // create animation component
        if (animsLoaded) {
            // append anim tracks to global list
            resource.animations.forEach((a: any) => {
                this.animTracks.push(a.resource);
            });
        }

        // store the loaded asset
        this.assets.push(asset);
    }

    /**
     * Cancel the auto-front timer and remove the user-interaction listeners
     * that were registered to detect early camera interaction.
     */
    private _cancelAutoFront() {
        if (this.autoFrontTimer) {
            clearTimeout(this.autoFrontTimer);
            this.autoFrontTimer = null;
        }
        this._removeAutoFrontListeners();
    }

    /**
     * Remove the pointerdown / wheel / touchstart listeners that cancel the
     * auto-front timer.  Safe to call even if listeners were never registered.
     */
    private _removeAutoFrontListeners() {
        if (this._boundCancelAutoFront) {
            const c = this.canvas;
            c.removeEventListener('pointerdown', this._boundCancelAutoFront);
            c.removeEventListener('wheel', this._boundCancelAutoFront);
            c.removeEventListener('touchstart', this._boundCancelAutoFront);
            this._boundCancelAutoFront = null;
        }
    }

    /**
     * Position the camera at the Front view with the given orbit distance.
     * Reuses the same orbit-state logic as PositionPanel.setCameraView('front').
     */
    setCameraViewToFront(distance: number) {
        const mc = this.multiCamera as any;
        mc.sceneSize = distance * 0.5;

        const focusPosition = new Vec3(0, 0, 0);
        const targetPosition = new Vec3(0, 0.3, distance);

        const dir = new Vec3();
        dir.sub2(targetPosition, focusPosition);
        const hDist = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
        const elevDeg = Math.atan2(dir.y, hDist) * (180 / Math.PI);
        const azimDeg = Math.atan2(dir.x, dir.z) * (180 / Math.PI);

        mc._origin.copy(focusPosition);
        mc._position.copy(focusPosition);
        mc._dir.set(-elevDeg, -azimDeg);
        mc._angles.set(-elevDeg, -azimDeg, 0);
        mc._zoomDist = distance;

        this.renderNextFrame();
    }

    // perform post-load operations on the scene
    private postSceneLoad() {
        // construct a list of meshInstances so we can quickly access them when configuring wireframe rendering etc.
        this.meshInstances = this.entities
            .map((entity) => {
                return this.collectMeshInstances(entity);
            })
            .flat();

        // if no meshes are currently loaded, then enable skeleton rendering so user can see something
        if (this.meshInstances.length === 0) {
            this.observer.set('debug.skeleton', true);
        }

        // update
        this.updateSceneStats();
        this.sceneBounds.center.set(0, 0, 0);

        // rebuild the anim state graph
        if (this.animTracks.length > 0) {
            this.rebuildAnimTracks();
        }

        // Initialize start position for the models - DEFERRED
        setTimeout(() => {
            this.calcSceneBounds(this.sceneBounds);
            const centerInit = new Vec3(0, 0, 0);
            let targetInitPosition: Vec3 = new Vec3(centerInit.x + 0.2, centerInit.y + 0.3, centerInit.z + 8);
            let focusInitPosition = centerInit;



            //Here is where we set the initial camera position
            this.multiCamera.focus(focusInitPosition, targetInitPosition);
            //this.setPlyPosition(targetInitPosition.x, targetInitPosition.y, targetInitPosition.z);
            this.renderNextFrame();
            //console.log('Initial camera position: ' + targetInitPosition.toString());
            //console.log('Initial Ply position: ' + this.getPlyPosition().toString());
            //console.log('Initial bounds: ' + this.sceneBounds.center.toString() + ' : ' + this.sceneBounds.halfExtents.toString());
        }, 100); // Esperar 100ms para que la escena esté lista

        // make a list of all the morph instance target names
        const morphs: Record<string, { name: string; targets: Record<string, MorphTargetData> }> = {};
        const morphInstances: Record<string, MorphInstance> = {};

        // get all morph targets
        this.meshInstances.forEach((meshInstance, i) => {
            if (meshInstance.morphInstance) {
                const morphInstance = meshInstance.morphInstance;
                morphInstances[i] = morphInstance;

                // mesh name line
                const meshName = (meshInstance && meshInstance.node && meshInstance.node.name) || `Mesh ${i}`;
                morphs[i] = {
                    name: meshName,
                    targets: {}
                };

                // morph targets
                morphInstance.morph.targets.forEach((target: MorphTarget, targetIndex: number) => {
                    morphs[i].targets[targetIndex] = {
                        name: target.name,
                        targetIndex: targetIndex
                    };
                    this.observer.on(`morphs.${i}.targets.${targetIndex}.weight:set`, (weight: number) => {
                        morphInstances[i].setWeight(targetIndex, weight);
                        this.dirtyNormals = true;
                        this.renderNextFrame();
                    });
                });
            }
        });

        this.observer.suspendEvents = true;
        this.observer.set('morphs', morphs);
        this.observer.suspendEvents = false;

        // handle animation update
        const observer = this.observer;
        observer.on('animationUpdate', () => {
            // set progress
            for (let i = 0; i < this.entities.length; ++i) {
                const entity = this.entities[i];
                if (entity && entity.anim) {
                    const baseLayer = entity.anim.baseLayer;
                    const progress = baseLayer.activeStateCurrentTime / baseLayer.activeStateDuration;
                    this.suppressAnimationProgressUpdate = true;
                    observer.set('animation.progress', progress === 1 ? progress : progress % 1);
                    this.suppressAnimationProgressUpdate = false;
                    break;
                }
            }
        });

        // dirty everything
        this.dirtyWireframe = this.dirtyBounds = this.dirtySkeleton = this.dirtyGrid = this.dirtyNormals = true;


        this.renderNextFrame();

        // we perform some special processing on the first frame
        this.firstFrame = true;

        //! Auto-level the PLY model after loading (only for automated models)
        const loadedPly = this.getPlyEntity();
        if (loadedPly) {
            // Only apply auto-leveling for automated models (with requestID)
            if (this.isAutomatedModel) {
                setTimeout(() => {
                    const levelingResults = this.autoLevelPly(3);
                    if (levelingResults && levelingResults.length > 0) {
                    } else {
                        console.warn('Auto-leveling failed, applying default rotation');
                        loadedPly.setLocalEulerAngles(157, 0.8, 0);
                    }
                    this.renderNextFrame();
                }, 150); // Wait a bit more to ensure the scene is fully loaded
            } else {
                // For legacy models, don't apply any rotation corrections
                this.renderNextFrame();
            }
            //loadedPly.rotate(0, 180, 0);
            //this.rotateGizmoPly(); // [165.1588610916293, 0.7919037307726507, 0.11353096813798615] // [159.60576733194122, 0.7991728928336267, 0.036369656683623644] // [149.4293491520419, 0.7930274073251669, -0.10539553768980384]
        }

        // Auto-front: schedule camera to Front view 5s after load completes
        // Cancel any previous timer and listeners
        this._cancelAutoFront();

        this.autoFrontTimer = setTimeout(() => {
            this.setCameraViewToFront(6);
            this.autoFrontTimer = null;
            // Timer fired normally – remove the cancel listeners
            this._removeAutoFrontListeners();
        }, 1000);

        // Register user-interaction listeners that cancel the auto-front timer
        this._boundCancelAutoFront = () => this._cancelAutoFront();
        this.canvas.addEventListener('pointerdown', this._boundCancelAutoFront);
        this.canvas.addEventListener('wheel', this._boundCancelAutoFront);
        this.canvas.addEventListener('touchstart', this._boundCancelAutoFront);

    }

    private rotateGizmoPly() {

        const loadedPly = this.getPlyEntity();
        if (loadedPly) {
            // if the loaded model is a ply, then we center it and set an initial camera position
            // Create gizmo layer using PlayCanvas Gizmo API
            const gizmoLayer = (pc as any).Gizmo.createLayer(this.app);

            // Create a TranslateGizmo and attach it to the sphere
            const gizmo = new (pc as any).RotateGizmo(this.camera.camera, gizmoLayer);

            // Configure gizmo properties
            gizmo.size = 1;
            gizmo.snap = false;
            gizmo.coordSpace = 'world';
            gizmo.name = 'gizmo';

            // Attach gizmo to the sphere
            gizmo.attach(loadedPly);

            // Add event listeners to render when gizmo is being used
            gizmo.on('transform:start', () => {
                this.renderNextFrame();
                //console.log('Actual Ply rotation: ' + loadedPly.getEulerAngles().toString());
            });

            gizmo.on('transform:move', () => {
                this.renderNextFrame();
                //console.log('Actual Ply rotation: ' + loadedPly.getEulerAngles().toString());
            });

            gizmo.on('transform:end', () => {
                this.renderNextFrame();
                //console.log('Actual Ply rotation: ' + loadedPly.getEulerAngles().toString());
            });

            // Continuously render while gizmo exists (alternative approach)
            let gizmoActive = true;
            const renderLoop = () => {
                if (gizmoActive) {
                    this.renderNextFrame();
                    requestAnimationFrame(renderLoop);
                    //console.log('Actual Ply rotation: ' + loadedPly.getLocalEulerAngles().toString());
                }
            };
            renderLoop();
            //console.log('Ply model loaded, centering and setting initial camera position');
        }


    }

    private initSceneBounds() {
        this.setCenterScene(this.observer.get('centerScene'));

        // set projective skybox radius
        this.setSkyboxDomeRadius(this.observer.get('skybox.domeProjection.domeRadius'));

        // set camera clipping planes
        this.focusSelection();
    }

    // rebuild the animation state graph
    private rebuildAnimTracks() {
        this.entities.forEach((entity) => {
            // create the anim component if there isn't one already
            if (!entity.anim) {
                entity.addComponent('anim', {
                    activate: true,
                    speed: this.animSpeed
                });
                entity.anim.rootBone = entity;
            } else {
                // clean up any previous animations
                entity.anim.removeStateGraph();
            }

            this.animTracks.forEach((t: any, i: number) => {
                // add an event to each track which transitions to the next track when it ends
                t.events = new AnimEvents([
                    {
                        name: 'transition',
                        time: t.duration,
                        nextTrack: `track_${i === this.animTracks.length - 1 ? 0 : i + 1}`
                    }
                ]);
                const path = `track_${i}`;
                entity.anim.assignAnimation(path, t);
                this.animationMap[t.name] = path;
            });
            // if the user has selected to play all tracks in succession, then transition to the next track after a set amount of loops
            entity.anim.on('transition', (e) => {
                const animationName: string = this.observer.get('animation.selectedTrack');
                if (animationName === 'ALL_TRACKS' && entity.anim.baseLayer.activeStateProgress >= this.animLoops) {
                    entity.anim.baseLayer.transition(e.nextTrack, this.animTransition);
                }
            });
        });

        // let the controls know about the new animations, set the selected track and immediately start playing the animation
        const animationState = this.observer.get('animation');
        const animationKeys = Object.keys(this.animationMap);
        animationState.list = JSON.stringify(animationKeys);
        animationState.selectedTrack = animationKeys[0];
        animationState.playing = true;
        this.observer.set('animation', animationState);
    }

    private calcSceneBounds(result: BoundingBox, root: Entity | null = null) {
        const meshInstances = root ? this.collectMeshInstances(root) : this.meshInstances;
        if (meshInstances.length) {
            Viewer.calcMeshBoundingBox(result, meshInstances);
        } else {
            root = root ?? this.sceneRoot;
            if (root.children.length) {
                Viewer.calcHierBoundingBox(result, root);
            } else {
                result.copy(defaultSceneBounds);
            }
        }
    }

    private resetWireframeMeshes() {
        this.app.scene.layers.getLayerByName('World').removeMeshInstances(this.wireframeMeshInstances);
        this.wireframeMeshInstances.forEach((mi) => {
            mi.clearShaders();
        });
        this.wireframeMeshInstances = [];
    }

    private buildWireframeMeshes() {
        this.wireframeMeshInstances = this.getSelectedMeshInstances().map((mi) => {
            const meshInstance = new MeshInstance(mi.mesh, this.wireframeMaterial, mi.node);
            meshInstance.renderStyle = PRIMITIVE_LINES;
            meshInstance.skinInstance = mi.skinInstance;
            meshInstance.morphInstance = mi.morphInstance;
            return meshInstance;
        });

        this.app.scene.layers.getLayerByName('World').addMeshInstances(this.wireframeMeshInstances);
    }

    private onFrameRender() {
        if (this.canvasResize) {
            const { width, height } = this.getCanvasSize();
            const pixelScale = this.observer.get('camera.pixelScale');
            const widthPixels = Math.floor((width * window.devicePixelRatio) / pixelScale);
            const heightPixels = Math.floor((height * window.devicePixelRatio) / pixelScale);
            this.app.graphicsDevice.setResolution(widthPixels, heightPixels);
            this.observer.set('runtime.viewportWidth', widthPixels);
            this.observer.set('runtime.viewportHeight', heightPixels);
            this.canvasResize = false;
        }

        // rebuild render targets
        this.rebuildRenderTargets();
    }

    // generate and render debug elements on prerender
    private onPrerender() {
        if (this.firstFrame) {
            return;
        }

        // wireframe
        if (this.dirtyWireframe) {
            this.dirtyWireframe = false;

            this.resetWireframeMeshes();
            if (this.showWireframe) {
                this.buildWireframeMeshes();
            }

            this.getSelectedMeshInstances().forEach((mi) => {
                mi.material.depthBias = this.showWireframe ? -1.0 : 0.0;
                mi.material.slopeDepthBias = this.showWireframe ? 1.0 : 0.0;
            });
        }

        // debug bounds
        if (this.dirtyBounds || this.xrMode?.active) {
            this.dirtyBounds = false;

            // calculate bounds
            this.calcSceneBounds(this.dynamicSceneBounds);

            this.debugBounds.clear();
            if (this.showBounds) {
                this.calcSceneBounds(bbox, this.selectedNode as Entity);
                this.debugBounds.box(bbox.getMin(), bbox.getMax());
            }
            this.debugBounds.update();

            const v = new Vec3(
                this.dynamicSceneBounds.halfExtents.x * 2,
                this.dynamicSceneBounds.halfExtents.y * 2,
                this.dynamicSceneBounds.halfExtents.z * 2
            );
            this.observer.set('scene.bounds', v.toString());
        }

        // debug normals
        if (this.dirtyNormals) {
            this.dirtyNormals = false;
            this.debugNormals.clear();

            if (this.normalLength > 0) {
                for (let i = 0; i < this.meshInstances.length; ++i) {
                    const meshInstance = this.meshInstances[i];

                    const vertexBuffer = meshInstance.morphInstance ? // @ts-ignore TODO not defined in pc
                        meshInstance.morphInstance._vertexBuffer :
                        meshInstance.mesh.vertexBuffer;

                    if (vertexBuffer) {
                        const skinMatrices = meshInstance.skinInstance ? meshInstance.skinInstance.matrices : null;

                        // if there is skinning we need to manually update matrices here otherwise
                        // our normals are always a frame behind
                        if (skinMatrices) {
                            // @ts-ignore TODO not defined in pc
                            meshInstance.skinInstance.updateMatrices(meshInstance.node);
                        }

                        this.debugNormals.generateNormals(
                            vertexBuffer,
                            meshInstance.node.getWorldTransform(),
                            this.normalLength,
                            skinMatrices
                        );
                    }
                }
            }
            this.debugNormals.update();
        }

        // debug skeleton
        if (this.dirtySkeleton) {
            this.dirtySkeleton = false;
            this.debugSkeleton.clear();

            if (this.showSkeleton || this.showAxes) {
                this.entities.forEach((entity) => {
                    if (this.meshInstances.length === 0 || entity.findComponent('render')) {
                        this.debugSkeleton.generateSkeleton(
                            entity,
                            this.showSkeleton,
                            this.showAxes,
                            this.selectedNode
                        );
                    }
                });
            }

            this.debugSkeleton.update();
        }

        // debug grid
        if (this.sceneBounds && this.dirtyGrid) {
            this.dirtyGrid = false;

            this.debugGrid.clear();
            if (this.showGrid) {
                // calculate primary spacing
                const spacing = Math.pow(10, Math.floor(Math.log10(this.sceneBounds.halfExtents.length())));

                const v0 = new Vec3(0, 0, 0);
                const v1 = new Vec3(0, 0, 0);

                const y = 0;

                const numGrids = 10;
                const a = numGrids * spacing;
                for (let x = -numGrids; x < numGrids + 1; ++x) {
                    const b = x * spacing;

                    v0.set(-a, y, b);
                    v1.set(a, y, b);
                    this.debugGrid.line(v0, v1, b === 0 ? 0x80000000 >>> 0 : 0x80ffffff >>> 0);

                    v0.set(b, y, -a);
                    v1.set(b, y, a);
                    this.debugGrid.line(v0, v1, b === 0 ? 0x80000000 >>> 0 : 0x80ffffff >>> 0);
                }
            }
            this.debugGrid.update();
        }

        // fit camera planes to the scene
        this.fitCameraClipPlanes();

        this.shadowCatcher.onUpdate(this.dynamicSceneBounds);
    }

    private onPostrender() {
        if (this.firstFrame) {
            this.firstFrame = false;

            // reinit scene bounds after first render in order to get accurate morph target and skinned bounds
            this.initSceneBounds();
        }

        // resolve the (possibly multisampled) render target
        const rt = this.camera.camera.renderTarget;
        if (!this.app.graphicsDevice.isWebGPU && rt.samples > 1) {
            rt.resolve();
        }

        // perform multiframe update. returned flag indicates whether more frames
        // are needed.
        this.multiframeBusy = this.multiframe.update();
    }

    private onFrameend() {
        if (this.loadTimestamp !== null) {
            this.observer.set('scene.loadTime', `${Date.now() - this.loadTimestamp}ms`);
            this.loadTimestamp = null;
        }

        if (this.multiframeBusy) {
            this.app.renderNextFrame = true;
        }
    }

    /**
     * Get the first .ply entity in the scene
     */
    public getPlyEntity(): Entity | null {
        for (const entityAsset of this.entityAssets) {
            if (entityAsset.asset.name.toLowerCase().endsWith('.ply')) {
                //console.log('Ply entity found: ' + entityAsset.asset.name);
                return entityAsset.entity;
            }
        }
        return null;
    }

    /**
     * Set rotation for the .ply model
     */
    public setPlyRotation(axis: 'x' | 'y' | 'z', degrees: number) {
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            const currentRotation = plyEntity.getEulerAngles();

            switch (axis) {
                case 'x':
                    plyEntity.setEulerAngles(degrees, currentRotation.y, currentRotation.z);
                    break;
                case 'y':
                    plyEntity.setEulerAngles(currentRotation.x, degrees, currentRotation.z);
                    break;
                case 'z':
                    plyEntity.setEulerAngles(currentRotation.x, currentRotation.y, degrees);
                    break;
            }

            this.renderNextFrame();
            return true;
        }
        return false;
    }

    public getPlyPosition(): { x: number, y: number, z: number } | null {
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            const position = plyEntity.getLocalScale();
            return {
                x: Math.round(position.x * 100) / 100,
                y: Math.round(position.y * 100) / 100,
                z: Math.round(position.z * 100) / 100
            };
        }
        return null;
    }


    public setPlyPosition(x: number, y: number, z: number) {
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            plyEntity.setLocalScale(x, y, z);
            this.renderNextFrame();
            return true;
        }
        return false;
    }

    /**
     * Get current rotation of the .ply model
     */
    public getPlyRotation(): { x: number, y: number, z: number } | null {
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            const rotation = plyEntity.getLocalEulerAngles();
            return {
                x: Math.round(rotation.x * 100) / 100,
                y: Math.round(rotation.y * 100) / 100,
                z: Math.round(rotation.z * 100) / 100
            };
        }
        return null;
    }

    /**
     * Reset .ply model rotation
     */
    public resetPlyRotation() {
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            plyEntity.setLocalEulerAngles(0, 0, 0);
            this.renderNextFrame();
            return true;
        }
        return false;
    }

    /**
     * Calculate and level the PLY model to make it straight
     * Strategy: Sample 4 cardinal points and level them to be at the same height
     * Points: 1-Front, 2-Back, 3-Left, 4-Right (as shown in diagram)
     * Level 1-2 for pitch (X rotation) and 3-4 for roll (Z rotation)
     */
    public levelPlyModel() {
        const plyEntity = this.getPlyEntity();
        if (!plyEntity) {
            console.warn('No PLY entity found to level');
            return null;
        }

        // Get all mesh instances
        const meshInstances: MeshInstance[] = this.collectMeshInstances(plyEntity);
        if (meshInstances.length === 0) {
            console.warn('No mesh instances found in PLY entity');
            return null;
        }

        const currentRotation = plyEntity.getLocalEulerAngles();

        //console.log('=== PLY LEVELING ANALYSIS (4-Point Cardinal Method) ===');
        //console.log('Current rotation:', { x: currentRotation.x, y: currentRotation.y, z: currentRotation.z });

        // Calculate WORLD space bounding box (mesh instances already have world AABBs)
        const worldBBox = new BoundingBox();
        for (const mi of meshInstances) {
            if (worldBBox.center.length() === 0) {
                worldBBox.copy(mi.aabb);
            } else {
                worldBBox.add(mi.aabb);
            }
        }

        // Get world bounds - these are already transformed
        const center = worldBBox.center;
        const min = worldBBox.getMin();
        const max = worldBBox.getMax();

        /*         console.log('World bounding box:', { 
                    min: { x: min.x.toFixed(2), y: min.y.toFixed(2), z: min.z.toFixed(2) }, 
                    max: { x: max.x.toFixed(2), y: max.y.toFixed(2), z: max.z.toFixed(2) },
                    center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) }
                }); */

        // Find the lowest Y coordinate at 4 cardinal XZ positions
        // We need to scan through mesh vertices at specific XZ locations
        let frontY = Infinity, backY = Infinity, leftY = Infinity, rightY = Infinity;

        for (const mi of meshInstances) {
            const mesh = mi.mesh;
            if (!mesh || !mesh.vertexBuffer) continue;

            const positions = [];
            const vertexBuffer = mesh.vertexBuffer;
            const format = vertexBuffer.format;

            // Get position data
            for (let i = 0; i < format.elements.length; i++) {
                if (format.elements[i].name === 'POSITION') {
                    const data = new Float32Array(vertexBuffer.storage);
                    const stride = format.size / 4; // Convert bytes to floats
                    const offset = format.elements[i].offset / 4;

                    for (let v = 0; v < vertexBuffer.numVertices; v++) {
                        const idx = v * stride + offset;
                        const localPos = new Vec3(data[idx], data[idx + 1], data[idx + 2]);
                        const worldPos = new Vec3();
                        mi.node.getWorldTransform().transformPoint(localPos, worldPos);

                        // Check if this vertex is at one of our cardinal positions (with tolerance)
                        const tolerance = (max.x - min.x) * 0.1; // 10% tolerance

                        // Front (min Z)
                        if (Math.abs(worldPos.z - min.z) < tolerance && worldPos.y < frontY) {
                            frontY = worldPos.y;
                        }
                        // Back (max Z)
                        if (Math.abs(worldPos.z - max.z) < tolerance && worldPos.y < backY) {
                            backY = worldPos.y;
                        }
                        // Left (min X)
                        if (Math.abs(worldPos.x - min.x) < tolerance && worldPos.y < leftY) {
                            leftY = worldPos.y;
                        }
                        // Right (max X)
                        if (Math.abs(worldPos.x - max.x) < tolerance && worldPos.y < rightY) {
                            rightY = worldPos.y;
                        }
                    }
                    break;
                }
            }
        }

        // Fallback to bounding box if no vertices found
        if (frontY === Infinity) frontY = min.y;
        if (backY === Infinity) backY = min.y;
        if (leftY === Infinity) leftY = min.y;
        if (rightY === Infinity) rightY = min.y;

        /*         console.log('Cardinal points (world Y heights):');
                console.log('  Point 1 (Front):', frontY.toFixed(4));
                console.log('  Point 2 (Back):', backY.toFixed(4));
                console.log('  Point 3 (Left):', leftY.toFixed(4));
                console.log('  Point 4 (Right):', rightY.toFixed(4)); */

        // Calculate height differences
        const heightDiff_1_2 = backY - frontY; // Front to Back difference
        const heightDiff_3_4 = rightY - leftY; // Left to Right difference

        /*         console.log('Height differences:');
                console.log('  Front→Back (1→2):', heightDiff_1_2.toFixed(4));
                console.log('  Left→Right (3→4):', heightDiff_3_4.toFixed(4)); */

        // Calculate the distance between points for angle calculation
        const distance_1_2 = Math.abs(max.z - min.z); // Front-back distance
        const distance_3_4 = Math.abs(max.x - min.x); // Left-right distance

        // Calculate rotation angles needed to level the points
        // Pitch (X rotation): Level points 1-2 (front-back)
        const pitchAngleRad = Math.atan2(heightDiff_1_2, distance_1_2);
        const pitchAngleDeg = pitchAngleRad * (180 / Math.PI);

        // Roll (Z rotation): Level points 3-4 (left-right)
        const rollAngleRad = Math.atan2(heightDiff_3_4, distance_3_4);
        const rollAngleDeg = rollAngleRad * (180 / Math.PI);

        /*         console.log('Calculated corrections:');
                console.log('  Pitch (X) to level 1-2:', (-pitchAngleDeg).toFixed(3) + '°');
                console.log('  Roll (Z) to level 3-4:', (-rollAngleDeg).toFixed(3) + '°'); */

        // Apply corrections
        const correctionX = -pitchAngleDeg;
        const correctionY = 0; // Y rotation (yaw) not needed for leveling
        const correctionZ = -rollAngleDeg;

        const newRotationX = currentRotation.x + correctionX;
        const newRotationY = currentRotation.y + correctionY;
        const newRotationZ = currentRotation.z + correctionZ;

        // Set the new rotation
        plyEntity.setLocalEulerAngles(newRotationX, newRotationY, newRotationZ);
        this.renderNextFrame();

        const result = {
            method: '4-point-cardinal',
            cardinalPoints: {
                point1_Front_Y: frontY,
                point2_Back_Y: backY,
                point3_Left_Y: leftY,
                point4_Right_Y: rightY
            },
            heightDifferences: {
                frontToBack: heightDiff_1_2,
                leftToRight: heightDiff_3_4
            },
            detectedInclination: {
                pitch: pitchAngleDeg,
                yaw: 0,
                roll: rollAngleDeg
            },
            correction: {
                x: correctionX,
                y: correctionY,
                z: correctionZ
            },
            previousRotation: {
                x: currentRotation.x,
                y: currentRotation.y,
                z: currentRotation.z
            },
            newRotation: {
                x: newRotationX,
                y: newRotationY,
                z: newRotationZ
            }
        };

        //console.log('=== LEVELING COMPLETE ===');
        return result;
    }

    /**
     * Auto-level PLY model iteratively for better precision
     * Each iteration recalculates and applies corrections until the model is level
     */
    public autoLevelPly(iterations: number = 3) {
        //console.log(`Starting auto-level process with up to ${iterations} iterations`);
        const results = [];
        const tolerance = 0.01; // Stop if height difference is less than 0.01 units

        for (let i = 0; i < iterations; i++) {
            const result = this.levelPlyModel();
            if (result) {
                results.push(result);
                //console.log(`Iteration ${i + 1}/${iterations} complete`);

                // Check if the model is sufficiently level
                const maxHeightDiff = Math.max(
                    Math.abs(result.heightDifferences.frontToBack),
                    Math.abs(result.heightDifferences.leftToRight)
                );

                //console.log(`  Max height difference: ${maxHeightDiff.toFixed(4)} units`);

                if (maxHeightDiff < tolerance) {
                    //console.log(`✓ Model sufficiently level (threshold: ${tolerance} units)`);
                    break;
                }
            } else {
                console.warn(`Auto-level iteration ${i + 1} failed`);
                break;
            }
        }

        // After leveling, rotate 180° on Y axis to show the front of the car
        const plyEntity = this.getPlyEntity();
        if (plyEntity) {
            const currentRotation = plyEntity.getLocalEulerAngles();
            //console.log('Applying Y rotation to show front view...');
            plyEntity.setLocalEulerAngles(currentRotation.x, 180, currentRotation.z);
            //console.log('Final rotation after front adjustment:', { x: currentRotation.x, y: 180, z: currentRotation.z });
            this.renderNextFrame();
        }

        //console.log(`Auto-level complete after ${results.length} iterations`);
        return results;
    }

    /**
     * Visualize a click point on the PLY model with a temporary sphere
     */
    private visualizePlyClickPoint(position: Vec3) {
        // Remove previous click visualization if it exists
        const existingMarker = this.app.root.findByName('ply-click-marker');
        if (existingMarker) {
            existingMarker.destroy();
        }

        // Create a small sphere to mark the click position
        const markerEntity = new Entity('ply-click-marker');
        markerEntity.addComponent('render', {
            type: 'sphere',
            material: new StandardMaterial()
        });

        // Set the material color to bright red for visibility
        const material = markerEntity.render.material as StandardMaterial;
        material.diffuse.set(1, 0, 0); // Red color
        material.emissive.set(1, 0.5, 0); // Orange emissive for better visibility
        material.update();

        // Position and scale the marker
        markerEntity.setPosition(position);
        markerEntity.setLocalScale(0.05, 0.05, 0.05); // Small sphere

        // Add to scene
        this.app.root.addChild(markerEntity);

        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (markerEntity && markerEntity.parent) {
                markerEntity.destroy();
            }
        }, 3000);
    }

    /**
     * Show PLY click information on screen
     */
    private showPlyClickInfo(position: Vec3, distance: number, meshName: string) {
        // Remove previous info panel if it exists
        const existingPanel = document.getElementById('ply-click-info-panel');
        if (existingPanel) {
            existingPanel.remove();
        }

        // Create info panel
        const infoPanel = document.createElement('div');
        infoPanel.id = 'ply-click-info-panel';
        infoPanel.style.position = 'absolute';
        infoPanel.style.top = '50%';
        infoPanel.style.left = '50%';
        infoPanel.style.transform = 'translate(-50%, -50%)';
        infoPanel.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
        infoPanel.style.color = '#fff';
        infoPanel.style.padding = '20px';
        infoPanel.style.borderRadius = '8px';
        infoPanel.style.fontFamily = 'monospace';
        infoPanel.style.fontSize = '14px';
        infoPanel.style.zIndex = '10000';
        infoPanel.style.border = '2px solid #ff6b00';
        infoPanel.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.5)';
        infoPanel.style.minWidth = '300px';

        infoPanel.innerHTML = `
            <div style="text-align: center; margin-bottom: 15px;">
                <strong style="color: #ff6b00; font-size: 16px;"> PLY Model Click Detected</strong>
            </div>
            <div style="line-height: 1.8;">
                <div style="margin-bottom: 8px;">
                    <strong style="color: #4CAF50;">World Position:</strong>
                </div>
                <div style="margin-left: 15px; margin-bottom: 10px;">
                    X: <span style="color: #ffeb3b">${position.x.toFixed(3)}</span><br>
                    Y: <span style="color: #ffeb3b">${position.y.toFixed(3)}</span><br>
                    Z: <span style="color: #ffeb3b">${position.z.toFixed(3)}</span>
                </div>
                <div style="margin-bottom: 5px;">
                    <strong style="color: #2196F3;">Distance:</strong> 
                    <span style="color: #ffeb3b">${distance.toFixed(3)}</span> units
                </div>
                <div>
                    <strong style="color: #9C27B0;">Mesh:</strong> 
                    <span style="color: #ffeb3b">${meshName}</span>
                </div>
            </div>
            <div style="text-align: center; margin-top: 15px; font-size: 11px; color: #999;">
                This panel will close in 3 seconds
            </div>
        `;

        // Add to document
        document.body.appendChild(infoPanel);

        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (infoPanel.parentNode) {
                infoPanel.remove();
            }
        }, 3000);
    }

    /**
     * Diagnostic method to analyze PLY model structure and mesh data
     * Call this method from console: window.viewer.diagnosePlyMesh()
     */
    public diagnosePlyMesh() {
        console.log('=== PLY MESH DIAGNOSTIC ===');

        const plyEntity = this.getPlyEntity();
        if (!plyEntity) {
            console.error(' No PLY entity found in scene');
            return;
        }

        console.log(' PLY Entity found:', plyEntity.name);
        console.log('Entity position:', plyEntity.getPosition().toString());
        console.log('Entity rotation:', plyEntity.getLocalEulerAngles().toString());
        console.log('Entity scale:', plyEntity.getLocalScale().toString());

        // Check if it's a Gaussian Splat
        if (plyEntity.gsplat) {
            console.log('\n === GAUSSIAN SPLAT MODEL DETECTED ===');
            console.log('This is NOT a traditional mesh-based PLY file');
            console.log('Gaussian Splats use point-based rendering');
            console.log('\nGsplat component info:');
            console.log('  Component:', plyEntity.gsplat);
            console.log('  Instance:', plyEntity.gsplat.instance);
            if (plyEntity.gsplat.instance) {
                console.log('  Splat count:', plyEntity.gsplat.instance.splat?.numSplats || 'unknown');
                console.log('  AABB:', plyEntity.gsplat.instance.meshInstance?.aabb);
            }
            console.log('\n️ Note: For Gaussian Splats, use depth buffer reading instead of raycast');
            console.log('Click detection will use handleGaussianSplatClick() method');
            console.log('=== END DIAGNOSTIC ===');
            return;
        }

        // Check if entity has render component
        if (!plyEntity.render) {
            console.log('️ PLY entity has no render component');
            console.log('This entity does not have traditional mesh data');
        } else {
            console.log(' PLY entity has render component');
            console.log('Mesh instances:', plyEntity.render.meshInstances.length);
        }

        // Collect all mesh instances
        const meshInstances: MeshInstance[] = [];
        this.collectMeshInstancesForRaycast(plyEntity, meshInstances);

        console.log(`\n Total mesh instances found: ${meshInstances.length}`);

        meshInstances.forEach((meshInstance, index) => {
            console.log(`\n--- Mesh Instance ${index + 1} ---`);
            console.log('Node name:', meshInstance.node.name);
            console.log('Visible:', meshInstance.visible);
            console.log('Layer:', meshInstance.layer || 'none');

            const mesh = meshInstance.mesh;
            if (!mesh) {
                console.log(' No mesh data');
                return;
            }

            console.log(' Mesh data available:');
            console.log('  Vertices:', mesh.vertexBuffer?.numVertices || 0);
            console.log('  Primitive type:', mesh.primitive);

            if (mesh.vertexBuffer) {
                const format = mesh.vertexBuffer.format;
                console.log('  Vertex format:');
                format.elements.forEach(el => {
                    console.log(`    - ${el.name}: type=${el.dataType}, offset=${el.offset}`);
                });
            }

            if (mesh.indexBuffer && mesh.indexBuffer[0]) {
                const indexBuffer = mesh.indexBuffer[0];
                const indexCount = indexBuffer.bytesPerIndex === 2
                    ? new Uint16Array(indexBuffer.storage).length
                    : new Uint32Array(indexBuffer.storage).length;
                console.log('  Indices:', indexCount, `(${Math.floor(indexCount / 3)} triangles)`);
                console.log('  Bytes per index:', indexBuffer.bytesPerIndex);
            } else {
                console.log('   No index buffer');
            }

            // Show bounding box
            const aabb = meshInstance.aabb;
            if (aabb) {
                console.log('  Bounding box:');
                console.log('    Center:', aabb.center.toString());
                console.log('    Half extents:', aabb.halfExtents.toString());
            }
        });

        console.log('\n=== END DIAGNOSTIC ===');
    }

    /**
     * Diagnostic method to analyze PLY model structure and orientation
     * This helps understand the model's current state without modifying it
     */
    public diagnosePlyModel() {
        const plyEntity = this.getPlyEntity();
        if (!plyEntity) {
            console.warn('No PLY entity found to diagnose');
            return null;
        }

        // Get all mesh instances using the same method as collectMeshInstances
        const meshInstances: MeshInstance[] = this.collectMeshInstances(plyEntity);

        if (meshInstances.length === 0) {
            console.warn('No mesh instances found');
            return null;
        }

        // Calculate bounds
        const bounds = new BoundingBox();
        Viewer.calcMeshBoundingBox(bounds, meshInstances);

        // Get current state
        const currentRotation = plyEntity.getLocalEulerAngles();
        const currentPosition = plyEntity.getLocalPosition();
        const worldTransform = plyEntity.getWorldTransform();

        // Analyze key points
        const min = bounds.getMin();
        const max = bounds.getMax();
        const center = bounds.center;
        const halfExtents = bounds.halfExtents;

        // Sample multiple heights at the center to understand vertical distribution
        const verticalSamples = [];
        for (let i = 0; i <= 10; i++) {
            const t = i / 10;
            const sampleY = min.y + (max.y - min.y) * t;
            const point = new Vec3(center.x, sampleY, center.z);
            const worldPoint = new Vec3();
            worldTransform.transformPoint(point, worldPoint);
            verticalSamples.push({
                localY: sampleY,
                worldY: worldPoint.y,
                percentage: t * 100
            });
        }

        const diagnosis = {
            entity: {
                name: plyEntity.name,
                position: { x: currentPosition.x, y: currentPosition.y, z: currentPosition.z },
                rotation: { x: currentRotation.x, y: currentRotation.y, z: currentRotation.z }
            },
            bounds: {
                min: { x: min.x, y: min.y, z: min.z },
                max: { x: max.x, y: max.y, z: max.z },
                center: { x: center.x, y: center.y, z: center.z },
                halfExtents: { x: halfExtents.x, y: halfExtents.y, z: halfExtents.z },
                size: {
                    width: max.x - min.x,
                    height: max.y - min.y,
                    depth: max.z - min.z
                }
            },
            verticalDistribution: verticalSamples,
            meshCount: meshInstances.length,
            targetRotation: {
                // Based on manual corrections, these are typical target values
                x: 'Expected around 150-165°',
                y: 'Expected around 0.8°',
                z: 'Expected around ±0.1°'
            }
        };

        /*         console.log('========================================');
                console.log('PLY MODEL DIAGNOSTIC REPORT');
                console.log('========================================');
                console.log('Entity:', diagnosis.entity);
                console.log('Bounds:', diagnosis.bounds);
                console.log('Vertical Samples (center column):');
                verticalSamples.forEach(sample => {
                    console.log(`  ${sample.percentage.toFixed(0)}%: Local Y=${sample.localY.toFixed(3)}, World Y=${sample.worldY.toFixed(3)}`);
                });
                console.log('Mesh Instances:', diagnosis.meshCount);
                console.log('Expected target rotation:', diagnosis.targetRotation);
                console.log('========================================'); */

        return diagnosis;
    }

    // ─── Camera Positions Layer ───────────────────────────────────────────────

    /**
     * Fetches camera positions from the API and spawns one Camera3DModel.stl
     * instance per camera returned.
     * All entities are hidden by default; call toggleCameraPositionsLayer(true)
     * to make them visible.
     */
    private async initCameraPositionsLayer(): Promise<void> {
        if (!this.requestId) {
            console.warn('[CameraPositionsLayer] No requestId available – skipping camera fetch');
            return;
        }

        try {
            const apiConfig = await this.resolveApiConfigByRequestId();

            const camerasUrl = (baseUrl: string, stagePath: 'pre' | 'pro') =>
                `${baseUrl}/${stagePath}/${this.API_PREFIX}/requests/${this.requestId}/cameras`;

            const fetchCameras = (baseUrl: string, stagePath: 'pre' | 'pro') =>
                fetch(camerasUrl(baseUrl, stagePath), {
                    method: 'GET',
                    mode: 'cors',
                    headers: this.buildAuthHeaders()
                });

            const geometryPromise = loadStl('static/Camera3DModel.stl');
            let camerasResponse = await fetchCameras(apiConfig.baseUrl, apiConfig.stagePath);

            if (!camerasResponse.ok && camerasResponse.status === 404) {
                const explicitStageFromLoad = this.getStageHintFromLoadParam();

                if (explicitStageFromLoad) {
                    console.warn(
                        `[CameraPositionsLayer] Cameras not found in ${explicitStageFromLoad.toUpperCase()} for request ${this.requestId}. ` +
                        'Skipping cross-environment fallback because stage is explicit in ?load=' 
                    );
                } else {
                    const fallbackStage: 'pre' | 'prod' = apiConfig.stage === 'pre' ? 'prod' : 'pre';
                    const fallbackBase = this.API_BASE_URLS[fallbackStage];
                    const fallbackPath = this.API_STAGE_PATHS[fallbackStage];
                    const fallbackResponse = await fetchCameras(fallbackBase, fallbackPath);

                    if (fallbackResponse.ok) {
                        this.setEnvironment(fallbackStage);
                        camerasResponse = fallbackResponse;
                        console.info(`[CameraPositionsLayer] Switched API environment to ${fallbackStage.toUpperCase()} for request ${this.requestId}`);
                    }
                }
            }

            const geometry = await geometryPromise;

            if (!camerasResponse.ok) {
                throw new Error(`Cameras API returned ${camerasResponse.status} ${camerasResponse.statusText}`);
            }

            const camerasData: ApiCamerasResponse = await camerasResponse.json();

            // Check if the model failed (too few cameras) and open fallback gallery
            // Prefer totalCameras field when available; fall back to cameras array length
            const cameraCount = camerasData.totalCameras ?? camerasData.cameras.length;
            if (isModelFailed(cameraCount)) {
                this.handleModelFallback(camerasData);
                return;
            }

            // Build a PlayCanvas mesh from STL geometry.
            const mesh = new Mesh(this.app.graphicsDevice);
            mesh.setPositions(geometry.positions);
            mesh.setNormals(geometry.normals);
            mesh.setIndices(geometry.indices);
            mesh.update();

            // Opaque material for the camera markers
            const material = new StandardMaterial();
            material.diffuse = CAMERA_MODEL_COLOR;
            material.specular = new Color(0.3, 0.3, 0.3);
            material.opacity = CAMERA_MODEL_OPACITY;
            material.blendType = BLEND_NORMAL;
            material.useMetalness = false;
            material.update();

            // ── Correction parent ─────────────────────────────────────────
            // Invisible entity at the world origin. Acts as a global rotation
            // controller for ALL camera models simultaneously. Rotating it here
            // corrects the orientation mismatch that comes from the backend data.
            //
            // Calibrated values (found with the RotateGizmo debug tool):
            //   X = 156.002   Y = 5.600   Z = -180.000
            //
            // To re-calibrate: uncomment the RotateGizmo block below and the
            // window helper functions, reload, drag the gizmo rings until the
            // cameras look correct, then copy the logged values back here.
            const cameraParent = new Entity('camera-correction-parent');
            this.app.root.addChild(cameraParent);
            cameraParent.setPosition(0, 0, 0);
            // Start at identity so children are positioned in clean local space.
            // The calibrated correction is applied in toggleCameraPositionsLayer()
            // the first time the layer is made visible (after the PLY has loaded).
            cameraParent.setEulerAngles(0, 0, 0);
            this.cameraParentEntity = cameraParent;
            // ─────────────────────────────────────────────────────────────

            for (const camData of camerasData.cameras) {
                const entity = new Entity(`camera-position-model-${camData.cameraId}`);

                const markerMaterial = material.clone() as StandardMaterial;
                markerMaterial.diffuse.copy(CAMERA_MODEL_COLOR);
                markerMaterial.opacity = CAMERA_MODEL_OPACITY;
                markerMaterial.update();

                entity.addComponent('render', {
                    meshInstances: [new MeshInstance(mesh, markerMaterial)],
                    material: markerMaterial
                });
                entity.tags.add('camera-position-marker');

                // Parent to the correction container so world-origin rotations apply to all
                cameraParent.addChild(entity);

                // Position and orient marker
                entity.setPosition(camData.position.x, camData.position.y, camData.position.z);
                this.orientCameraModelTowardsOrigin(entity);
                entity.setLocalScale(CAMERA_MODEL_SCALE, CAMERA_MODEL_SCALE, CAMERA_MODEL_SCALE);

                // Hidden until the user toggles the layer on
                entity.enabled = false;

                // Store world-space spawn position (for lift reset) + associated photo URL
                this.cameraModelSpawnPositions.push(entity.getPosition().clone());
                this.cameraModelPhotoUrls.push(camData.imageUrl);
                this.cameraModelPhotoIds.push(camData.imageName || '');
                this.cameraModelCameraIds.push(camData.cameraId);
                this.cameraModelApiPositions.push(new Vec3(camData.position.x, camData.position.y, camData.position.z));
                this.cameraModelMaterials.push(markerMaterial);
                this.cameraIndexById.set(camData.cameraId, this.cameraPositionEntities.length);
                this.cameraPositionEntities.push(entity);
            }

            this.applyCameraDamageColors();

            // ── RotateGizmo (calibration tool – uncomment to re-calibrate) ─
            // Attach a visual rotation gizmo to the correction parent so you can
            // drag it interactively. The console will log the resulting Euler
            // angles; copy them into toggleCameraPositionsLayer() below when done.
            //
            // try {
            //     const gizmoLayer = (pc as any).Gizmo.createLayer(this.app);
            //     const rotateGizmo = new (pc as any).RotateGizmo(this.camera.camera, gizmoLayer);
            //     rotateGizmo.size = 2;
            //     rotateGizmo.coordSpace = 'world';
            //     rotateGizmo.attach(cameraParent);
            //
            //     const logRotation = (label: string) => {
            //         const e = cameraParent.getEulerAngles();
            //         console.log(`[CameraCorrection] ${label} → X=${e.x.toFixed(3)}  Y=${e.y.toFixed(3)}  Z=${e.z.toFixed(3)}`);
            //     };
            //
            //     rotateGizmo.on('transform:move', () => { logRotation('live'); this.renderNextFrame(); });
            //     rotateGizmo.on('transform:end',  () => { logRotation('FINAL – copy these values into toggleCameraPositionsLayer'); this.renderNextFrame(); });
            //
            //     console.log('[CameraPositionsLayer] RotateGizmo ready. Drag the rings to calibrate.');
            // } catch (gizmoErr) {
            //     console.warn('[CameraPositionsLayer] Could not create RotateGizmo:', gizmoErr);
            // }
            //
            // ── Browser-console helpers (uncomment together with gizmo above) ─
            // (window as any).setCameraCorrection = (x: number, y: number, z: number) => {
            //     cameraParent.setEulerAngles(x, y, z);
            //     const e = cameraParent.getEulerAngles();
            //     console.log(`[CameraCorrection] Rotation set → X=${e.x.toFixed(3)}  Y=${e.y.toFixed(3)}  Z=${e.z.toFixed(3)}`);
            //     this.renderNextFrame();
            // };
            // (window as any).getCameraCorrection = () => {
            //     const e = cameraParent.getEulerAngles();
            //     console.log(`[CameraCorrection] Current rotation → X=${e.x.toFixed(3)}  Y=${e.y.toFixed(3)}  Z=${e.z.toFixed(3)}`);
            //     return { x: e.x, y: e.y, z: e.z };
            // };
            // ─────────────────────────────────────────────────────────────
        } catch (error) {
            console.error('[CameraPositionsLayer] Failed to initialise:', error);
        }
    }

    private handleModelFallback(camerasData: ApiCamerasResponse): void {
        // Stop fake loading interval if active
        if (this.fakeLoadingInterval) {
            clearInterval(this.fakeLoadingInterval);
            this.fakeLoadingInterval = null;
        }
        this.fakeLoadingActive = false;
        this.realLoadingStarted = false;
        this.observer.set('ui.spinner', false);

        // Use the photos array from the API response (available on failed models)
        // Fall back to transforming cameras if photos is not present
        const photos = camerasData.photos && camerasData.photos.length > 0
            ? transformPhotosToGalleryPhotos(camerasData.photos)
            : transformCamerasToGalleryPhotos(camerasData.cameras);
        (window as any).openFallbackGallery?.(photos);

        console.info(`[Viewer] Model failed (cameras: ${camerasData.totalCameras ?? camerasData.cameras.length} < ${CAMERA_THRESHOLD}). Opening fallback gallery with ${photos.length} photos.`);
    }

    private ensureCameraModelRotateGizmo(): void {
        if (this.cameraModelRotateGizmo || this.cameraPositionEntities.length === 0) {
            return;
        }

        try {
            const gizmoLayer = (pc as any).Gizmo.createLayer(this.app);
            const rotateGizmo = new (pc as any).RotateGizmo(this.camera.camera, gizmoLayer);
            rotateGizmo.size = 0.9;
            rotateGizmo.snap = false;
            rotateGizmo.coordSpace = 'local';
            rotateGizmo.name = 'camera-model-rotate-gizmo';
            const targetCameraEntity = this.cameraPositionEntities[0];
            rotateGizmo.attach(targetCameraEntity);

            rotateGizmo.on('transform:start', () => {
                this.renderNextFrame();
            });
            rotateGizmo.on('transform:move', () => {
                this.renderNextFrame();
            });
            rotateGizmo.on('transform:end', () => {
                this.renderNextFrame();
            });

            this.cameraModelRotateGizmo = rotateGizmo;
        } catch (gizmoErr) {
            console.warn('[CameraPositionsLayer] Could not create camera model RotateGizmo:', gizmoErr);
        }
    }

    private orientCameraModelTowardsOrigin(entity: Entity): void {
        entity.lookAt(Vec3.ZERO);
        entity.rotateLocal(0, CAMERA_MODEL_FORWARD_FLIP_Y, 0);
        entity.rotateLocal(CAMERA_MODEL_ROTATION_X, CAMERA_MODEL_ROTATION_Y, CAMERA_MODEL_ROTATION_Z);
    }

    private runInitialCameraPositionsImport(): void {
        if (this.cameraPositionsInitialImportDone) {
            return;
        }

        this.cameraPositionsInitialImportDone = true;
        void this.loadDamagesFromAPI();
    }

    /**
     * Shows or hides all camera-position model entities.
     * Called by the UI toggle button.
     *
     * The global orientation correction is applied here (not at spawn time) so
     * that it takes effect AFTER children have stored their local positions and
     * AFTER the PLY model has been loaded and auto-leveled. Applying it at
     * spawn time would be canceled out by PlayCanvas's own world→local
     * position computation, producing no visible effect.
     *
     * Calibrated values (found with the RotateGizmo debug tool):
    *   X = 163.422   Y = 13.600   Z = 1.878
     */
    toggleCameraPositionsLayer(visible: boolean): void {
        this.cameraPositionsVisible = visible;

        if (visible) {
            this.runInitialCameraPositionsImport();
        }

        if (this.cameraModelRotateGizmo) {
            try {
                if (typeof this.cameraModelRotateGizmo.destroy === 'function') {
                    this.cameraModelRotateGizmo.destroy();
                } else if (typeof this.cameraModelRotateGizmo.detach === 'function') {
                    this.cameraModelRotateGizmo.detach();
                }
            } catch (gizmoErr) {
                console.warn('[CameraPositionsLayer] Could not fully disable camera model RotateGizmo:', gizmoErr);
            }
            this.cameraModelRotateGizmo = null;
        }

        if (this.cameraParentEntity) {
            this.cameraParentEntity.setEulerAngles(
                visible ? 163.422 : 0,
                visible ? 13.600  : 0,
                visible ? 1.878   : 0
            );
        }

        if (visible) {
            this.cameraPositionEntities.forEach((entity) => {
                this.orientCameraModelTowardsOrigin(entity);
            });
        }

        this.applyCameraMarkerVisibility();
    }

    /**
     * Resets all camera-position model entities to their original spawn
     * world-space positions. Call this after any operation that may have
     * displaced them (e.g. the lift slider moving the PLY model).
     */
    public resetCameraModelPositions(): void {
        this.cameraPositionEntities.forEach((entity, i) => {
            const spawn = this.cameraModelSpawnPositions[i];
            if (spawn) {
                entity.setPosition(spawn.x, spawn.y, spawn.z);
            }
        });
        this.renderNextFrame();
    }

    // ─────────────────────────────────────────────────────────────────────────
}

export default Viewer;

